import React, { useState, useEffect, useRef, useCallback, useMemo, Fragment } from 'react';
import './App.css';
import ProductionPage from './ProductionPage';

const API_BASE = process.env.NODE_ENV === 'production' ? '/api' : '';
const TOKEN_KEY = 'cgm_token';
const GLC_LAST_META_KEY = 'cgm_glc_last_meta';
const UI_STATE_KEY = 'cgm_ui_state';
const BRAND_SINOCARE = 'Sinocare';
const BRAND_ARIA = 'ARIA';

function loadUiState() {
  try {
    return JSON.parse(localStorage.getItem(UI_STATE_KEY) || '{}') || {};
  } catch {
    return {};
  }
}

/** Double-rAF focus for Bluetooth wedge inputs (order / marketing / transfer). */
function focusScannerInput(ref) {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const el = ref?.current;
      if (!el || el.disabled) return;
      el.focus();
      try {
        const len = el.value?.length || 0;
        el.setSelectionRange(len, len);
      } catch {
        /* ignore */
      }
    });
  });
}

function isTypingTarget(el) {
  if (!el || el === document.body) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (el.isContentEditable) return true;
  return Boolean(el.closest?.('.modal-overlay, .modal-card, [role="dialog"]'));
}

function isoToDmy(iso) {
  if (!iso) return '';
  const s = String(iso).slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : '';
}

function dmyToIso(dmy) {
  const s = String(dmy || '').trim();
  const m = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (!m) return null;
  const d = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10);
  const y = parseInt(m[3], 10);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function formatDmyInput(value) {
  const digits = String(value).replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`;
}

function emptyGlcLotLine(sku, qty = '') {
  return {
    key: `${sku}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    sku,
    lot_number: '',
    expiration: '',
    qty: qty === '' || qty == null ? '' : String(qty),
  };
}

function glcLotLinesFromOrder(o) {
  const raw = Array.isArray(o.glc_lot_lines) ? o.glc_lot_lines : [];
  if (raw.length) {
    return raw.map((l, i) => ({
      key: `${l.sku}-${l.lot_number || i}-${i}`,
      sku: l.sku,
      lot_number: l.lot_number || '',
      expiration: isoToDmy(l.expiration_date),
      qty: String(l.qty ?? ''),
    }));
  }
  const lines = [];
  if ((o.qty_gluck || 0) > 0) {
    lines.push({
      key: 'gluck-legacy',
      sku: 'gluck',
      lot_number: o.lot_number_gluck || '',
      expiration: isoToDmy(o.expiration_gluck),
      qty: String(o.qty_gluck),
    });
  }
  if ((o.qty_tp50 || 0) > 0) {
    lines.push({
      key: 'tp50-legacy',
      sku: 'tp50',
      lot_number: o.lot_number_tp50 || '',
      expiration: isoToDmy(o.expiration_tp50),
      qty: String(o.qty_tp50),
    });
  }
  return lines;
}

function glcLotLinesToPayload(lines) {
  return (lines || [])
    .filter((l) => l.lot_number?.trim() && (parseInt(l.qty, 10) || 0) > 0)
    .map((l) => ({
      sku: l.sku,
      lot_number: l.lot_number.trim(),
      expiration_date: dmyToIso(l.expiration),
      qty: parseInt(l.qty, 10) || 0,
    }));
}

function glcLotLinesUiErrors(order, lines) {
  if (!order) return ['Нет заявки'];
  const errors = [];
  const payload = glcLotLinesToPayload(lines);
  for (const sku of ['gluck', 'tp50']) {
    const need = sku === 'gluck' ? (order.qty_gluck || 0) : (order.qty_tp50 || 0);
    const label = sku === 'gluck' ? 'Глюкометр' : 'ТП50';
    if (need <= 0) continue;
    const rows = (lines || []).filter((l) => l.sku === sku);
    if (!rows.length) {
      errors.push(`${label}: добавьте лот`);
      continue;
    }
    for (const row of rows) {
      if (!row.lot_number?.trim()) errors.push(`${label}: выберите лот`);
      if (!dmyToIso(row.expiration)) errors.push(`${label}: срок лота ${row.lot_number || '—'}`);
      if ((parseInt(row.qty, 10) || 0) <= 0) errors.push(`${label}: qty > 0`);
    }
    const sum = payload.filter((l) => l.sku === sku).reduce((s, l) => s + l.qty, 0);
    if (sum !== need) errors.push(`${label}: сумма лотов ${sum} ≠ ${need}`);
    const lots = payload.filter((l) => l.sku === sku).map((l) => l.lot_number);
    if (new Set(lots).size !== lots.length) errors.push(`${label}: дубли лотов`);
  }
  return [...new Set(errors)];
}

function applyGlcReservations(stockLots, reservedLots) {
  const held = new Map();
  for (const row of reservedLots || []) {
    held.set(`${row.sku}|${String(row.lot_number || '').toUpperCase()}`, row);
  }
  const out = { gluck: [], tp50: [] };
  for (const sku of ['gluck', 'tp50']) {
    out[sku] = (stockLots[sku] || []).map((lot) => {
      const hit = held.get(`${sku}|${String(lot.lot_number || '').toUpperCase()}`);
      const reserved = hit?.qty || 0;
      return {
        ...lot,
        on_hand: lot.qty,
        reserved,
        reserved_orders: hit?.orders || [],
        qty: Math.max(0, (lot.qty || 0) - reserved),
      };
    });
  }
  return out;
}

function lotsFromInventory(snapshot) {
  const bySku = { gluck: [], tp50: [] };
  for (const item of snapshot?.items || []) {
    if (item.sku === 'gluck' || item.sku === 'tp50') {
      bySku[item.sku] = [...(item.lots || [])].sort((a, b) => {
        const ae = a.expiration_date ? String(a.expiration_date).slice(0, 10) : '9999-99-99';
        const be = b.expiration_date ? String(b.expiration_date).slice(0, 10) : '9999-99-99';
        if (ae !== be) return ae.localeCompare(be);
        return String(a.lot_number || '').localeCompare(String(b.lot_number || ''));
      });
    }
  }
  return bySku;
}

function openPdfBlob(blob, filename, { autoPrint = true } = {}) {
  const url = URL.createObjectURL(blob);
  // Landscape PDF: iframe.print() на macOS часто ставит Portrait и крутит страницу.
  // Открываем в вкладке — ориентация видна как в файле.
  if (!autoPrint) {
    const w = window.open(url, '_blank', 'noopener');
    if (!w) {
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
    }
    setTimeout(() => URL.revokeObjectURL(url), 120000);
    return;
  }
  const iframe = document.createElement('iframe');
  iframe.setAttribute('title', 'label-pdf');
  Object.assign(iframe.style, {
    position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: 'none',
  });
  iframe.src = url;
  document.body.appendChild(iframe);
  iframe.onload = () => {
    setTimeout(() => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } catch {
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
      }
    }, 400);
  };
  setTimeout(() => URL.revokeObjectURL(url), 120000);
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 120000);
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function monthStartIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function presetAccountingRange(preset) {
  const today = todayIso();
  if (preset === 'today') return { from: today, to: today };
  if (preset === 'month') return { from: monthStartIso(), to: today };
  if (preset === 'last_month') {
    const d = new Date();
    d.setDate(1);
    d.setHours(12, 0, 0, 0);
    d.setDate(0);
    const to = d.toISOString().slice(0, 10);
    d.setDate(1);
    const from = d.toISOString().slice(0, 10);
    return { from, to };
  }
  return null;
}

function formatIsoRu(iso) {
  if (!iso) return '';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : String(iso);
}

const STATUS_LABEL = {
  NEW: 'Новая',
  ASSEMBLING: 'Сборка',
  ASSEMBLED: 'Собран',
  IN_TRANSIT: 'В пути',
  DELIVERED: 'Доставлен',
};

const WAREHOUSE_LABEL = { F: 'Фергана', T: 'Ташкент', R: 'Фергана — резерв тендера' };

function warehouseLabel(code) {
  return WAREHOUSE_LABEL[code] || code || 'Фергана';
}

const CGM_FLOW = ['NEW', 'ASSEMBLING', 'ASSEMBLED', 'IN_TRANSIT', 'DELIVERED'];
const GLC_FLOW = ['NEW', 'ASSEMBLED', 'IN_TRANSIT', 'DELIVERED'];

function statusFlow(orderType) {
  return orderType === 'glucometer' ? GLC_FLOW : CGM_FLOW;
}

function canChangeToStatus(role, from, to, orderType) {
  const flow = statusFlow(orderType);
  if (!flow.includes(to) || to === from) return false;
  if (role === 'admin') return true;

  const fromIdx = flow.indexOf(from);
  const toIdx = flow.indexOf(to);
  const isForward = toIdx === fromIdx + 1;
  const isBackward = toIdx === fromIdx - 1;

  if (role === 'warehouse') {
    if (isForward || isBackward) return true;
    if (toIdx > fromIdx && (to === 'IN_TRANSIT' || to === 'DELIVERED')) return true;
    return false;
  }

  if (isBackward) {
    if (role === 'driver' && to === 'IN_TRANSIT' && from === 'DELIVERED') return true;
    if (role === 'driver' && to === 'ASSEMBLED' && from === 'IN_TRANSIT') return true;
    return false;
  }

  if (!isForward) return false;
  if (role === 'driver' && to === 'IN_TRANSIT' && from === 'ASSEMBLED') return true;
  if (role === 'driver' && to === 'DELIVERED' && from === 'IN_TRANSIT') return true;
  return false;
}

/** Следующий шаг вперёд по воронке (если роль позволяет). */
function nextForwardStatus(role, order) {
  if (!order) return null;
  const flow = statusFlow(order.order_type);
  const idx = flow.indexOf(order.status);
  if (idx < 0 || idx >= flow.length - 1) return null;
  const next = flow[idx + 1];
  if (order.is_shipment_linked && (next === 'IN_TRANSIT' || next === 'DELIVERED')) return null;
  return canChangeToStatus(role, order.status, next, order.order_type) ? next : null;
}

/** Быстрый переход с карточки: без формы лота/скана. */
function isCardQuickStatus(from, to) {
  return (from === 'ASSEMBLED' && to === 'IN_TRANSIT')
    || (from === 'IN_TRANSIT' && to === 'DELIVERED');
}

function shipmentPrimaryTitle(primary) {
  if (!primary) return '';
  const prefix = primary.order_type === 'glucometer' ? 'ARIA' : 'CGM';
  return `${prefix} #${primary.external_order_id}`;
}

function localYmd(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function orderDateYmd(order) {
  const raw = order?.order_date;
  if (!raw) return '';
  const s = String(raw).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : '';
}

function formatOrderDate(order) {
  return isoToDmy(order?.order_date) || '';
}

function normalizeSearch(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/^\s*#/, '')
    .replace(/[\s\-()]+/g, '');
}

function orderMatchesSearch(order, query) {
  const q = String(query || '').trim();
  if (!q) return true;
  const qNorm = normalizeSearch(q);
  const qLower = q.toLowerCase();
  const hay = [
    order.external_order_id,
    order.client_name,
    order.phone_rec,
    order.name_rec,
    order.city,
    order.address,
    order.client_inn,
  ].map((v) => String(v || '').toLowerCase());
  if (hay.some((h) => h.includes(qLower))) return true;
  // телефон / id без пробелов и дефисов
  return hay.some((h) => normalizeSearch(h).includes(qNorm));
}

/** IN_TRANSIT дольше 24ч (по shipped_at, иначе updated_at). */
function isStuckInTransit(order, nowMs = Date.now()) {
  if (order?.status !== 'IN_TRANSIT') return false;
  const raw = order.shipped_at || order.updated_at;
  if (!raw) return false;
  const t = new Date(raw).getTime();
  if (Number.isNaN(t)) return false;
  return (nowMs - t) >= 24 * 60 * 60 * 1000;
}

function stuckHours(order, nowMs = Date.now()) {
  const raw = order?.shipped_at || order?.updated_at;
  if (!raw) return 0;
  const t = new Date(raw).getTime();
  if (Number.isNaN(t)) return 0;
  return Math.floor((nowMs - t) / (60 * 60 * 1000));
}

function OrdersFilterBar({
  statusFilter,
  onStatusChange,
  dateFilter,
  onDateChange,
  searchQuery,
  onSearchChange,
  attentionFilter,
  onAttentionChange,
  statusCounts,
  dateCounts,
  stuckCount,
  flowStatuses,
}) {
  return (
    <div className="orders-filters">
      <div className="orders-filter-row orders-filter-search-row">
        <span className="orders-filter-label">Поиск</span>
        <div className="orders-search-wrap">
          <input
            type="search"
            className="orders-search-input"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="#ID, клиент, телефон…"
            enterKeyHint="search"
            autoComplete="off"
          />
          {searchQuery && (
            <button
              type="button"
              className="orders-search-clear"
              onClick={() => onSearchChange('')}
              aria-label="Очистить поиск"
            >
              ×
            </button>
          )}
        </div>
      </div>
      <div className="orders-filter-row">
        <span className="orders-filter-label">Период</span>
        <div className="chip-filter orders-filter-chips">
          {[
            { code: '', label: 'Все', count: dateCounts.all },
            { code: 'today', label: 'Сегодня', count: dateCounts.today },
            { code: 'yesterday', label: 'Вчера', count: dateCounts.yesterday },
          ].map((f) => (
            <button
              type="button"
              key={f.code || 'all-date'}
              className={dateFilter === f.code ? 'active' : ''}
              onClick={() => onDateChange(f.code)}
            >
              {f.label}
              {f.count > 0 && <span className="chip-count">{f.count}</span>}
            </button>
          ))}
        </div>
      </div>
      <div className="orders-filter-row">
        <span className="orders-filter-label">Внимание</span>
        <div className="chip-filter orders-filter-chips">
          <button
            type="button"
            className={`attention-chip ${attentionFilter === 'stuck24' ? 'active' : ''}`}
            onClick={() => onAttentionChange(attentionFilter === 'stuck24' ? '' : 'stuck24')}
          >
            Не доставлено &gt;24ч
            {stuckCount > 0 && <span className="chip-count">{stuckCount}</span>}
          </button>
        </div>
      </div>
      <div className="orders-filter-row">
        <span className="orders-filter-label">Статус</span>
        <div className="chip-filter orders-filter-chips">
          <button
            type="button"
            className={!statusFilter ? 'active' : ''}
            onClick={() => onStatusChange('')}
          >
            Все
            {statusCounts.all > 0 && <span className="chip-count">{statusCounts.all}</span>}
          </button>
          {flowStatuses.map((st) => (
            <button
              type="button"
              key={st}
              className={`status-chip status-${st} ${statusFilter === st ? 'active' : ''}`}
              onClick={() => onStatusChange(st)}
            >
              {STATUS_LABEL[st]}
              {(statusCounts[st] || 0) > 0 && (
                <span className="chip-count">{statusCounts[st]}</span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function OrderStatusBar({
  order,
  role,
  onStep,
  glcCanAssemble,
  glcStockOk = true,
  isComplete,
}) {
  const flow = statusFlow(order.order_type);
  const currentIdx = flow.indexOf(order.status);
  const snBypassOk = Boolean(order.sn_bypass) || role === 'admin';
  const next = nextForwardStatus(role, order);

  const stepDisabled = (st) => {
    if (st === order.status) return true;
    if (!canChangeToStatus(role, order.status, st, order.order_type)) return true;
    if (
      order.order_type === 'glucometer'
      && ['ASSEMBLED', 'IN_TRANSIT', 'DELIVERED'].includes(st)
      && !['IN_TRANSIT', 'DELIVERED'].includes(order.status)
      && (!glcCanAssemble || !glcStockOk)
    ) {
      return true;
    }
    if (
      order.order_type !== 'glucometer'
      && ['ASSEMBLED', 'IN_TRANSIT', 'DELIVERED'].includes(st)
      && !['IN_TRANSIT', 'DELIVERED'].includes(order.status)
      && !isComplete
      && !snBypassOk
    ) {
      return true;
    }
    if (
      order.is_shipment_linked
      && ['IN_TRANSIT', 'DELIVERED'].includes(st)
      && !['IN_TRANSIT', 'DELIVERED'].includes(order.status)
    ) {
      return true;
    }
    return false;
  };

  const nextDisabled = !next || stepDisabled(next);

  return (
    <div className="status-bar-wrap">
      {next && (
        <button
          type="button"
          className="btn-primary status-next-cta"
          disabled={nextDisabled}
          onClick={() => onStep(next)}
        >
          → {STATUS_LABEL[next]}
        </button>
      )}
      <div className="status-bar">
        {flow.map((st, i) => (
          <React.Fragment key={st}>
            {i > 0 && <span className={`status-connector ${i <= currentIdx ? 'done' : ''}`} />}
            <button
              type="button"
              className={`status-step status-${st} ${i < currentIdx ? 'passed' : ''} ${i === currentIdx ? 'current' : ''}`}
              disabled={stepDisabled(st)}
              onClick={() => onStep(st)}
              title={st === order.status ? 'Текущий статус' : `Перейти: ${STATUS_LABEL[st]}`}
            >
              {STATUS_LABEL[st]}
            </button>
          </React.Fragment>
        ))}
      </div>
      {order.sn_bypass && (
        <p className="hint-small status-bypass-note">
          Без скана SN{order.sn_bypass_note ? `: ${order.sn_bypass_note}` : ''}
        </p>
      )}
      {order.is_shipment_linked && (
        <p className="hint-small status-bar-hint">
          Отгрузка через primary {shipmentPrimaryTitle(order.shipment_primary)
            || `#${order.shipment_primary_id}`}
          — статус и списание идут с неё.
        </p>
      )}
      {order.is_shipment_primary && (order.linked_orders?.length || 0) > 0 && (
        <p className="hint-small status-bar-hint">
          Primary цепочки ({(order.linked_orders || []).length + 1} заявок) — «В пути»/EMU двигают всех linked.
        </p>
      )}
      {role !== 'admin' && !order.is_shipment_linked && (
        <p className="hint-small status-bar-hint">Крупная кнопка — следующий шаг · клик по шагу — любой доступный статус</p>
      )}
    </div>
  );
}

const ROLE_LABEL = {
  warehouse: 'Завсклад',
  driver: 'Водитель',
  admin: 'Администратор',
  viewer: 'Просмотр',
};

const MONTH_NAMES = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

function monthLabel(monthKey) {
  const m = String(monthKey || '').match(/^(\d{4})-(\d{2})$/);
  if (!m) return monthKey;
  const idx = parseInt(m[2], 10) - 1;
  return `${MONTH_NAMES[idx] || m[2]} ${m[1]}`;
}

function monthNameOnly(monthKey) {
  const m = String(monthKey || '').match(/^(\d{4})-(\d{2})$/);
  if (!m) return monthKey;
  return MONTH_NAMES[parseInt(m[2], 10) - 1] || monthKey;
}

function localMonthKey(offset = 0) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function WarehouseContextBar({ value, onChange }) {
  const wh = value === 'T' || value === 'F' ? value : '';
  return (
    <div className="warehouse-filter warehouse-filter-global">
      <span className="warehouse-filter-label">Склад отгрузки:</span>
      <button type="button" className={wh === 'F' ? 'active' : ''} onClick={() => onChange('F')}>
        Фергана
      </button>
      <button type="button" className={wh === 'T' ? 'active' : ''} onClick={() => onChange('T')}>
        Ташкент
      </button>
      <button type="button" className={wh === '' ? 'active' : ''} onClick={() => onChange('')}>
        Все
      </button>
    </div>
  );
}

function fmtPcsBoxes(qty, boxSize) {
  const n = Math.max(0, Number(qty) || 0);
  const pcs = n.toLocaleString('ru-RU');
  const size = Number(boxSize) || 0;
  if (!size || n === 0) return `${pcs} шт`;
  const boxes = Math.floor(n / size);
  const rem = n % size;
  if (boxes <= 0) return `${pcs} шт`;
  if (rem) return `${pcs} шт · ${boxes.toLocaleString('ru-RU')} кор. + ${rem}`;
  return `${pcs} шт · ${boxes.toLocaleString('ru-RU')} кор.`;
}

function isTenderPlanActive(p) {
  return !!p && p.plan_qty > 0 && (p.remaining_pack > 0 || p.remaining_ship > 0);
}

function parseTenderPlans(data) {
  if (!data) return [];
  if (Array.isArray(data.items)) return data.items.filter(isTenderPlanActive);
  if (isTenderPlanActive(data)) return [data];
  return [];
}

function TenderPlanBanner({ plan, compact = false }) {
  if (!plan || !(plan.plan_qty > 0)) return null;
  const boxSize = plan.box_size || BOX_QTY[plan.sku] || 0;
  const packDone = plan.remaining_pack <= 0;
  const shipDone = plan.remaining_ship <= 0;
  return (
    <section className={`tender-plan${compact ? ' compact' : ''}`}>
      <div className="tender-plan-head">
        <span className="tender-plan-title">План тендера · {plan.name || 'ТП50'}</span>
        <span className="tender-plan-goal">
          {plan.plan_qty.toLocaleString('ru-RU')} шт
          {boxSize ? ` · ${boxSize} шт/кор.` : ''}
        </span>
      </div>
      <div className="tender-plan-cols">
        <div className={`tender-plan-col is-pack${packDone ? ' is-done' : ''}`}>
          <div className="tender-plan-label">Осталось упаковать</div>
          <div className="tender-plan-remain">
            {packDone
              ? (plan.pack_over > 0 ? `сверх +${plan.pack_over.toLocaleString('ru-RU')}` : 'готово')
              : plan.remaining_pack.toLocaleString('ru-RU')}
          </div>
          {!packDone && (
            <div className="tender-plan-boxes">{fmtPcsBoxes(plan.remaining_pack, boxSize)}</div>
          )}
          <div className="tender-plan-bar" aria-hidden>
            <div
              className="tender-plan-bar-fill pack"
              style={{ width: `${Math.min(100, plan.pack_pct || 0)}%` }}
            />
          </div>
          <div className="tender-plan-meta">
            упаковано {(plan.packed_qty || 0).toLocaleString('ru-RU')} шт
            {' · '}{plan.pack_pct || 0}%
          </div>
        </div>
        <div className={`tender-plan-col is-ship${shipDone ? ' is-done' : ''}`}>
          <div className="tender-plan-label">Осталось отгрузить</div>
          <div className="tender-plan-remain">
            {shipDone
              ? (plan.ship_over > 0 ? `сверх +${plan.ship_over.toLocaleString('ru-RU')}` : 'готово')
              : plan.remaining_ship.toLocaleString('ru-RU')}
          </div>
          {!shipDone && (
            <div className="tender-plan-boxes">{fmtPcsBoxes(plan.remaining_ship, boxSize)}</div>
          )}
          <div className="tender-plan-bar" aria-hidden>
            <div
              className="tender-plan-bar-fill ship"
              style={{ width: `${Math.min(100, plan.ship_pct || 0)}%` }}
            />
          </div>
          <div className="tender-plan-meta">
            отгружено {(plan.shipped_qty || 0).toLocaleString('ru-RU')} шт
            {' · '}{plan.ship_pct || 0}%
          </div>
        </div>
      </div>
      {(plan.on_hand || 0) > 0 && (
        <div className="tender-plan-onhand">
          На складе R готово к отгрузке: <b>{fmtPcsBoxes(plan.on_hand, boxSize)}</b>
        </div>
      )}
    </section>
  );
}

function StatsDashboard({ token, logout, warehouse = '', embedded = false }) {
  const [monthFilter, setMonthFilter] = useState('');
  const [data, setData] = useState(null);
  const [tenderPlans, setTenderPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadStats = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (warehouse) params.set('warehouse', warehouse);
      if (monthFilter) params.set('month', monthFilter);
      const [res, planRes] = await Promise.all([
        apiFetch(token, `/stats/shipping-monthly?${params}`),
        apiFetch(token, '/stock/tender/plan'),
      ]);
      if (res.status === 401 || planRes.status === 401) { logout(); return; }
      const json = await res.json();
      if (!res.ok) {
        setError(json.error || 'Ошибка загрузки');
        setData(null);
        return;
      }
      setData(json);
      if (planRes.ok) {
        setTenderPlans(parseTenderPlans(await planRes.json()));
      }
    } catch {
      setError('Сервер недоступен');
    } finally {
      setLoading(false);
    }
  }, [token, monthFilter, logout, warehouse]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { setMonthFilter(''); }, [warehouse]);

  const products = data?.products || [];
  const totals = data?.totals || { i3: 0, i6: 0, gluck: 0, tp50: 0, orders: 0, total: 0 };
  const months = data?.months || [];
  const availableMonths = data?.availableMonths || [];
  const whLabel = warehouse ? warehouseLabel(warehouse) : 'все склады';
  const periodLabel = monthFilter ? monthLabel(monthFilter) : 'все периоды';
  const maxByKey = products.reduce((acc, p) => {
    acc[p.key] = Math.max(1, ...months.map((m) => m[p.key] || 0));
    return acc;
  }, {});

  return (
    <div className={`stats-page ${embedded ? 'stats-page-embedded' : ''}`}>
      <div className="stats-toolbar">
        <h2>{embedded ? 'Отгрузки' : 'Отгрузки по месяцам'}</h2>
      </div>

      <div className="stats-period-filter">
        <span className="stats-period-label">Период:</span>
        <button
          type="button"
          className={!monthFilter ? 'active' : ''}
          onClick={() => setMonthFilter('')}
        >
          Все
        </button>
        {availableMonths.map((mk) => (
          <button
            type="button"
            key={mk}
            className={monthFilter === mk ? 'active' : ''}
            onClick={() => setMonthFilter(mk)}
          >
            {monthLabel(mk)}
          </button>
        ))}
      </div>

      <p className="hint-small stats-note">
        {warehouse ? `Склад: ${whLabel}. ` : ''}
        Период: {periodLabel}. Заказы «В пути» и «Доставлен».
      </p>

      {tenderPlans.map((plan) => (
        <TenderPlanBanner key={plan.sku} plan={plan} compact={embedded} />
      ))}

      {loading && <p>Загрузка…</p>}
      {error && <p className="login-error">{error}</p>}

      {!loading && data && (
        <>
          <div className="stats-cards">
            {products.map((p) => (
              <div key={p.key} className="stats-card" style={{ borderTopColor: p.color }}>
                <div className="stats-card-title">{p.name}</div>
                <div className="stats-card-value" style={{ color: p.color }}>{totals[p.key] || 0}</div>
                <div className="stats-card-sub">шт · {periodLabel}</div>
              </div>
            ))}
            <div className="stats-card stats-card-total">
              <div className="stats-card-title">Заказов</div>
              <div className="stats-card-value">{totals.orders || 0}</div>
              <div className="stats-card-sub">{periodLabel}</div>
            </div>
          </div>

          {months.length === 0 ? (
            <p className="hint">Нет отгрузок{monthFilter ? ` за ${monthLabel(monthFilter)}` : ''}</p>
          ) : (
            <div className="stats-table-wrap">
              <table className="stats-table">
                <thead>
                  <tr>
                    <th>Месяц</th>
                    {products.map((p) => <th key={p.key}>{p.name}</th>)}
                    <th>Заказов</th>
                  </tr>
                </thead>
                <tbody>
                  {months.map((m) => (
                    <tr key={m.month_key}>
                      <td>{monthLabel(m.month_key)}</td>
                      {products.map((p) => (
                        <td key={p.key}>
                          <div className="stats-cell">
                            <span>{m[p.key] || 0}</span>
                            <div className="stats-bar-track">
                              <div
                                className="stats-bar-fill"
                                style={{
                                  width: `${((m[p.key] || 0) / maxByKey[p.key]) * 100}%`,
                                  background: p.color,
                                }}
                              />
                            </div>
                          </div>
                        </td>
                      ))}
                      <td>{m.orders}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td><strong>Итого {periodLabel}</strong></td>
                    {products.map((p) => <td key={p.key}><strong>{totals[p.key] || 0}</strong></td>)}
                    <td><strong>{totals.orders}</strong></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function apiFetch(token, path, options = {}) {
  const headers = { ...options.headers };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (options.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  return fetch(`${API_BASE}${path}`, { ...options, headers });
}

const SKU_LABEL = { i3: 'i3', i6: 'i6', gluck: 'Глюкометр', tp50: 'ТП50' };
const TRANSFER_STATUS_LABEL = {
  DRAFT: 'Черновик',
  SENT: 'Отправлено',
  RECEIVED: 'Принято',
  CANCELLED: 'Отменено',
};

const STOCK_SKUS = ['i3', 'i6', 'gluck', 'tp50'];
const WH_SHORT = { F: 'Фергана', R: 'Тендер', T: 'Ташкент' };
const BOX_SIZE = 416;
const BOX_QTY = { i3: 24, i6: 36, gluck: 75, tp50: 416 };
const DEFAULT_BOXES_PER_PALLET = 20;
const TENDER_TRUCK_PALLETS = 20;
const MOVEMENT_TYPE_LABEL = {
  receipt: 'Приход',
  expense: 'Расход',
  return: 'Возврат',
  transfer_out: 'Перемещение −',
  transfer_in: 'Перемещение +',
  adjustment: 'Корректировка',
};

function fmtDateTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

function SinocareReconPanel({ recon }) {
  if (!recon) {
    return <p className="hint-small">Нет данных сверки</p>;
  }
  const skuName = (sku) => ({ i3: 'iCan i3', i6: 'iCan i6' }[sku] || sku);
  return (
    <div>
      <p className="hint-small">{recon.note}</p>
      <div className="accounting-table-scroll">
        <table className="stats-table accounting-table">
          <thead>
            <tr>
              <th>SKU</th>
              <th>2300</th>
              <th>Заявки</th>
              <th>Наивный остаток</th>
              <th>Live F+T</th>
              <th>Клиент</th>
              <th>F→T</th>
              <th>Маркетинг</th>
              <th>Разрыв</th>
              <th>Журнал−баланс</th>
            </tr>
          </thead>
          <tbody>
            {(recon.identity || []).map((r) => (
              <tr key={r.sku}>
                <td><b>{skuName(r.sku)}</b></td>
                <td>{r.seed_2300}</td>
                <td>{r.orders_shipped_incl_ft}</td>
                <td>{r.naive_remain}</td>
                <td><b>{r.live_ft}</b> <span className="cell-muted">({r.qty_f}+{r.qty_t})</span></td>
                <td>{r.customer_shipped}</td>
                <td>{r.transfer_ft_qty}</td>
                <td>{r.expense_marketing}</td>
                <td className={r.gap_naive_vs_live ? 'qty-neg' : ''}>{r.gap_naive_vs_live}</td>
                <td className={r.gap_journal_vs_balance ? 'qty-neg' : ''}>{r.gap_journal_vs_balance}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(recon.gaps || []).length > 0 && (
        <>
          <p className="hint-small">Заявки, где qty ≠ журнал</p>
          <div className="accounting-table-scroll">
            <table className="stats-table accounting-table">
              <thead>
                <tr>
                  <th>Заявка</th><th>Статус</th><th>Склад</th><th>Тип</th>
                  <th>qty i3/i6</th><th>нетто</th><th>expense</th><th>Почему</th>
                </tr>
              </thead>
              <tbody>
                {recon.gaps.map((g) => (
                  <tr key={g.id}>
                    <td>#{g.external_order_id}</td>
                    <td>{g.status}</td>
                    <td>{g.warehouse_code}</td>
                    <td>{g.stock_action}</td>
                    <td>{g.qty_i3}/{g.qty_i6}</td>
                    <td>{g.net_i3}/{g.net_i6}</td>
                    <td>{g.exp_i3}/{g.exp_i6}</td>
                    <td>{g.why}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function filenameFromDisposition(res, fallback) {
  const cd = res.headers.get('content-disposition') || '';
  const match = cd.match(/filename="?([^";]+)"?/i);
  return match ? match[1] : fallback;
}

function AccountingPage({ token, logout }) {
  const [section, setSection] = useState('movements');
  const [preset, setPreset] = useState('month');
  const [range, setRange] = useState(() => presetAccountingRange('month'));
  const [warehouse, setWarehouse] = useState('');
  const [view, setView] = useState('summary');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [invScope, setInvScope] = useState('raw');
  const [invSku, setInvSku] = useState('');
  const [invZeros, setInvZeros] = useState(false);
  const [invBusy, setInvBusy] = useState('');
  // период инвентаризации: '' = остаток на сейчас, YYYY-MM = месяц, 'custom' = свои даты
  const [invMonth, setInvMonth] = useState('');
  const [invRange, setInvRange] = useState({ from: '', to: '' });
  const [error, setError] = useState('');
  const isTender = warehouse === 'R';
  const isInventory = section === 'inventory';

  const loadReport = useCallback(async () => {
    if (!token || !range.from || !range.to || section !== 'movements') return;
    if (range.from > range.to) {
      setError('Дата начала позже даты конца');
      setData(null);
      return;
    }
    setLoading(true);
    setError('');
    setData(null);
    try {
      const params = new URLSearchParams({ from: range.from, to: range.to, limit: '500' });
      if (warehouse) params.set('warehouse', warehouse);
      const res = await apiFetch(token, `/stock/accounting-report?${params}`);
      if (res.status === 401) { logout(); return; }
      const json = await res.json();
      if (!res.ok) {
        setError(json.error || 'Ошибка загрузки');
        setData(null);
        return;
      }
      setData(json);
    } catch {
      setError('Сервер недоступен');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token, range, warehouse, logout, section]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const applyPreset = (nextPreset) => {
    setPreset(nextPreset);
    const nextRange = presetAccountingRange(nextPreset);
    if (nextRange) setRange(nextRange);
  };

  const downloadExcel = async () => {
    if (!token || !range.from || !range.to) return;
    setExportLoading(true);
    try {
      const params = new URLSearchParams({ from: range.from, to: range.to });
      if (warehouse) params.set('warehouse', warehouse);
      const res = await apiFetch(token, `/stock/accounting-report.xlsx?${params}`);
      if (res.status === 401) { logout(); return; }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setError(err.error || 'Не удалось выгрузить Excel');
        return;
      }
      const blob = await res.blob();
      downloadBlob(blob, `accounting-${range.from}-${range.to}-${warehouse || 'all'}.xlsx`);
    } finally {
      setExportLoading(false);
    }
  };

  const openTenderProductionPdf = async (productionId) => {
    const res = await apiFetch(token, `/raw/productions/${productionId}/report.pdf`);
    if (res.status === 401) { logout(); return; }
    if (!res.ok) {
      const json = await res.json().catch(() => ({}));
      setError(json.error || 'Не удалось открыть PDF выработки');
      return;
    }
    const blob = await res.blob();
    openPdfBlob(blob, filenameFromDisposition(res, `production-${productionId}.pdf`), { autoPrint: false });
  };

  const invMonths = useMemo(
    () => Array.from({ length: 12 }, (_, i) => localMonthKey(-i)),
    [],
  );
  const invIsCustom = invMonth === 'custom';
  const invPeriodParams = () => {
    const qs = new URLSearchParams();
    if (invIsCustom) {
      if (invRange.from) qs.set('from', invRange.from);
      if (invRange.to) qs.set('to', invRange.to);
    } else if (invMonth) {
      qs.set('month', invMonth);
    }
    return qs;
  };
  const invPeriodLabel = invIsCustom
    ? (invRange.from || invRange.to
      ? `${invRange.from ? formatIsoRu(invRange.from) : '…'} — ${invRange.to ? formatIsoRu(invRange.to) : 'сейчас'}`
      : 'свой период (укажите даты)')
    : (invMonth ? monthLabel(invMonth) : 'остаток на сейчас');

  const inventoryPath = (format) => {
    const qs = invPeriodParams();
    if (invScope === 'raw') {
      if (invZeros) qs.set('zeros', '1');
      qs.set('_t', String(Date.now()));
      return `/raw/inventory.${format}?${qs}`;
    }
    qs.set('warehouse', invScope === 'gp-T' ? 'T' : 'F');
    if (invSku) qs.set('sku', invSku);
    qs.set('_t', String(Date.now()));
    return `/stock/inventory.${format}?${qs}`;
  };

  const exportInventory = async (format) => {
    if (!token) return;
    if (invIsCustom && invRange.from && invRange.to && invRange.from > invRange.to) {
      setError('Дата начала позже даты конца');
      return;
    }
    if (invIsCustom && !invRange.from && !invRange.to) {
      setError('Укажите даты периода или выберите месяц');
      return;
    }
    setInvBusy(format);
    setError('');
    try {
      const res = await apiFetch(token, inventoryPath(format));
      if (res.status === 401) { logout(); return; }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setError(err.error || `Не удалось выгрузить ${format.toUpperCase()}`);
        return;
      }
      const blob = await res.blob();
      const fallback = format === 'pdf' ? 'inventarizaciya.pdf' : 'inventarizaciya.xlsx';
      const name = filenameFromDisposition(res, fallback);
      if (format === 'pdf') {
        openPdfBlob(blob, name, { autoPrint: false });
      } else {
        downloadBlob(blob, name);
      }
    } catch {
      setError('Сервер недоступен');
    } finally {
      setInvBusy('');
    }
  };

  const totals = {
    receipt: Number(data?.totals?.receipt) || 0,
    expense: Number(data?.totals?.expense) || 0,
    return: Number(data?.totals?.return) || 0,
    transfer_in: Number(data?.totals?.transfer_in) || 0,
    transfer_out: Number(data?.totals?.transfer_out) || 0,
    adjustment: Number(data?.totals?.adjustment) || 0,
    net: Number(data?.totals?.net) || 0,
  };

  const invScopeLabel = invScope === 'raw'
    ? 'Сырьё (Фергана)'
    : invScope === 'gp-T'
      ? 'ГП Ташкент'
      : 'ГП Фергана';

  return (
    <div className="accounting-page">
      <div className="accounting-head">
        <div>
          <h2>Бухгалтерия</h2>
          <p className="hint-small">
            {isInventory
              ? 'Остатки на сейчас или на конец месяца / своего периода: PDF — чеклист обхода, Excel — для бухгалтера (пустые колонки «Факт»).'
              : isTender
                ? 'Тендерные лоты по датам производства и документы из журнала выработок.'
                : 'Период — приход/расход. Excel: Итоги, По дням, Операции (i3/i6/ГЛК/ТП50 + ИНН), вкладки по SKU, Сверка CGM. Сверка i3/i6 — весь срок, не фильтр дат.'}
          </p>
        </div>
        {!isInventory && !isTender && (
          <button
            type="button"
            className="btn-primary"
            onClick={downloadExcel}
            disabled={exportLoading || loading}
          >
            {exportLoading ? 'Готовлю Excel…' : '⬇ Скачать Excel'}
          </button>
        )}
      </div>

      <div className="accounting-view-tabs">
        <button
          type="button"
          className={!isInventory ? 'active' : ''}
          onClick={() => { setSection('movements'); setError(''); }}
        >
          Обороты
        </button>
        <button
          type="button"
          className={isInventory ? 'active' : ''}
          onClick={() => { setSection('inventory'); setError(''); }}
        >
          Инвентаризация
        </button>
      </div>

      {isInventory ? (
        <>
          <div className="accounting-filters">
            <div className="chip-filter">
              {[
                { id: 'raw', label: 'Сырьё (Фергана)' },
                { id: 'gp-F', label: 'ГП Фергана' },
                { id: 'gp-T', label: 'ГП Ташкент' },
              ].map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className={invScope === s.id ? 'active' : ''}
                  onClick={() => setInvScope(s.id)}
                >
                  {s.label}
                </button>
              ))}
            </div>
            {invScope !== 'raw' && (
              <div className="chip-filter">
                {[
                  { id: '', label: 'ГП все' },
                  { id: 'gluck', label: 'Глюкометры' },
                  { id: 'tp50', label: 'ТП50' },
                ].map((s) => (
                  <button
                    key={s.id || 'all'}
                    type="button"
                    className={invSku === s.id ? 'active' : ''}
                    onClick={() => setInvSku(s.id)}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}
            {invScope === 'raw' && (
              <label className="accounting-check">
                <input
                  type="checkbox"
                  checked={invZeros}
                  onChange={(e) => setInvZeros(e.target.checked)}
                />
                Включая нулевые остатки
              </label>
            )}
            <div className="chip-filter">
              <button
                type="button"
                className={!invMonth ? 'active' : ''}
                onClick={() => { setInvMonth(''); setInvRange({ from: '', to: '' }); setError(''); }}
              >
                Сейчас
              </button>
              {invMonths.map((mk) => (
                <button
                  key={mk}
                  type="button"
                  className={invMonth === mk ? 'active' : ''}
                  onClick={() => { setInvMonth(mk); setInvRange({ from: '', to: '' }); setError(''); }}
                >
                  {monthLabel(mk)}
                </button>
              ))}
              <button
                type="button"
                className={invIsCustom ? 'active' : ''}
                onClick={() => { setInvMonth('custom'); setError(''); }}
              >
                Свой период
              </button>
            </div>
            {invIsCustom && (
              <>
                <label>С
                  <input
                    type="date"
                    value={invRange.from}
                    onChange={(e) => setInvRange((prev) => ({ ...prev, from: e.target.value }))}
                  />
                </label>
                <label>По
                  <input
                    type="date"
                    value={invRange.to}
                    onChange={(e) => setInvRange((prev) => ({ ...prev, to: e.target.value }))}
                  />
                </label>
              </>
            )}
            <div className="accounting-actions">
              <button
                type="button"
                className="btn-secondary"
                disabled={Boolean(invBusy)}
                onClick={() => exportInventory('pdf')}
              >
                {invBusy === 'pdf' ? 'PDF…' : '📄 PDF'}
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={Boolean(invBusy)}
                onClick={() => exportInventory('xlsx')}
              >
                {invBusy === 'xlsx' ? 'Excel…' : '⬇ Excel'}
              </button>
            </div>
          </div>

          {error && <div className="login-error accounting-error">{error}</div>}

          <div className="stock-card">
            <h3>Выгрузка: {invScopeLabel} — {invPeriodLabel}</h3>
            <p className="hint-small">
              {invMonth
                ? 'Остаток на конец выбранного периода (по журналу движений); период виден в титуле файла, шапке PDF и в имени файла. Плюс колонки «На начало» / «Приход» / «Расход» за период.'
                : 'Учётный снимок на момент скачивания.'}
              {' '}В Excel — колонки «Факт» / «Откл.» для заполнения вручную.
              PDF — landscape-чеклист для обхода склада.
              {invScope === 'raw'
                ? ' Сырьё одно — только Фергана (зоны карты обхода).'
                : ' Операционный PDF также в Склады → Остатки.'}
            </p>
          </div>
        </>
      ) : (
        <>
          <div className="accounting-filters">
            <div className="chip-filter">
              {[
                { id: 'today', label: 'Сегодня' },
                { id: 'month', label: 'Текущий месяц' },
                { id: 'last_month', label: 'Прошлый месяц' },
                { id: 'custom', label: 'Свой период' },
              ].map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={preset === p.id ? 'active' : ''}
                  onClick={() => applyPreset(p.id)}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <label>С
              <input
                type="date"
                value={range.from}
                onChange={(e) => {
                  setPreset('custom');
                  setRange((prev) => ({ ...prev, from: e.target.value }));
                }}
              />
            </label>
            <label>По
              <input
                type="date"
                value={range.to}
                onChange={(e) => {
                  setPreset('custom');
                  setRange((prev) => ({ ...prev, to: e.target.value }));
                }}
              />
            </label>
            <div className="chip-filter">
              {[
                { code: '', label: 'Все склады' },
                { code: 'F', label: 'Фергана' },
                { code: 'T', label: 'Ташкент' },
                { code: 'R', label: 'Тендер' },
              ].map((w) => (
                <button
                  key={w.code || 'all'}
                  type="button"
                  className={warehouse === w.code ? 'active' : ''}
                  onClick={() => setWarehouse(w.code)}
                >
                  {w.label}
                </button>
              ))}
            </div>
            <button type="button" className="btn-secondary" onClick={loadReport} disabled={loading}>
              {loading ? 'Загрузка…' : 'Обновить'}
            </button>
          </div>

          {error && <div className="login-error accounting-error">{error}</div>}

          {isTender ? (
            <div className="accounting-kpis accounting-tender-kpis">
              <div className="accounting-kpi">
                <span className="accounting-kpi-label">Лотов за период</span>
                <strong>{data?.totals?.lots ?? 0}</strong>
              </div>
              <div className="accounting-kpi">
                <span className="accounting-kpi-label">Произведено</span>
                <strong>{Number(data?.totals?.produced_qty || 0).toLocaleString('ru-RU')}</strong>
              </div>
              <div className="accounting-kpi">
                <span className="accounting-kpi-label">Документов PDF</span>
                <strong>{data?.totals?.documents ?? 0}</strong>
              </div>
            </div>
          ) : (
            <>
              <div className="accounting-kpis">
                <div className="accounting-kpi">
                  <span className="accounting-kpi-label">Приход</span>
                  <strong className="qty-pos">{totals.receipt.toLocaleString('ru-RU')}</strong>
                </div>
                <div className="accounting-kpi">
                  <span className="accounting-kpi-label">Расход</span>
                  <strong className="qty-neg">{totals.expense.toLocaleString('ru-RU')}</strong>
                </div>
                <div className="accounting-kpi">
                  <span className="accounting-kpi-label">Возврат</span>
                  <strong className="qty-pos">{totals.return.toLocaleString('ru-RU')}</strong>
                </div>
                <div className="accounting-kpi">
                  <span className="accounting-kpi-label">Перемещение +</span>
                  <strong>{totals.transfer_in.toLocaleString('ru-RU')}</strong>
                </div>
                <div className="accounting-kpi">
                  <span className="accounting-kpi-label">Перемещение −</span>
                  <strong>{totals.transfer_out.toLocaleString('ru-RU')}</strong>
                </div>
                <div className="accounting-kpi">
                  <span className="accounting-kpi-label">Нетто</span>
                  <strong>{totals.net.toLocaleString('ru-RU')}</strong>
                </div>
                <div className="accounting-kpi">
                  <span className="accounting-kpi-label">Операций</span>
                  <strong>{data?.operations_count ?? 0}</strong>
                </div>
              </div>

              <div className="accounting-view-tabs">
                <button type="button" className={view === 'summary' ? 'active' : ''} onClick={() => setView('summary')}>
                  Итоги по товару
                </button>
                <button type="button" className={view === 'days' ? 'active' : ''} onClick={() => setView('days')}>
                  По дням
                </button>
                <button type="button" className={view === 'operations' ? 'active' : ''} onClick={() => setView('operations')}>
                  Операции
                </button>
                <button type="button" className={view === 'recon' ? 'active' : ''} onClick={() => setView('recon')}>
                  Сверка CGM
                </button>
              </div>
            </>
          )}

          {loading && !data && <p className="hint-small">Загрузка отчета…</p>}

          {!loading && data && isTender && (
            <div className="accounting-table-scroll">
              <table className="stats-table accounting-table accounting-tender-table">
                <thead>
                  <tr>
                    <th>Продукт</th><th>Лот</th><th>Даты производства</th>
                    <th>Произведено</th><th>Срок годности</th><th>Документ</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.lots || []).length === 0 ? (
                    <tr><td colSpan={6} className="cell-muted">Нет тендерных лотов с датой производства за период</td></tr>
                  ) : (data.lots || []).map((lot) => {
                    const expiration = lot.expiration_date
                      || [...new Set(
                        (lot.batches || []).map((batch) => batch.expiration_date).filter(Boolean),
                      )][0]
                      || null;
                    const invalidExpiration = Boolean(
                      expiration
                      && (lot.batches || []).some((batch) => (
                        batch.production_date && expiration < batch.production_date
                      )),
                    );
                    return (
                      <tr key={`${lot.sku}-${lot.lot_number}`}>
                        <td><b>{lot.sku_name}</b></td>
                        <td><b>{lot.lot_number}</b></td>
                        <td>
                          <div className="accounting-tender-batches">
                            {(lot.batches || []).map((batch) => (
                              <span key={`${batch.production_date}-${batch.qty}`}>
                                {formatIsoRu(batch.production_date)}
                                {' · '}
                                <b>{Number(batch.qty).toLocaleString('ru-RU')} шт</b>
                              </span>
                            ))}
                          </div>
                        </td>
                        <td><b>{Number(lot.produced_qty).toLocaleString('ru-RU')}</b></td>
                        <td className={invalidExpiration ? 'accounting-date-warning' : ''}>
                          {expiration ? formatIsoRu(expiration) : '—'}
                          {invalidExpiration && (
                            <span className="accounting-date-warning-note">
                              ⚠ Проверить срок
                            </span>
                          )}
                        </td>
                        <td>
                          {lot.production_id ? (
                            <button
                              type="button"
                              className="btn-link"
                              onClick={() => openTenderProductionPdf(lot.production_id)}
                            >
                              PDF выработки #{lot.production_id}
                            </button>
                          ) : (
                            <span className="cell-muted">Нет в журнале</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!loading && data && !isTender && view === 'summary' && (
            <div className="accounting-table-scroll">
              <table className="stats-table accounting-table">
                <thead>
                  <tr>
                    <th>Склад</th><th>Продукт</th><th>Приход</th><th>Расход</th><th>Возврат</th>
                    <th>Перемещ. +</th><th>Перемещ. −</th><th>Корр.</th><th>Нетто</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.by_sku || []).length === 0 ? (
                    <tr><td colSpan={9} className="cell-muted">Нет операций за период</td></tr>
                  ) : (data.by_sku || []).map((row) => (
                    <tr key={`${row.warehouse_code}-${row.sku_code}`}>
                      <td>{row.warehouse}</td>
                      <td>{row.sku}</td>
                      <td>{row.receipt || '—'}</td>
                      <td>{row.expense || '—'}</td>
                      <td>{row.return || '—'}</td>
                      <td>{row.transfer_in || '—'}</td>
                      <td>{row.transfer_out || '—'}</td>
                      <td>{row.adjustment || '—'}</td>
                      <td><b>{row.net}</b></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && data && !isTender && view === 'days' && (
            <div className="accounting-table-scroll">
              <table className="stats-table accounting-table">
                <thead>
                  <tr>
                    <th>Дата</th><th>Склад</th><th>Продукт</th><th>Приход</th><th>Расход</th><th>Возврат</th>
                    <th>Перемещ. +</th><th>Перемещ. −</th><th>Корр.</th><th>Нетто</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.by_day || []).length === 0 ? (
                    <tr><td colSpan={10} className="cell-muted">Нет операций за период</td></tr>
                  ) : (data.by_day || []).map((row) => (
                    <tr key={`${row.date}-${row.warehouse_code}-${row.sku_code}`}>
                      <td>{formatIsoRu(row.date)}</td>
                      <td>{row.warehouse}</td>
                      <td>{row.sku}</td>
                      <td>{row.receipt || '—'}</td>
                      <td>{row.expense || '—'}</td>
                      <td>{row.return || '—'}</td>
                      <td>{row.transfer_in || '—'}</td>
                      <td>{row.transfer_out || '—'}</td>
                      <td>{row.adjustment || '—'}</td>
                      <td><b>{row.net}</b></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && data && !isTender && view === 'operations' && (
            <>
              {data.operations_truncated && (
                <p className="hint-small accounting-truncated">
                  Показаны первые {data.operations.length} из {data.operations_count} операций.
                  Полный список — в Excel.
                </p>
              )}
              <div className="accounting-table-scroll">
                <table className="stats-table accounting-table">
                  <thead>
                    <tr>
                      <th>Дата</th><th>Склад</th><th>Операция</th>
                      <th>i3</th><th>i6</th><th>ГЛК</th><th>ТП50</th>
                      <th>Приход</th><th>Расход</th><th>Клиент</th><th>ИНН</th>
                      <th>Документ-основание</th><th>Лот</th><th>Комментарий</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.operations || []).length === 0 ? (
                      <tr><td colSpan={14} className="cell-muted">Нет операций за период</td></tr>
                    ) : (data.operations || []).map((row) => (
                      <tr key={row.id}>
                        <td className="cell-nowrap">{fmtDateTime(row.created_at)}</td>
                        <td>{row.warehouse_name}</td>
                        <td>{row.type_name}</td>
                        <td>{row.sku === 'i3' ? row.qty : '—'}</td>
                        <td>{row.sku === 'i6' ? row.qty : '—'}</td>
                        <td>{row.sku === 'gluck' ? row.qty : '—'}</td>
                        <td>{row.sku === 'tp50' ? row.qty : '—'}</td>
                        <td className="qty-pos">{row.qty_in || '—'}</td>
                        <td className="qty-neg">{row.qty_out || '—'}</td>
                        <td>{row.client_name || '—'}</td>
                        <td>{row.client_inn || '—'}</td>
                        <td>{row.doc_basis || '—'}</td>
                        <td>{row.lot_number || '—'}</td>
                        <td>{row.comment || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {!loading && data && !isTender && view === 'recon' && (
            <SinocareReconPanel recon={data.sinocare_recon} />
          )}

          {data && (
            <p className="hint-small accounting-foot">
              Период: {formatIsoRu(data.from)} — {formatIsoRu(data.to)}
              {' · '}
              {isTender ? 'Отбор по датам производства' : `Склад: ${data.warehouse_name}`}
            </p>
          )}
        </>
      )}
    </div>
  );
}

function RawMaterialsPage(props) {
  return (
    <ProductionPage
      {...props}
      apiFetch={apiFetch}
      fmtDateTime={fmtDateTime}
      formatIsoRu={formatIsoRu}
      openPdfBlob={openPdfBlob}
    />
  );
}

const DEFAULT_TENDER_CLIENT =
  'O‘ZBEKISTON RESPUBLIKASI SOG‘LIQNI SAQLASH VAZIRLIGI HUZURIDAGI XARIDLAR MARKAZI DM';

const BOX_SKUS = ['gluck', 'tp50'];

function previewGluckReceipt(row) {
  const from = String(row?.sn_from || '').trim().toUpperCase();
  const to = String(row?.sn_to || '').trim().toUpperCase();
  const m1 = from.match(/^ARIA(\d+)$/);
  const m2 = to.match(/^ARIA(\d+)$/);
  if (!m1 || !m2 || m1[1].length !== m2[1].length || m1[1].length > 15) return '';
  const count = Number(m2[1]) - Number(m1[1]) + 1;
  if (!Number.isFinite(count) || count <= 0 || count > 100000) return '';
  return `${count.toLocaleString('ru-RU')} шт`;
}

function makeReceiptRows() {
  return {
    i3: { qty: '' },
    i6: { qty: '' },
    gluck: { qty: '', lot: '', exp: '', prod: '', sn_from: '', sn_to: '' },
    tp50: { qty: '', lot: '', exp: '', prod: '' },
  };
}

function makeTenderReceipt() {
  return {
    gluck: { qty: '', sn_from: '', sn_to: '', prod: '' },
    tp50: { qty: '', lot: '', exp: '', prod: '' },
  };
}

const TENDER_NO_LOT = '__NO_LOT__';

function isUnlabeledTenderGluck(m) {
  return m
    && m.warehouse_code === 'R'
    && m.sku === 'gluck'
    && !m.lot_number;
}

function tenderLotKey(sku, lot) {
  return `${sku}|${lot.unlabeled ? 'no-lot' : lot.lot_number}`;
}

function tenderPickKey(lot) {
  return lot.unlabeled ? TENDER_NO_LOT : lot.lot_number;
}

function sortLotsFefo(a, b) {
  const ea = a.expiration_date || '9999-12-31';
  const eb = b.expiration_date || '9999-12-31';
  if (ea !== eb) return String(ea).localeCompare(String(eb));
  return String(a.lot_number || '').localeCompare(String(b.lot_number || ''));
}

function fillTenderTruck(lots, sku, boxesPerPallet, targetPallets = TENDER_TRUCK_PALLETS) {
  const boxSize = BOX_QTY[sku] || BOX_SIZE;
  const perPallet = parseInt(boxesPerPallet, 10) || DEFAULT_BOXES_PER_PALLET;
  const targetBoxes = (parseInt(targetPallets, 10) || TENDER_TRUCK_PALLETS) * perPallet;
  let need = targetBoxes;
  const picks = {};
  for (const l of (lots || []).filter((x) => (x.qty || 0) > 0).slice().sort(sortLotsFefo)) {
    if (need <= 0) break;
    const lotBoxes = Math.floor((l.qty || 0) / boxSize);
    if (lotBoxes <= 0) continue;
    const take = Math.min(lotBoxes, need);
    picks[tenderPickKey(l)] = String(take * boxSize);
    need -= take;
  }
  return { picks, targetBoxes, filledBoxes: targetBoxes - need, shortBoxes: need };
}

/** Все лоты с остатком, включая неполные коробки. */
function fillTenderAllRemaining(lots) {
  const picks = {};
  let qty = 0;
  let lotsN = 0;
  for (const l of (lots || []).filter((x) => (x.qty || 0) > 0)) {
    picks[tenderPickKey(l)] = String(l.qty);
    qty += l.qty;
    lotsN += 1;
  }
  return { picks, qty, lots: lotsN };
}

function fmtPallets(boxes, perPallet) {
  const n = parseInt(boxes, 10) || 0;
  const p = parseInt(perPallet, 10) || 0;
  if (!p) return '';
  const full = Math.floor(n / p);
  const rem = n % p;
  if (n === 0) return '0 палет';
  if (rem === 0) return `${full} палет`;
  return `${full} палет + ${rem} кор.`;
}

function groupTenderShipmentRows(rows) {
  const groups = [];
  for (const m of rows || []) {
    const t = new Date(m.created_at).getTime();
    const last = groups[groups.length - 1];
    const sameTruck = last
      && last.client === (m.client_name || '')
      && last.sku === m.sku
      && Number.isFinite(t)
      && Number.isFinite(last.t)
      && Math.abs(last.t - t) <= 3000;
    if (sameTruck) {
      last.items.push(m);
      last.t = Math.min(last.t, t);
    } else {
      groups.push({
        t,
        client: m.client_name || '',
        sku: m.sku,
        items: [m],
      });
    }
  }
  return groups;
}

function splitBoxes(qty, sku = 'tp50') {
  const size = BOX_QTY[sku] || BOX_SIZE;
  const n = parseInt(qty, 10) || 0;
  if (!n) return { boxes: 0, rem: 0, size, label: '' };
  const abs = Math.abs(n);
  const boxes = Math.floor(abs / size);
  const rem = abs % size;
  let label;
  if (boxes === 0) label = `${rem} шт (неполная)`;
  else if (rem > 0) label = `${boxes} кор. × ${size} + ${rem} шт`;
  else label = `${boxes} кор. × ${size}`;
  return { boxes, rem, size, label };
}

function boxCount(qty, sku = 'tp50') {
  return splitBoxes(qty, sku).label || null;
}

function fmtBoxRange(from, to) {
  const a = parseInt(from, 10);
  const b = parseInt(to, 10);
  if (!a) return '';
  if (!b || b === a) return `№${a}`;
  return `№${a}–${b}`;
}

function previewTenderBoxes(sku, qty, tenderReserve) {
  const group = (tenderReserve || []).find((g) => g.sku === sku);
  const next = group?.next_box_number || 1;
  const size = BOX_QTY[sku] || BOX_SIZE;
  const n = parseInt(qty, 10) || 0;
  const boxes = Math.floor(n / size);
  if (boxes <= 0) return next > 1 ? `тендер: след. №${next}` : '';
  const range = fmtBoxRange(next, next + boxes - 1);
  return `тендер: ${boxes} кор. ${range}`;
}

function sortLotsNewestFirst(a, b) {
  const ta = a.updated_at || a.last_receipt_at || '';
  const tb = b.updated_at || b.last_receipt_at || '';
  if (ta !== tb) return String(tb).localeCompare(String(ta));
  return String(b.lot_number || '').localeCompare(String(a.lot_number || ''));
}

function QtyWithBoxes({ qty, sku, bold = false }) {
  const n = Number(qty) || 0;
  const label = n ? splitBoxes(n, sku).label : '';
  const shown = n ? n.toLocaleString('ru-RU') : '—';
  return (
    <>
      {bold ? <b>{shown}</b> : shown}
      {label ? <span className="stock-box-hint">{label}</span> : null}
    </>
  );
}

function groupLotsByNumber(lots) {
  const map = {};
  lots.forEach((l) => {
    if (!map[l.lot_number]) {
      map[l.lot_number] = { lot_number: l.lot_number, expiration_date: l.expiration_date, F: 0, R: 0, T: 0 };
    }
    map[l.lot_number][l.warehouse_code] = (map[l.lot_number][l.warehouse_code] || 0) + l.qty;
    if (!map[l.lot_number].expiration_date && l.expiration_date) {
      map[l.lot_number].expiration_date = l.expiration_date;
    }
  });
  return Object.values(map).sort((a, b) => a.lot_number.localeCompare(b.lot_number));
}

function orderLotLines(o, isGlc) {
  const lines = [];
  if (isGlc) {
    const raw = Array.isArray(o.glc_lot_lines) ? o.glc_lot_lines : [];
    if (raw.length) {
      for (const l of raw) {
        const label = l.sku === 'gluck' ? 'Гл' : 'ТП';
        const multi = raw.filter((x) => x.sku === l.sku).length > 1;
        lines.push(
          `${label}: ${multi ? `${l.qty}×` : ''}${l.lot_number}`
          + `${l.expiration_date ? ` · ${isoToDmy(l.expiration_date)}` : ''}`,
        );
      }
    } else {
      if (o.qty_gluck > 0 && o.lot_number_gluck) {
        lines.push(`Гл: ${o.lot_number_gluck}${o.expiration_gluck ? ` · ${isoToDmy(o.expiration_gluck)}` : ''}`);
      }
      if (o.qty_tp50 > 0 && o.lot_number_tp50) {
        lines.push(`ТП: ${o.lot_number_tp50}${o.expiration_tp50 ? ` · ${isoToDmy(o.expiration_tp50)}` : ''}`);
      }
    }
  } else {
    if (o.lot_number_i3) lines.push(`i3: ${o.lot_number_i3}`);
    if (o.lot_number_i6) lines.push(`i6: ${o.lot_number_i6}`);
  }
  return lines;
}

function orderQtyLines(o, isGlc) {
  if (isGlc) {
    const parts = [];
    if (o.qty_gluck > 0) parts.push(`Гл ${o.qty_gluck}`);
    if (o.qty_tp50 > 0) parts.push(`ТП ${o.qty_tp50}`);
    return parts;
  }
  const parts = [];
  if (o.qty_i3 > 0) parts.push(`i3 ${o.scanned_i3}/${o.qty_i3}`);
  if (o.qty_i6 > 0) parts.push(`i6 ${o.scanned_i6}/${o.qty_i6}`);
  return parts;
}

function StockPage({ token, logout, role }) {
  const savedUi = loadUiState();
  const [stockView, setStockView] = useState(() => {
    const v = savedUi.stockView || 'balances';
    // legacy: «Приход / Движения» держал журнал+перемещения — оставляем приход
    if (v === 'movements') return 'journal';
    return v;
  });
  const [balances, setBalances] = useState([]);
  const [inventorySnap, setInventorySnap] = useState(null);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [inventoryFlash, setInventoryFlash] = useState(false);
  const inventorySnapRef = useRef(null);
  const [lotSummary, setLotSummary] = useState(null);
  const [stockLots, setStockLots] = useState([]);
  const [tenderReserve, setTenderReserve] = useState([]);
  const [tenderPlans, setTenderPlans] = useState([]);
  const [tenderBoxLots, setTenderBoxLots] = useState([]);
  const [tenderPartialBoxes, setTenderPartialBoxes] = useState([]);
  const [tenderGluckSheet, setTenderGluckSheet] = useState(null);
  const [whGluckSheet, setWhGluckSheet] = useState(null);
  const [tenderLabelsLoading, setTenderLabelsLoading] = useState(false);
  const [labelsMsg, setLabelsMsg] = useState(null); // { type: 'ok'|'err', text }
  const [labelSourcesLoading, setLabelSourcesLoading] = useState(false);
  const [labelSourcesLoaded, setLabelSourcesLoaded] = useState(false);
  const [labelPrints, setLabelPrints] = useState([]);
  const [labelsOpenSections, setLabelsOpenSections] = useState(() => new Set());
  const [tenderShipments, setTenderShipments] = useState([]);
  const [tenderShip, setTenderShip] = useState({
    sku: 'tp50',
    sn_from: '',
    sn_to: '',
    client_name: DEFAULT_TENDER_CLIENT,
    comment: '',
    boxes_per_pallet: String(DEFAULT_BOXES_PER_PALLET),
  });
  const [tenderShipPicks, setTenderShipPicks] = useState({});
  const [tenderShipMsg, setTenderShipMsg] = useState(null); // { type: 'ok'|'err', text }
  const [tenderShipBusy, setTenderShipBusy] = useState(false);
  const [tenderEditShip, setTenderEditShip] = useState(null); // { id, client_name, comment }
  const [tenderOpenLots, setTenderOpenLots] = useState(() => new Set());
  const [monthlyBalances, setMonthlyBalances] = useState([]);
  const [shipMonthStats, setShipMonthStats] = useState(null);
  const [shipMonthKey, setShipMonthKey] = useState(() => localMonthKey(0));
  const [shipDailySku, setShipDailySku] = useState('');
  const [shipCustomFrom, setShipCustomFrom] = useState(() => `${localMonthKey(0)}-01`);
  const [shipCustomTo, setShipCustomTo] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [shipCustomDraft, setShipCustomDraft] = useState(() => {
    const d = new Date();
    const to = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { from: `${localMonthKey(0)}-01`, to };
  });
  const [transfers, setTransfers] = useState([]);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [msg, setMsg] = useState('');
  const [receiptRows, setReceiptRows] = useState(makeReceiptRows());
  const [tenderReceipt, setTenderReceipt] = useState(makeTenderReceipt());
  const [receiptMeta, setReceiptMeta] = useState({ warehouse_code: 'F', comment: '' });
  const [receiptSubmitting, setReceiptSubmitting] = useState(false);
  const [receiptSuccess, setReceiptSuccess] = useState(null);
  const [receiptMsg, setReceiptMsg] = useState(null); // { type: 'ok'|'err', text }
  const [movements, setMovements] = useState([]);
  const [movFilter, setMovFilter] = useState({
    warehouse: savedUi.movWarehouse || 'F',
    sku: '',
    type: '',
    no_lot: false,
    q: '',
    limit: '100',
  });
  const [movSearch, setMovSearch] = useState('');
  const [editMovement, setEditMovement] = useState(null);
  const [editForm, setEditForm] = useState({
    qty: '',
    lot_number: '',
    expiration_date: '',
    production_date: '',
    comment: '',
    sn_from: '',
    sn_to: '',
  });
  const [editError, setEditError] = useState('');
  const [lotFilter, setLotFilter] = useState(savedUi.lotFilter || 'F');
  const [invLoading, setInvLoading] = useState(false);
  const [reserveTransfer, setReserveTransfer] = useState({
    sku: 'tp50',
    lot_number: '',
    qty: '',
    comment: '',
  });
  const [newTransfer, setNewTransfer] = useState({
    qty_i3: '',
    qty_i6: '',
    qty_gluck: '',
    qty_tp50: '',
    lot_number_gluck: '',
    lot_number_tp50: '',
    comment: '',
  });
  const [assignLotForm, setAssignLotForm] = useState(null);
  // { warehouse, sku, qty, lot_number, expiration, busy }
  const [transferScan, setTransferScan] = useState('');
  const transferScanRef = useRef(null);
  const [marketingSummary, setMarketingSummary] = useState({ i3: 0, i6: 0 });
  const [marketingMonthKey, setMarketingMonthKey] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const marketingMonthKeyRef = useRef(marketingMonthKey);
  marketingMonthKeyRef.current = marketingMonthKey;
  const [marketingMonthSummary, setMarketingMonthSummary] = useState({ month_key: '', items: [] });
  const [marketingBatches, setMarketingBatches] = useState([]);
  const [marketingItems, setMarketingItems] = useState([]);
  const [marketingScan, setMarketingScan] = useState('');
  const [marketingComment, setMarketingComment] = useState('');
  const [marketingBatchId, setMarketingBatchId] = useState('');
  const [marketingMsg, setMarketingMsg] = useState(null); // { type, text }
  const [marketingBusy, setMarketingBusy] = useState(false);
  const marketingScanRef = useRef(null);

  const canReceipt = role === 'admin' || role === 'warehouse';
  const canTransfer = role === 'warehouse' || role === 'admin';
  const canViewJournal = role === 'warehouse' || role === 'admin' || role === 'viewer';
  const canRepairStock = role === 'admin';
  const canMarketingScan = role === 'warehouse' || role === 'admin';
  const canMarketingCancel = role === 'admin';

  useEffect(() => {
    const prev = loadUiState();
    localStorage.setItem(UI_STATE_KEY, JSON.stringify({
      ...prev,
      stockView,
      movWarehouse: movFilter.warehouse,
      lotFilter,
      tab: 'stock',
    }));
  }, [stockView, movFilter.warehouse, lotFilter]);

  const loadMovements = useCallback(async (filterOverride = null) => {
    if (!token || !canViewJournal) return;
    const f = filterOverride ? { ...movFilter, ...filterOverride } : movFilter;
    const params = new URLSearchParams({ limit: f.limit || '100' });
    if (f.warehouse) params.set('warehouse', f.warehouse);
    if (f.sku) params.set('sku', f.sku);
    if (f.type) params.set('type', f.type);
    if (f.no_lot) params.set('no_lot', '1');
    if (f.q?.trim()) params.set('q', f.q.trim());
    const res = await apiFetch(token, `/stock/movements?${params}`);
    if (res.ok) {
      const rows = await res.json();
      setMovements(Array.isArray(rows) ? rows : []);
    }
  }, [token, canViewJournal, movFilter]);

  const loadMarketing = useCallback(async () => {
    if (!token) return;
    const params = new URLSearchParams({ limit: '100' });
    const month = marketingMonthKeyRef.current;
    if (month) params.set('month', month);
    const mktRes = await apiFetch(token, `/stock/marketing?${params}`);
    if (mktRes.status === 401) { logout(); return; }
    if (mktRes.ok) {
      const mkt = await mktRes.json();
      setMarketingSummary(mkt.summary || { i3: 0, i6: 0 });
      setMarketingMonthSummary(mkt.month_summary || { month_key: '', items: [] });
      setMarketingBatches(Array.isArray(mkt.batches) ? mkt.batches : []);
      setMarketingItems(Array.isArray(mkt.items) ? mkt.items : []);
    }
  }, [token, logout]);

  const loadShipMonthStats = useCallback(async () => {
    if (!token) return;
    if (lotFilter !== 'F' && lotFilter !== 'T') {
      setShipMonthStats(null);
      return;
    }
    const params = new URLSearchParams({ warehouse: lotFilter });
    if (shipMonthKey === 'custom') {
      params.set('from', shipCustomFrom);
      params.set('to', shipCustomTo);
    } else {
      params.set('month', shipMonthKey);
    }
    if (shipDailySku) params.set('sku', shipDailySku);
    const res = await apiFetch(token, `/stock/ship-month-stats?${params}`);
    if (res.status === 401) { logout(); return; }
    if (res.ok) setShipMonthStats(await res.json());
  }, [token, lotFilter, shipMonthKey, shipCustomFrom, shipCustomTo, shipDailySku, logout]);

  const load = useCallback(async () => {
    if (!token) return;
    const balRes = await apiFetch(token, '/stock/balances');
    if (balRes.status === 401) { logout(); return; }
    const bal = await balRes.json();
    if (balRes.ok) setBalances(Array.isArray(bal) ? bal : []);

    const lotRes = await apiFetch(token, '/stock/lot-summary');
    if (lotRes.ok) {
      setLotSummary(await lotRes.json());
    }

    const lotsRes = await apiFetch(token, '/stock/lots');
    if (lotsRes.status === 401) { logout(); return; }
    if (lotsRes.ok) {
      const lots = await lotsRes.json();
      setStockLots(Array.isArray(lots) ? lots : []);
    }

    const tenderRes = await apiFetch(token, '/stock/tender');
    if (tenderRes.ok) {
      const tender = await tenderRes.json();
      setTenderReserve(Array.isArray(tender) ? tender : []);
    }

    const planRes = await apiFetch(token, '/stock/tender/plan');
    if (planRes.ok) {
      setTenderPlans(parseTenderPlans(await planRes.json()));
    }

    const shipRes = await apiFetch(token, '/stock/tender/shipments?limit=80');
    if (shipRes.ok) {
      const ships = await shipRes.json();
      setTenderShipments(Array.isArray(ships) ? ships : []);
    }

    // Листы упаковки (Google Sheets) грузятся только по кнопке в сабтабе «Бирки»:
    // печать одноразовая, а три запроса к Sheets на каждый рефреш складов не нужны.

    if (canTransfer) {
      const trRes = await apiFetch(token, '/stock/transfers');
      if (trRes.ok) {
        const tr = await trRes.json();
        setTransfers(Array.isArray(tr) ? tr : []);
      }
    }

    const mbRes = await apiFetch(token, '/stock/monthly-balances');
    if (mbRes.ok) {
      const mb = await mbRes.json();
      setMonthlyBalances(Array.isArray(mb) ? mb : []);
    }

    loadMarketing();
    loadMovements();
  }, [token, logout, canTransfer, loadMovements, loadMarketing]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => { loadMarketing(); }, [loadMarketing, marketingMonthKey]);

  useEffect(() => { loadShipMonthStats(); }, [loadShipMonthStats, balances]);

  useEffect(() => {
    if (stockView === 'tashkent' && canMarketingScan && marketingScanRef.current) {
      focusScannerInput(marketingScanRef);
    }
  }, [stockView, canMarketingScan]);

  useEffect(() => {
    if (stockView === 'journal' && canViewJournal) loadMovements();
  }, [stockView, canViewJournal, loadMovements]);

  const loadLabelPrints = useCallback(async () => {
    if (!token) return;
    const res = await apiFetch(token, '/stock/box-label-prints?limit=50');
    if (res.ok) {
      const rows = await res.json();
      setLabelPrints(Array.isArray(rows) ? rows : []);
    }
  }, [token]);

  // История печати — из PG, дёшево; листы Sheets остаются за кнопкой.
  useEffect(() => {
    if (stockView === 'labels') loadLabelPrints();
  }, [stockView, loadLabelPrints]);

  const balanceQty = (wh, sku) => balances
    .find((b) => b.warehouse_code === wh && b.sku === sku)?.qty ?? 0;

  const setReceiptRow = (sku, patch) => {
    setReceiptRows((rows) => ({ ...rows, [sku]: { ...rows[sku], ...patch } }));
  };

  const setTenderReceiptRow = (sku, patch) => {
    setTenderReceipt((rows) => ({ ...rows, [sku]: { ...rows[sku], ...patch } }));
  };

  const submitReceipt = async () => {
    const lines = [];
    for (const sku of STOCK_SKUS) {
      const row = receiptRows[sku];
      const qty = parseInt(row.qty, 10) || 0;
      if (qty <= 0) continue;
      if (sku === 'i3' || sku === 'i6') {
        lines.push({ sku, qty });
        continue;
      }
      const line = {
        sku,
        qty,
        lot_number: row.lot?.trim() || undefined,
        expiration_date: row.exp?.trim() || undefined,
        production_date: row.prod?.trim() || undefined,
      };
      if (!line.lot_number) {
        setReceiptMsg({ type: 'err', text: `Укажите лот для ${SKU_LABEL[sku]} (Фергана/Ташкент)` });
        return;
      }
      if (sku === 'gluck') {
        const snFrom = row.sn_from?.trim() || '';
        const snTo = row.sn_to?.trim() || '';
        if ((snFrom || snTo) && (!snFrom || !snTo)) {
          setReceiptMsg({ type: 'err', text: 'Глюкометр: укажите SN с и SN по, либо оставьте оба пустыми' });
          return;
        }
        if (snFrom && snTo) {
          line.sn_from = snFrom;
          line.sn_to = snTo;
        }
      }
      lines.push(line);
    }

    const tGluck = tenderReceipt.gluck;
    const tGluckQty = parseInt(tGluck.qty, 10) || 0;
    if (tGluckQty > 0) {
      if (!tGluck.sn_from?.trim() || !tGluck.sn_to?.trim()) {
        setReceiptMsg({ type: 'err', text: 'Тендер / глюкометр: укажите SN с и SN по' });
        return;
      }
      lines.push({
        sku: 'gluck',
        qty: tGluckQty,
        sn_from: tGluck.sn_from.trim(),
        sn_to: tGluck.sn_to.trim(),
        production_date: tGluck.prod?.trim() || undefined,
        tender_reserve: true,
      });
    }
    const tTp = tenderReceipt.tp50;
    const tTpQty = parseInt(tTp.qty, 10) || 0;
    if (tTpQty > 0) {
      if (!tTp.lot?.trim()) {
        setReceiptMsg({ type: 'err', text: 'Тендер / ТП50: укажите лот' });
        return;
      }
      lines.push({
        sku: 'tp50',
        qty: tTpQty,
        lot_number: tTp.lot.trim(),
        expiration_date: tTp.exp?.trim() || undefined,
        production_date: tTp.prod?.trim() || undefined,
        tender_reserve: true,
      });
    }

    if (!lines.length) {
      setReceiptMsg({ type: 'err', text: 'Укажите количество хотя бы по одному продукту' });
      return;
    }
    setReceiptSubmitting(true);
    setReceiptMsg(null);
    try {
      const res = await apiFetch(token, '/stock/receipt', {
        method: 'POST',
        body: JSON.stringify({
          warehouse_code: receiptMeta.warehouse_code,
          lines,
          comment: receiptMeta.comment || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setReceiptMsg({ type: 'err', text: data.error || 'Ошибка прихода' });
        return;
      }
      setReceiptSuccess({
        warehouse: receiptMeta.warehouse_code,
        lines,
      });
      setReceiptRows(makeReceiptRows());
      setTenderReceipt(makeTenderReceipt());
      setReceiptMeta({ warehouse_code: receiptMeta.warehouse_code, comment: '' });
      await load();
    } finally {
      setReceiptSubmitting(false);
    }
  };

  const repairMissingExpenses = async () => {
    if (!canRepairStock) return;
    if (!window.confirm('Досписать со склада все отгруженные заявки без расхода в журнале?')) return;
    const res = await apiFetch(token, '/stock/repair-expenses', { method: 'POST', body: '{}' });
    const data = await res.json();
    if (!res.ok) {
      setMsg(data.error || 'Ошибка досписания');
      return;
    }
    const fixed = data.fixed?.length ? data.fixed.map((id) => `#${id}`).join(', ') : 'нет';
    const errs = data.errors?.length
      ? `; ошибки: ${data.errors.map((e) => `#${e.id}: ${e.error}`).join('; ')}`
      : '';
    setMsg(`Досписано: ${fixed}${errs}`);
    load();
  };

  const openEditMovement = (m) => {
    if (m.type !== 'receipt') return;
    setEditError('');
    setEditMovement(m);
    setEditForm({
      qty: String(m.qty || ''),
      lot_number: m.lot_number || '',
      expiration_date: isoToDmy(m.expiration_date),
      production_date: isoToDmy(m.production_date),
      comment: m.comment || '',
      sn_from: m.sn_from || '',
      sn_to: m.sn_to || '',
    });
  };

  const closeEditMovement = () => {
    setEditMovement(null);
    setEditError('');
    setEditForm({
      qty: '',
      lot_number: '',
      expiration_date: '',
      production_date: '',
      comment: '',
      sn_from: '',
      sn_to: '',
    });
  };

  const saveEditMovement = async () => {
    if (!editMovement) return;
    const unlabeledGluck = isUnlabeledTenderGluck(editMovement);
    const needsLot = BOX_SKUS.includes(editMovement.sku) && !unlabeledGluck;
    if (needsLot && !editForm.lot_number.trim()) {
      setEditError(`Укажите лот для ${SKU_LABEL[editMovement.sku]}`);
      return;
    }
    if (unlabeledGluck && (!editForm.sn_from.trim() || !editForm.sn_to.trim())) {
      setEditError('Тендер / глюкометр: укажите SN с и SN по');
      return;
    }
    const body = {
      qty: parseInt(editForm.qty, 10),
      lot_number: unlabeledGluck ? null : (editForm.lot_number.trim() || null),
      comment: editForm.comment.trim() || null,
      production_date: null,
      expiration_date: null,
    };
    if (unlabeledGluck || editMovement.sku === 'gluck') {
      body.sn_from = editForm.sn_from.trim();
      body.sn_to = editForm.sn_to.trim();
    }
    if (!Number.isFinite(body.qty) || body.qty <= 0) {
      setEditError('Количество прихода должно быть больше нуля');
      return;
    }
    if (editForm.expiration_date.trim()) {
      const iso = dmyToIso(editForm.expiration_date);
      if (!iso) {
        setEditError('Срок годности: ДД.ММ.ГГГГ');
        return;
      }
      body.expiration_date = iso;
    }
    if (editForm.production_date.trim()) {
      const iso = dmyToIso(editForm.production_date);
      if (!iso) {
        setEditError('Дата производства: ДД.ММ.ГГГГ');
        return;
      }
      body.production_date = iso;
    }
    const res = await apiFetch(token, `/stock/movements/${editMovement.id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) {
      setEditError(data.error || 'Ошибка сохранения прихода');
      return;
    }
    if (data.balances) setBalances(data.balances);
    if (data.lots) setStockLots(Array.isArray(data.lots) ? data.lots : []);
    setMsg(`Приход #${editMovement.id} обновлён`);
    closeEditMovement();
    loadMovements();
    load();
  };

  const deleteEditMovement = async () => {
    if (!editMovement || role !== 'admin') return;
    if (!window.confirm(
      `Удалить ошибочный приход #${editMovement.id} на ${editMovement.qty} шт? Остаток будет уменьшен.`,
    )) return;
    const res = await apiFetch(token, `/stock/movements/${editMovement.id}`, {
      method: 'DELETE',
    });
    const data = await res.json();
    if (!res.ok) {
      setEditError(data.error || 'Не удалось удалить приход');
      return;
    }
    if (data.balances) setBalances(data.balances);
    if (data.lots) setStockLots(Array.isArray(data.lots) ? data.lots : []);
    setMsg(`Ошибочный приход #${editMovement.id} удалён`);
    closeEditMovement();
    loadMovements();
    load();
  };

  const submitReserveTransfer = async () => {
    const qty = parseInt(reserveTransfer.qty, 10) || 0;
    if (!reserveTransfer.lot_number?.trim() || qty <= 0) {
      setMsg('Укажите лот и количество');
      return;
    }
    const res = await apiFetch(token, '/stock/reserve-transfer', {
      method: 'POST',
      body: JSON.stringify({
        lines: [{
          sku: reserveTransfer.sku,
          lot_number: reserveTransfer.lot_number.trim(),
          qty,
        }],
        comment: reserveTransfer.comment || undefined,
      }),
    });
    const data = await res.json();
    if (!res.ok) {
      setMsg(data.error || 'Ошибка перемещения');
      return;
    }
    setMsg(`Перемещено ${qty} шт на основной склад Фергана`);
    setReserveTransfer({ sku: 'tp50', lot_number: '', qty: '', comment: '' });
    load();
  };

  const submitTenderShip = async () => {
    const sku = tenderShip.sku;
    const boxSize = BOX_QTY[sku] || BOX_SIZE;
    const avail = (tenderReserve.find((g) => g.sku === sku)?.lots || [])
      .filter((l) => (l.qty || 0) > 0);
    const lines = [];
    const labels = [];
    for (const l of avail) {
      const key = tenderPickKey(l);
      if (!(key in tenderShipPicks)) continue;
      const qty = parseInt(tenderShipPicks[key], 10) || 0;
      if (qty <= 0) continue;
      if (qty > (l.qty || 0)) {
        setTenderShipMsg({
          type: 'err',
          text: `В тендере только ${l.qty} шт${l.unlabeled ? ' без лота' : `, лот ${l.lot_number}`}`,
        });
        return;
      }
      const line = { sku, qty };
      if (l.unlabeled) {
        if (tenderShip.sn_from?.trim()) line.sn_from = tenderShip.sn_from.trim();
        if (tenderShip.sn_to?.trim()) line.sn_to = tenderShip.sn_to.trim();
      } else {
        line.lot_number = l.lot_number;
      }
      lines.push(line);
      const boxes = Math.floor(qty / boxSize);
      const rem = qty % boxSize;
      labels.push(
        `${l.unlabeled ? 'без лота' : l.lot_number} ${qty.toLocaleString('ru-RU')} шт`
        + (boxes ? ` (${boxes} кор.${rem ? ` + ${rem}` : ''})` : ''),
      );
    }
    if (!lines.length) {
      setTenderShipMsg({ type: 'err', text: 'Отметьте лоты и укажите количество' });
      return;
    }
    setTenderShipBusy(true);
    setTenderShipMsg(null);
    try {
      const res = await apiFetch(token, '/stock/tender/ship', {
        method: 'POST',
        body: JSON.stringify({
          lines,
          client_name: tenderShip.client_name?.trim() || DEFAULT_TENDER_CLIENT,
          comment: tenderShip.comment?.trim() || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setTenderShipMsg({ type: 'err', text: data.error || 'Ошибка отгрузки' });
        return;
      }
      const qtyTotal = lines.reduce((s, x) => s + x.qty, 0);
      const boxesTotal = Math.floor(qtyTotal / boxSize);
      const remTotal = qtyTotal % boxSize;
      const perPallet = parseInt(tenderShip.boxes_per_pallet, 10) || DEFAULT_BOXES_PER_PALLET;
      const client = tenderShip.client_name?.trim() || DEFAULT_TENDER_CLIENT;
      setTenderShipMsg({
        type: 'ok',
        text: `✓ Отгружено ${qtyTotal.toLocaleString('ru-RU')} шт`
          + `${boxesTotal ? ` (${boxesTotal} кор.${remTotal ? ` + ${remTotal}` : ''})` : ''}`
          + ` · ${fmtPallets(boxesTotal, perPallet)}`
          + ` · ${lines.length} ${lines.length === 1 ? 'лот' : lines.length < 5 ? 'лота' : 'лотов'}`
          + ` · ${client}\n${labels.join('\n')}`,
      });
      setMsg('');
      setTenderShip({
        sku: 'tp50',
        sn_from: '',
        sn_to: '',
        client_name: DEFAULT_TENDER_CLIENT,
        comment: '',
        boxes_per_pallet: tenderShip.boxes_per_pallet || String(DEFAULT_BOXES_PER_PALLET),
      });
      setTenderShipPicks({});
      if (Array.isArray(data.tender)) setTenderReserve(data.tender);
      await load();
    } finally {
      setTenderShipBusy(false);
    }
  };

  const fillTenderShipTruck = () => {
    const avail = (tenderReserve.find((g) => g.sku === tenderShip.sku)?.lots || [])
      .filter((l) => (l.qty || 0) > 0);
    const { picks, filledBoxes, shortBoxes, targetBoxes } = fillTenderTruck(
      avail,
      tenderShip.sku,
      tenderShip.boxes_per_pallet,
      TENDER_TRUCK_PALLETS,
    );
    setTenderShipPicks(picks);
    if (!tenderShip.comment?.trim()) {
      setTenderShip((s) => ({ ...s, comment: `фура ${TENDER_TRUCK_PALLETS} палет` }));
    }
    if (shortBoxes > 0) {
      setTenderShipMsg({
        type: 'err',
        text: `Не хватает ${shortBoxes} кор. до ${TENDER_TRUCK_PALLETS} палет`
          + ` (цель ${targetBoxes} кор., набрано ${filledBoxes}). `
          + `Цельные коробки только; хвосты — «Весь остаток» или вручную.`,
      });
    } else {
      setTenderShipMsg(null);
    }
  };

  const fillTenderShipAll = () => {
    const avail = (tenderReserve.find((g) => g.sku === tenderShip.sku)?.lots || [])
      .filter((l) => (l.qty || 0) > 0);
    const { picks, qty, lots } = fillTenderAllRemaining(avail);
    setTenderShipPicks(picks);
    if (!lots) {
      setTenderShipMsg({ type: 'err', text: 'Нет остатка для списания' });
      return;
    }
    if (!tenderShip.comment?.trim()) {
      setTenderShip((s) => ({ ...s, comment: 'весь остаток R' }));
    }
    setTenderShipMsg({
      type: 'ok',
      text: `Набран весь остаток: ${qty.toLocaleString('ru-RU')} шт · ${lots} `
        + `${lots === 1 ? 'лот' : lots < 5 ? 'лота' : 'лотов'} (включая неполные коробки).`,
    });
  };

  const cancelTenderShipment = async (id) => {
    if (role !== 'admin') return;
    if (!window.confirm('Отменить эту отгрузку? Остаток вернётся на склад R.')) return;
    const res = await apiFetch(token, `/stock/tender/shipments/${id}`, { method: 'DELETE' });
    const data = await res.json();
    if (!res.ok) {
      setTenderShipMsg({ type: 'err', text: data.error || 'Не удалось отменить' });
      return;
    }
    setTenderShipMsg({ type: 'ok', text: `Отгрузка #${id} отменена, остаток возвращён` });
    if (Array.isArray(data.tender)) setTenderReserve(data.tender);
    await load();
  };

  const saveTenderShipmentEdit = async () => {
    if (!tenderEditShip || role !== 'admin') return;
    const res = await apiFetch(token, `/stock/tender/shipments/${tenderEditShip.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        client_name: tenderEditShip.client_name,
        comment: tenderEditShip.comment,
      }),
    });
    const data = await res.json();
    if (!res.ok) {
      setTenderShipMsg({ type: 'err', text: data.error || 'Ошибка сохранения' });
      return;
    }
    setTenderEditShip(null);
    setTenderShipMsg({ type: 'ok', text: `Отгрузка #${tenderEditShip.id} обновлена` });
    await load();
  };

  const toggleTenderLot = (key) => {
    setTenderOpenLots((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleLabelsSection = (key) => {
    setLabelsOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleTenderSkuLots = (sku, lots) => {
    const keys = lots.map((lot) => tenderLotKey(sku, lot));
    setTenderOpenLots((prev) => {
      const allOpen = keys.length > 0 && keys.every((k) => prev.has(k));
      const next = new Set(prev);
      if (allOpen) keys.forEach((k) => next.delete(k));
      else keys.forEach((k) => next.add(k));
      return next;
    });
  };

  const rebuildTenderStock = async () => {
    if (role !== 'admin') return;
    if (!window.confirm(
      'Пересчитать остатки склада R из журнала движений?\n'
      + 'Это исправит рассинхрон «лот показывает больше, чем склад».',
    )) return;
    setTenderShipBusy(true);
    try {
      const res = await apiFetch(token, '/stock/rebuild', {
        method: 'POST',
        body: JSON.stringify({ warehouse: 'R' }),
      });
      const data = await res.json();
      if (!res.ok) {
        setTenderShipMsg({ type: 'err', text: data.error || 'Ошибка пересчёта' });
        return;
      }
      const bal = (data.report?.balances || [])
        .filter((b) => b.warehouse_code === 'R' && b.sku === 'tp50')
        .map((b) => `tp50=${b.qty}`)
        .join(', ') || 'ok';
      setTenderShipMsg({ type: 'ok', text: `Остатки R пересчитаны (${bal})` });
      if (Array.isArray(data.tender)) setTenderReserve(data.tender);
      await load();
    } finally {
      setTenderShipBusy(false);
    }
  };

  const createTransferDoc = async () => {
    const qtyGluck = parseInt(newTransfer.qty_gluck, 10) || 0;
    const qtyTp50 = parseInt(newTransfer.qty_tp50, 10) || 0;
    if (qtyGluck > 0 && !newTransfer.lot_number_gluck?.trim()) {
      setMsg('Укажите лот глюкометра');
      return;
    }
    if (qtyTp50 > 0 && !newTransfer.lot_number_tp50?.trim()) {
      setMsg('Укажите лот ТП50');
      return;
    }
    const res = await apiFetch(token, '/stock/transfers', {
      method: 'POST',
      body: JSON.stringify({
        qty_i3: parseInt(newTransfer.qty_i3, 10) || 0,
        qty_i6: parseInt(newTransfer.qty_i6, 10) || 0,
        qty_gluck: qtyGluck,
        qty_tp50: qtyTp50,
        lot_number_gluck: newTransfer.lot_number_gluck || undefined,
        lot_number_tp50: newTransfer.lot_number_tp50 || undefined,
        comment: newTransfer.comment || undefined,
      }),
    });
    const data = await res.json();
    if (!res.ok) {
      setMsg(data.error || 'Ошибка создания');
      return;
    }
    setMsg(`Перемещение #${data.id} создано`);
    setNewTransfer({
      qty_i3: '',
      qty_i6: '',
      qty_gluck: '',
      qty_tp50: '',
      lot_number_gluck: '',
      lot_number_tp50: '',
      comment: '',
    });
    setSelectedTransfer(data);
    load();
  };

  const submitAssignLot = async () => {
    if (!assignLotForm) return;
    const qty = parseInt(assignLotForm.qty, 10) || 0;
    if (!assignLotForm.lot_number?.trim() || qty <= 0) {
      setMsg('Укажите лот и количество');
      return;
    }
    setAssignLotForm({ ...assignLotForm, busy: true });
    const res = await apiFetch(token, '/stock/assign-lot', {
      method: 'POST',
      body: JSON.stringify({
        warehouse: assignLotForm.warehouse,
        sku: assignLotForm.sku,
        lot_number: assignLotForm.lot_number.trim(),
        qty,
        expiration_date: assignLotForm.expiration || undefined,
        comment: assignLotForm.comment || undefined,
      }),
    });
    const data = await res.json();
    if (!res.ok) {
      setMsg(data.error || 'Ошибка присвоения лота');
      setAssignLotForm({ ...assignLotForm, busy: false });
      return;
    }
    if (data.balances) setBalances(Array.isArray(data.balances) ? data.balances : []);
    if (data.lots) setStockLots(Array.isArray(data.lots) ? data.lots : []);
    setMsg(
      `Лот ${data.lot_number}: +${data.qty} шт `
      + `(${WH_SHORT[data.warehouse_code] || data.warehouse_code}, ${SKU_LABEL[data.sku] || data.sku})`,
    );
    setAssignLotForm(null);
    load();
  };

  const openTransfer = async (id) => {
    const res = await apiFetch(token, `/stock/transfers/${id}`);
    const data = await res.json();
    if (!res.ok) {
      setMsg(data.error);
      return;
    }
    setSelectedTransfer(data);
    setTransferScan('');
  };

  const patchTransferStatus = async (status) => {
    if (!selectedTransfer) return;
    const res = await apiFetch(token, `/stock/transfers/${selectedTransfer.id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
    const data = await res.json();
    if (!res.ok) {
      setMsg(data.error);
      return;
    }
    setSelectedTransfer(data);
    setMsg(`Статус: ${TRANSFER_STATUS_LABEL[status] || status}`);
    load();
  };

  const doTransferScan = async () => {
    if (!selectedTransfer || !transferScan.trim()) return;
    const res = await apiFetch(token, `/stock/transfers/${selectedTransfer.id}/scan`, {
      method: 'POST',
      body: JSON.stringify({ barcode: transferScan.trim() }),
    });
    const data = await res.json();
    setTransferScan('');
    if (!res.ok) {
      setMsg(data.error);
      return;
    }
    setSelectedTransfer(data);
    setMsg('SN добавлен');
    focusScannerInput(transferScanRef);
  };

  const applyMarketingPayload = (data) => {
    if (data.summary) setMarketingSummary(data.summary);
    if (Array.isArray(data.batches)) setMarketingBatches(data.batches);
    if (Array.isArray(data.items)) setMarketingItems(data.items);
    if (Array.isArray(data.balances)) setBalances(data.balances);
  };

  const focusMarketingScanInput = useCallback(() => {
    focusScannerInput(marketingScanRef);
  }, []);

  const doMarketingScan = async () => {
    if (!canMarketingScan || !marketingScan.trim() || marketingBusy) return;
    const effectiveBatchId = marketingBatchId
      || `mkt-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    setMarketingBusy(true);
    setMarketingMsg(null);
    try {
      const res = await apiFetch(token, '/stock/marketing/scan', {
        method: 'POST',
        body: JSON.stringify({
          barcode: marketingScan.trim(),
          comment: marketingComment.trim() || undefined,
          batch_id: effectiveBatchId,
        }),
      });
      const data = await res.json();
      setMarketingScan('');
      if (!res.ok) {
        setMarketingMsg({ type: 'err', text: data.error || 'Ошибка скана' });
        return;
      }
      setMarketingBatchId(effectiveBatchId);
      setMarketingComment('');
      applyMarketingPayload(data);
      await loadMarketing();
      setMarketingMsg({
        type: 'ok',
        text: `Списано ${data.item?.model}: ${data.item?.sn}`,
      });
    } catch {
      setMarketingMsg({ type: 'err', text: 'Сервер недоступен' });
    } finally {
      setMarketingBusy(false);
      focusMarketingScanInput();
    }
  };

  const closeMarketingBatch = () => {
    setMarketingBatchId('');
    setMarketingComment('');
    setMarketingMsg(null);
    focusMarketingScanInput();
  };

  const cancelMarketingItem = async (sn) => {
    if (!canMarketingCancel || !sn) return;
    if (!window.confirm(`Вернуть на склад T и удалить маркетинг SN ${sn}?`)) return;
    setMarketingBusy(true);
    setMarketingMsg(null);
    try {
      const res = await apiFetch(token, `/stock/marketing/${encodeURIComponent(sn)}`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (!res.ok) {
        setMarketingMsg({ type: 'err', text: data.error || 'Не удалось отменить' });
        return;
      }
      applyMarketingPayload(data);
      await loadMarketing();
      setMarketingMsg({ type: 'ok', text: `Отменено: ${sn}` });
    } catch {
      setMarketingMsg({ type: 'err', text: 'Сервер недоступен' });
    } finally {
      setMarketingBusy(false);
    }
  };

  const printTenderPackingList = async (movementId) => {
    if (!movementId) {
      setTenderShipMsg({ type: 'err', text: 'Нет прихода для упаковочного листа' });
      return;
    }
    setTenderLabelsLoading(true);
    try {
      const params = new URLSearchParams({
        movement_id: String(movementId),
        _t: String(Date.now()),
      });
      const res = await apiFetch(token, `/stock/tender/packing-list.pdf?${params}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setTenderShipMsg({ type: 'err', text: err.error || 'Ошибка упаковочного листа' });
        return;
      }
      const blob = await res.blob();
      openPdfBlob(blob, `tender-gluck-packing-${movementId}.pdf`);
    } catch {
      setTenderShipMsg({ type: 'err', text: 'Сервер недоступен' });
    } finally {
      setTenderLabelsLoading(false);
    }
  };

  // Бирки печатаются из сабтаба «Бирки» и из журнала приходов на вкладке «Тендер».
  const setPrintMsg = (m) => {
    setLabelsMsg(m);
    setTenderShipMsg(m);
  };

  // Три листа Google Sheets — только по кнопке.
  const loadLabelSources = async () => {
    setLabelSourcesLoading(true);
    setLabelsMsg(null);
    try {
      const [tp50Res, gluckRes, whRes] = await Promise.all([
        apiFetch(token, '/stock/tender/boxes'),
        apiFetch(token, '/stock/tender/gluck-boxes'),
        apiFetch(token, '/stock/gluck-boxes'),
      ]);

      if (tp50Res.ok) {
        const boxData = await tp50Res.json();
        const lots = Array.isArray(boxData.lots) ? boxData.lots : [];
        setTenderBoxLots(lots);
        setTenderPartialBoxes(Array.isArray(boxData.partials) ? boxData.partials : []);
      }
      if (gluckRes.ok) {
        setTenderGluckSheet(await gluckRes.json());
      } else {
        setTenderGluckSheet(null);
      }
      if (whRes.ok) {
        setWhGluckSheet(await whRes.json());
      } else {
        setWhGluckSheet(null);
      }

      const failed = [
        !tp50Res.ok && 'ТП50',
        !gluckRes.ok && 'Gluck_tender',
        !whRes.ok && 'Глюк Фер Склад',
      ].filter(Boolean);
      setLabelSourcesLoaded(true);
      if (failed.length) {
        setLabelsMsg({ type: 'err', text: `Не прочитаны листы: ${failed.join(', ')}` });
      }
    } catch {
      setLabelsMsg({ type: 'err', text: 'Сервер недоступен' });
    } finally {
      setLabelSourcesLoading(false);
    }
  };

  const reprintLabels = async (row) => {
    setTenderLabelsLoading(true);
    try {
      const res = await apiFetch(token, `/stock/box-label-prints/${row.id}/pdf?_t=${Date.now()}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setLabelsMsg({ type: 'err', text: err.error || 'Ошибка перепечатки' });
        return;
      }
      const blob = await res.blob();
      openPdfBlob(blob, `box-labels-${row.id}.pdf`);
      setLabelsMsg({
        type: 'ok',
        text: `Перепечатка #${row.id}: ${row.kind_label}${row.lot_number ? ` · ${row.lot_number}` : ''}`,
      });
    } catch {
      setLabelsMsg({ type: 'err', text: 'Сервер недоступен' });
    } finally {
      setTenderLabelsLoading(false);
    }
  };

  const deleteLabelPrint = async (row) => {
    if (role !== 'admin') return;
    if (!window.confirm(`Удалить запись #${row.id} из журнала бирок?`)) return;
    const res = await apiFetch(token, `/stock/box-label-prints/${row.id}`, { method: 'DELETE' });
    if (res.ok) {
      loadLabelPrints();
    } else {
      const err = await res.json().catch(() => ({}));
      setLabelsMsg({ type: 'err', text: err.error || 'Не удалось удалить запись' });
    }
  };

  const printTenderGluckBoxLabels = async (movementId) => {
    setTenderLabelsLoading(true);
    try {
      const params = new URLSearchParams({ _t: String(Date.now()) });
      if (movementId) params.set('movement_id', String(movementId));
      const res = await apiFetch(token, `/stock/tender/gluck-box-labels.pdf?${params}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setPrintMsg({ type: 'err', text: err.error || 'Ошибка бирок глюкометров' });
        return;
      }
      const blob = await res.blob();
      const filename = movementId
        ? `tender-gluck-boxes-${movementId}.pdf`
        : 'tender-gluck-boxes.pdf';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 120000);
      setPrintMsg({
        type: 'ok',
        text: `Скачан ${filename}: вес с листа Gluck_tender (GROSS/NET). Печать → Landscape`,
      });
      loadLabelPrints();
    } catch {
      setPrintMsg({ type: 'err', text: 'Сервер недоступен' });
    } finally {
      setTenderLabelsLoading(false);
    }
  };

  const printTenderBoxLabels = async ({ box, lot } = {}) => {
    const boxNum = box != null ? parseInt(box, 10) : null;
    const lotNum = lot || null;
    if (!boxNum && !lotNum) {
      setLabelsMsg({ type: 'err', text: 'Выберите лот' });
      return;
    }
    setTenderLabelsLoading(true);
    try {
      const params = new URLSearchParams({ _t: String(Date.now()) });
      if (boxNum) {
        params.set('box', String(boxNum));
        if (lotNum) params.set('lot', lotNum);
      } else {
        params.set('lot', lotNum);
        params.set('full_only', '1');
      }
      const res = await apiFetch(token, `/stock/tender/box-labels.pdf?${params}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setLabelsMsg({ type: 'err', text: err.error || 'Ошибка генерации PDF' });
        return;
      }
      const blob = await res.blob();
      const filename = boxNum
        ? `tender-box-${boxNum}.pdf`
        : `tender-boxes-${lotNum}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 120000);
      setLabelsMsg({
        type: 'ok',
        text: boxNum
          ? `Скачан ${filename}: хвост №${boxNum} (исключение, неполная коробка). Печать → Landscape`
          : `Скачан ${filename}: landscape A4, 2 вертикальные A5 рядом. Печать → Orientation: Landscape`,
      });
      loadLabelPrints();
    } finally {
      setTenderLabelsLoading(false);
    }
  };

  // Бирки коробок глюкометров склада F (комплект с ТП10, лист «Глюк Фер Склад»).
  const printWarehouseGluckBoxLabels = async ({ lot, box } = {}) => {
    const lotNum = lot || null;
    const boxNum = box != null ? parseInt(box, 10) : null;
    if (!lotNum && !boxNum) {
      setLabelsMsg({ type: 'err', text: 'Выберите лот' });
      return;
    }
    setTenderLabelsLoading(true);
    try {
      const params = new URLSearchParams({ _t: String(Date.now()) });
      if (lotNum) params.set('lot', lotNum);
      if (boxNum) params.set('box', String(boxNum));
      const res = await apiFetch(token, `/stock/gluck-box-labels.pdf?${params}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setLabelsMsg({ type: 'err', text: err.error || 'Ошибка бирок глюкометров F' });
        return;
      }
      const blob = await res.blob();
      const filename = boxNum
        ? `gluck-box-${boxNum}.pdf`
        : `gluck-boxes-${lotNum || 'all'}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 120000);
      setLabelsMsg({
        type: 'ok',
        text: `Скачан ${filename}: вес и даты с листа «Глюк Фер Склад». Печать → Landscape`,
      });
      loadLabelPrints();
    } catch {
      setLabelsMsg({ type: 'err', text: 'Сервер недоступен' });
    } finally {
      setTenderLabelsLoading(false);
    }
  };

  const printInventory = async (sku) => {
    const wh = lotFilter === 'T' ? 'T' : 'F';
    const key = sku || 'all';
    setInvLoading(key);
    setMsg('');
    try {
      const qs = new URLSearchParams({ warehouse: wh, _t: String(Date.now()) });
      if (sku) qs.set('sku', sku);
      const res = await apiFetch(token, `/stock/inventory.pdf?${qs}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setMsg(err.error || 'Не удалось сформировать чеклист');
        return;
      }
      const blob = await res.blob();
      const tag = sku === 'tp50' ? 'TP50' : (sku === 'gluck' ? 'ARIA' : 'GP');
      const filename = `inventarizaciya-${tag}-${wh}.pdf`;
      openPdfBlob(blob, filename, { autoPrint: false });
      const label = sku === 'tp50' ? 'ТП50' : (sku === 'gluck' ? 'глюкометров' : 'ГП');
      setMsg(`Чеклист инвентаризации ${label} — ${WH_SHORT[wh]}. Печать → ориентация Landscape.`);
    } catch {
      setMsg('Сервер недоступен');
    } finally {
      setInvLoading(false);
    }
  };

  const loadInventorySnap = useCallback(async ({ focus = false } = {}) => {
    if (!token) return;
    if (focus && lotFilter !== 'F' && lotFilter !== 'T') {
      setMsg('Сверка экрана — выберите склад Фергана или Ташкент (не «Все»).');
      return;
    }
    const wh = lotFilter === 'T' ? 'T' : 'F';
    setInventoryLoading(true);
    if (focus) setMsg('');
    try {
      const qs = new URLSearchParams({ warehouse: wh });
      const res = await apiFetch(token, `/stock/inventory?${qs}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setInventorySnap(null);
        if (focus) setMsg(err.error || 'Не удалось загрузить сверку');
        return;
      }
      const data = await res.json();
      setInventorySnap(data);
      if (focus) {
        const unlabeled = (data.items || []).filter((it) => it.unlabeled_qty);
        const n = (data.items || []).length;
        setMsg(
          unlabeled.length
            ? `Сверка ${WH_SHORT[wh]}: ${n} SKU, расхождения «без лота» у ${unlabeled.map((it) => SKU_LABEL[it.sku] || it.sku).join(', ')} — блок ниже.`
            : `Сверка ${WH_SHORT[wh]}: ${n} SKU, лоты сходятся с остатком — блок «Сверка инвентаризации» ниже.`,
        );
        setInventoryFlash(true);
        requestAnimationFrame(() => {
          inventorySnapRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        setTimeout(() => setInventoryFlash(false), 1800);
      }
    } catch {
      setInventorySnap(null);
      if (focus) setMsg('Сервер недоступен');
    } finally {
      setInventoryLoading(false);
    }
  }, [token, lotFilter]);

  useEffect(() => {
    if (stockView === 'balances') loadInventorySnap();
  }, [stockView, loadInventorySnap, balances]);

  const rebuildShipStock = async () => {
    if (role !== 'admin') return;
    const wh = lotFilter === 'T' ? 'T' : 'F';
    if (!window.confirm(
      `Пересчитать остатки склада ${WH_SHORT[wh]} (${wh}) из журнала движений?\n`
      + 'Это синхронизирует лоты с жирным итогом SKU.',
    )) return;
    setInvLoading('rebuild');
    setMsg('');
    try {
      const res = await apiFetch(token, '/stock/rebuild', {
        method: 'POST',
        body: JSON.stringify({ warehouse: wh }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMsg(data.error || 'Ошибка пересчёта');
        return;
      }
      setMsg(`Склад ${wh} пересчитан из журнала.`);
      await load();
    } catch {
      setMsg('Сервер недоступен');
    } finally {
      setInvLoading(false);
    }
  };

  const reserveLots = stockLots.filter((l) => l.warehouse_code === 'R' && l.qty > 0);
  const shipLotFilter = lotFilter === 'R' ? '' : lotFilter;
  const shipMonthMode = shipLotFilter === 'F' || shipLotFilter === 'T';
  const shipMonthOptions = (() => {
    const opts = new Set();
    if (shipMonthKey !== 'all' && shipMonthKey !== 'custom') opts.add(shipMonthKey);
    (monthlyBalances || []).forEach((r) => { if (r.month_key) opts.add(r.month_key); });
    const d = new Date();
    for (let i = 0; i < 6; i += 1) {
      const x = new Date(d.getFullYear(), d.getMonth() - i, 1);
      opts.add(`${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}`);
    }
    return [...opts].sort().reverse();
  })();
  const shipCurrentMonthKey = localMonthKey(0);
  const shipPrevMonthKey = localMonthKey(-1);
  const shipPeriodLabel = (() => {
    if (shipMonthKey === 'all' || shipMonthStats?.is_all_period) return 'весь период';
    if (shipMonthKey === 'custom' || shipMonthStats?.is_custom_period) {
      const a = shipMonthStats?.period_from || shipCustomFrom;
      const b = shipMonthStats?.period_to || shipCustomTo;
      return `${isoToDmy(a)}–${isoToDmy(b)}`;
    }
    return monthLabel(shipMonthStats?.month_key || shipMonthKey);
  })();
  const shipDailyRows = shipMonthStats?.daily || [];
  const shipDailyShippedSum = shipDailyRows.reduce((s, r) => s + (r.shipped || 0), 0);
  const filteredLots = shipLotFilter
    ? stockLots.filter((l) => l.warehouse_code === shipLotFilter)
    : stockLots.filter((l) => l.warehouse_code === 'F' || l.warehouse_code === 'T');

  const tenderBySku = (tenderReserve.length
    ? tenderReserve
    : STOCK_SKUS.map((sku) => {
      const lots = reserveLots
        .filter((l) => l.sku === sku)
        .map((l) => ({ ...l, batches: [] }));
      const lotsSum = lots.reduce((s, l) => s + (l.qty || 0), 0);
      const bal = balanceQty('R', sku);
      return { sku, total: lots.length ? lotsSum : bal, lots };
    }).filter((g) => g.total > 0 || g.lots.length > 0)
  );
  const tp50LabelLots = tenderBoxLots || [];
  const tp50LabelPartials = tenderPartialBoxes || [];
  const aria10LabelBoxes = tenderGluckSheet?.boxes || [];
  const aria11LabelLots = whGluckSheet?.lots || [];
  const hasTp50Labels = tp50LabelLots.length > 0 || tp50LabelPartials.length > 0;
  const hasAria10Sheet = labelSourcesLoaded && tenderGluckSheet != null;
  const hasAria10Labels = aria10LabelBoxes.length > 0;
  const hasAria11Labels = aria11LabelLots.length > 0;
  const tenderShipLotsAvail = (tenderReserve.find((g) => g.sku === tenderShip.sku)?.lots || [])
    .filter((l) => (l.qty || 0) > 0)
    .slice()
    .sort(sortLotsNewestFirst);
  const tenderShipBoxSize = BOX_QTY[tenderShip.sku] || BOX_SIZE;
  const tenderShipPerPallet = parseInt(tenderShip.boxes_per_pallet, 10) || DEFAULT_BOXES_PER_PALLET;
  const tenderShipPicked = tenderShipLotsAvail
    .map((l) => {
      const key = tenderPickKey(l);
      if (!(key in tenderShipPicks)) return null;
      const qty = parseInt(tenderShipPicks[key], 10) || 0;
      if (qty <= 0) return null;
      return { lot: l, key, qty };
    })
    .filter(Boolean);
  const tenderShipQtyTotal = tenderShipPicked.reduce((s, x) => s + x.qty, 0);
  const tenderShipBoxesTotal = Math.floor(tenderShipQtyTotal / tenderShipBoxSize);
  const tenderShipRemTotal = tenderShipQtyTotal % tenderShipBoxSize;
  const tenderShipTruckExact = tenderShipPerPallet > 0
    && tenderShipRemTotal === 0
    && tenderShipBoxesTotal === TENDER_TRUCK_PALLETS * tenderShipPerPallet;
  const unlabeledPicked = tenderShip.sku === 'gluck' && (TENDER_NO_LOT in tenderShipPicks);

  const STOCK_VIEWS = [
    { id: 'balances', label: 'Остатки' },
    { id: 'receipt', label: 'Приход' },
    { id: 'transfers', label: 'Перемещения' },
    { id: 'tender', label: 'Тендер' },
    { id: 'labels', label: 'Бирки' },
    { id: 'tashkent', label: 'Маркетинг T' },
    { id: 'journal', label: 'Журнал' },
    ...(canRepairStock ? [{ id: 'service', label: 'Сервис' }] : []),
  ];

  const mbMonths = [...new Set(monthlyBalances.map((r) => r.month_key))].slice(0, 6);
  const mbGet = (month, wh, sku) =>
    monthlyBalances.find((r) => r.month_key === month && r.warehouse_code === wh && r.sku === sku)?.closing_qty ?? null;
  const tashkentMonthRow = (sku) =>
    marketingMonthSummary.items?.find((row) => row.sku === sku) || null;
  const marketingCurrentMonthKey = localMonthKey(0);
  const marketingPrevMonthKey = localMonthKey(-1);
  const marketingAllPeriod = marketingMonthKey === 'all' || marketingMonthSummary.is_all_period;
  const currentMarketingBatch = marketingBatchId
    ? marketingBatches.find((b) => b.batch_id === marketingBatchId)
    : null;

  return (
    <div className="stock-page">
      <div className="stock-view-tabs">
        {STOCK_VIEWS.map((v) => (
          <button
            key={v.id}
            type="button"
            className={`stock-view-tab${stockView === v.id ? ' active' : ''}`}
            onClick={() => setStockView(v.id)}
          >
            {v.label}
          </button>
        ))}
      </div>

      {msg && <p className="scan-msg">{msg}</p>}

      {stockView === 'balances' && (<>
      <section className="stock-card">
        <div className="stock-card-head">
          <h3>Остатки — отгрузка</h3>
          <div className="stock-card-head-right">
            <div className="chip-filter">
              {['', 'F', 'T'].map((code) => (
                <button
                  type="button"
                  key={code || 'all'}
                  className={shipLotFilter === code || (code === '' && !shipLotFilter) ? 'active' : ''}
                  onClick={() => setLotFilter(code)}
                >
                  {code ? WH_SHORT[code] : 'Все (F+T)'}
                </button>
              ))}
            </div>
            {shipMonthMode && (
              <div className="warehouse-filter stock-period-filter" role="group" aria-label="Период остатков">
                <button
                  type="button"
                  className={shipMonthKey === shipCurrentMonthKey ? 'active' : ''}
                  onClick={() => setShipMonthKey(shipCurrentMonthKey)}
                >
                  {monthNameOnly(shipCurrentMonthKey)}
                </button>
                <button
                  type="button"
                  className={shipMonthKey === shipPrevMonthKey ? 'active' : ''}
                  onClick={() => setShipMonthKey(shipPrevMonthKey)}
                >
                  {monthNameOnly(shipPrevMonthKey)}
                </button>
                <button
                  type="button"
                  className={shipMonthKey === 'all' ? 'active' : ''}
                  onClick={() => setShipMonthKey('all')}
                >
                  За весь период
                </button>
                <label className="stock-inline-label">
                  Ещё
                  <select
                    value={shipMonthOptions.includes(shipMonthKey) ? shipMonthKey : ''}
                    onChange={(e) => {
                      if (e.target.value) setShipMonthKey(e.target.value);
                    }}
                  >
                    <option value="">…</option>
                    {shipMonthOptions.map((mk) => (
                      <option key={mk} value={mk}>{monthLabel(mk)}</option>
                    ))}
                  </select>
                </label>
              </div>
            )}
            {lotSummary && (
              <span className="hint-small">
                {BRAND_SINOCARE} отгружено: i3 <b>{lotSummary.shipped_total?.i3 ?? 0}</b>
                {' · '}i6 <b>{lotSummary.shipped_total?.i6 ?? 0}</b> шт
              </span>
            )}
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={!!invLoading}
              onClick={() => printInventory('gluck')}
            >
              {invLoading === 'gluck' ? 'PDF…' : 'Лист глюк'}
            </button>
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={!!invLoading}
              onClick={() => printInventory('tp50')}
            >
              {invLoading === 'tp50' ? 'PDF…' : 'Лист ТП50'}
            </button>
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={!!invLoading}
              onClick={() => printInventory()}
            >
              {invLoading === 'all' ? 'PDF…' : 'ГП все'}
            </button>
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={inventoryLoading}
              title="Сравнить остаток SKU с суммой лотов; скролл к блоку ниже"
              onClick={() => loadInventorySnap({ focus: true })}
            >
              {inventoryLoading ? 'Сверка…' : 'Сверка экрана'}
            </button>
          </div>
        </div>
        <p className="hint-small stock-expense-hint">
          {shipMonthMode ? (
            <>
              Склад {WH_SHORT[shipLotFilter]}: колонки «на начало» / «отгружено за {shipPeriodLabel}» / «сейчас».
              Лоты — из журнала; «без лота» — движения без номера партии.
              Входящие = приход + F→T.
            </>
          ) : (
            <>
              Только склады отгрузки: F — Фергана, T — Ташкент. Тендерный резерв — на вкладке «Тендер».
              {' '}
              Выберите Фергану или Ташкент, чтобы видеть начало месяца и отгрузки.
            </>
          )}
          {' '}
          Фасовка: i3×24 · i6×36 · глюкометр×75 · ТП50×416.
          Листы инвентаризации: глюк / ТП50 / ГП все — PDF Landscape.
          {' '}
          «Сверка экрана» — таблица остаток ↔ лоты в блоке ниже (нужен фильтр F или T).
        </p>
        <table className="stats-table stock-matrix">
          <thead>
            <tr>
              <th>Продукт / партия</th>
              {shipMonthMode ? (
                <>
                  <th>На {shipMonthStats?.is_all_period
                    ? 'начало'
                    : (shipMonthStats?.month_start
                      ? isoToDmy(shipMonthStats.month_start)
                      : (shipMonthKey === 'custom'
                        ? isoToDmy(shipCustomFrom)
                        : `01.${String(shipMonthKey).slice(5)}.${String(shipMonthKey).slice(0, 4)}`))}</th>
                  <th>Отгружено</th>
                  <th>{!shipMonthStats?.is_all_period && !shipMonthStats?.is_custom_period && shipMonthStats?.is_current_month === false ? 'На конец мес.' : 'Сейчас'}</th>
                </>
              ) : (
                <>
                  {(!shipLotFilter || shipLotFilter === 'F') && <th>Фергана</th>}
                  {(!shipLotFilter || shipLotFilter === 'T') && <th>Ташкент</th>}
                  {!shipLotFilter && <th className="col-total">Итого</th>}
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {STOCK_SKUS.map((sku) => {
              const f = balanceQty('F', sku);
              const t = balanceQty('T', sku);
              const isLotSku = sku === 'gluck' || sku === 'tp50';
              const showF = !shipLotFilter || shipLotFilter === 'F';
              const showT = !shipLotFilter || shipLotFilter === 'T';
              const skuStat = shipMonthMode
                ? (shipMonthStats?.by_sku || []).find((r) => r.sku === sku)
                : null;
              const opening = skuStat?.opening ?? 0;
              const shipped = skuStat?.shipped ?? 0;
              const current = skuStat?.current ?? (shipLotFilter === 'T' ? t : f);

              const monthLots = shipMonthMode
                ? (shipMonthStats?.by_lot || []).filter((l) => l.sku === sku)
                : [];
              const shipLots = filteredLots.filter((l) => l.sku === sku);
              const groupedLots = (!shipMonthMode && isLotSku) ? groupLotsByNumber(shipLots) : [];
              const lotsSumF = groupedLots.reduce((s, l) => s + (l.F || 0), 0);
              const lotsSumT = groupedLots.reduce((s, l) => s + (l.T || 0), 0);
              const gapF = f - lotsSumF;
              const gapT = t - lotsSumT;
              const showGap = !shipMonthMode && isLotSku && (
                (showF && gapF !== 0)
                || (showT && gapT !== 0)
                || (!shipLotFilter && (gapF + gapT) !== 0)
              );

              return (
                <Fragment key={sku}>
                  <tr className="stock-matrix-summary">
                    <td><b>{SKU_LABEL[sku]}</b></td>
                    {shipMonthMode ? (
                      <>
                        <td><b>{opening || '—'}</b></td>
                        <td className={shipped ? 'qty-neg' : ''}>
                          <b>{shipped ? `−${shipped.toLocaleString('ru-RU')}` : '—'}</b>
                        </td>
                        <td><QtyWithBoxes qty={current} sku={sku} bold /></td>
                      </>
                    ) : (
                      <>
                        {showF && <td><QtyWithBoxes qty={f} sku={sku} bold /></td>}
                        {showT && <td><QtyWithBoxes qty={t} sku={sku} bold /></td>}
                        {!shipLotFilter && (
                          <td className="col-total"><QtyWithBoxes qty={f + t} sku={sku} bold /></td>
                        )}
                      </>
                    )}
                  </tr>
                  {shipMonthMode && isLotSku && monthLots.map((lot) => {
                    const isGap = !lot.lot_number;
                    if (!lot.opening && !lot.shipped && !lot.current && !lot.received) return null;
                    return (
                      <tr
                        key={`${sku}-${lot.lot_number || 'nolot'}`}
                        className={`stock-matrix-lot-row${isGap ? ' stock-matrix-gap-row' : ''}`}
                      >
                        <td className="stock-lot-indent">
                          <span className={`stock-lot-label${isGap ? ' cell-muted' : ''}`}>
                            {lot.lot_number || 'без лота'}
                          </span>
                          {!isGap && lot.expiration_date && (
                            <span className="stock-lot-exp">до {isoToDmy(lot.expiration_date)}</span>
                          )}
                          {isGap && (
                            <span className="stock-lot-exp">отгрузки/приходы без номера партии</span>
                          )}
                        </td>
                        <td>{lot.opening || '—'}</td>
                        <td className={lot.shipped ? 'qty-neg' : ''}>
                          {lot.shipped ? `−${lot.shipped.toLocaleString('ru-RU')}` : '—'}
                        </td>
                        <td><QtyWithBoxes qty={lot.current} sku={sku} /></td>
                      </tr>
                    );
                  })}
                  {!shipMonthMode && groupedLots.map((lot) => {
                    const lotF = lot.F || 0;
                    const lotT = lot.T || 0;
                    if (shipLotFilter === 'F' && !lotF) return null;
                    if (shipLotFilter === 'T' && !lotT) return null;
                    const lotTotal = lotF + lotT;
                    return (
                      <tr key={`${sku}-${lot.lot_number}`} className="stock-matrix-lot-row">
                        <td className="stock-lot-indent">
                          <span className="stock-lot-label">{lot.lot_number}</span>
                          {lot.expiration_date && (
                            <span className="stock-lot-exp">до {isoToDmy(lot.expiration_date)}</span>
                          )}
                        </td>
                        {showF && <td><QtyWithBoxes qty={lotF} sku={sku} /></td>}
                        {showT && <td><QtyWithBoxes qty={lotT} sku={sku} /></td>}
                        {!shipLotFilter && (
                          <td className="col-total"><QtyWithBoxes qty={lotTotal} sku={sku} /></td>
                        )}
                      </tr>
                    );
                  })}
                  {showGap && (
                    <tr className="stock-matrix-lot-row stock-matrix-gap-row">
                      <td className="stock-lot-indent">
                        <span className="stock-lot-label cell-muted">без лота</span>
                        <span className="stock-lot-exp">
                          расхождение баланс − сумма лотов (часто без строк в журнале)
                        </span>
                        {canTransfer && (
                          <div className="stock-gap-actions">
                            {showF && gapF > 0 && (
                              <button
                                type="button"
                                className="btn-link"
                                onClick={() => setAssignLotForm({
                                  warehouse: 'F',
                                  sku,
                                  qty: String(gapF),
                                  lot_number: '',
                                  expiration: '',
                                  comment: '',
                                  busy: false,
                                })}
                              >
                                Прописать лот F ({gapF})
                              </button>
                            )}
                            {showT && gapT > 0 && (
                              <button
                                type="button"
                                className="btn-link"
                                onClick={() => setAssignLotForm({
                                  warehouse: 'T',
                                  sku,
                                  qty: String(gapT),
                                  lot_number: '',
                                  expiration: '',
                                  comment: '',
                                  busy: false,
                                })}
                              >
                                Прописать лот T ({gapT})
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                      {showF && (
                        <td className={gapF < 0 ? 'qty-neg' : (gapF > 0 ? 'qty-pos' : '')}>
                          {gapF ? <QtyWithBoxes qty={gapF} sku={sku} /> : '—'}
                        </td>
                      )}
                      {showT && (
                        <td className={gapT < 0 ? 'qty-neg' : (gapT > 0 ? 'qty-pos' : '')}>
                          {gapT ? <QtyWithBoxes qty={gapT} sku={sku} /> : '—'}
                        </td>
                      )}
                      {!shipLotFilter && (
                        <td className={`col-total${(gapF + gapT) < 0 ? ' qty-neg' : ((gapF + gapT) > 0 ? ' qty-pos' : '')}`}>
                          {(gapF + gapT) ? <QtyWithBoxes qty={gapF + gapT} sku={sku} /> : '—'}
                        </td>
                      )}
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
        {shipMonthMode && shipMonthStats?.totals && (
          <p className="hint-small" style={{ marginTop: 8 }}>
            Итого за {shipPeriodLabel}: отгружено{' '}
            <b className="qty-neg">−{(shipMonthStats.totals.shipped || 0).toLocaleString('ru-RU')}</b>
            {' · '}вход <b className="qty-pos">+{(shipMonthStats.totals.received || 0).toLocaleString('ru-RU')}</b>
            {' · '}сейчас <b>{(shipMonthStats.totals.current || 0).toLocaleString('ru-RU')}</b>
          </p>
        )}
      </section>

      {shipMonthMode && (
        <section className="stock-card">
          <div className="stock-card-head">
            <h3>Отгрузки по дням — {WH_SHORT[shipLotFilter]} · {shipPeriodLabel}</h3>
          </div>
          <div className="ship-daily-filters">
            <div className="chip-filter" role="group" aria-label="Продукт">
              {[
                { id: '', label: 'Все' },
                { id: 'i3', label: 'i3' },
                { id: 'i6', label: 'i6' },
                { id: 'gluck', label: 'Глюкометр' },
                { id: 'tp50', label: 'ТП50' },
              ].map((opt) => (
                <button
                  type="button"
                  key={opt.id || 'all'}
                  className={shipDailySku === opt.id ? 'active' : ''}
                  onClick={() => setShipDailySku(opt.id)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="warehouse-filter stock-period-filter" role="group" aria-label="Период отгрузок">
              <button
                type="button"
                className={shipMonthKey === shipCurrentMonthKey ? 'active' : ''}
                onClick={() => setShipMonthKey(shipCurrentMonthKey)}
              >
                {monthNameOnly(shipCurrentMonthKey)}
              </button>
              <button
                type="button"
                className={shipMonthKey === shipPrevMonthKey ? 'active' : ''}
                onClick={() => setShipMonthKey(shipPrevMonthKey)}
              >
                {monthNameOnly(shipPrevMonthKey)}
              </button>
              <button
                type="button"
                className={shipMonthKey === 'all' ? 'active' : ''}
                onClick={() => setShipMonthKey('all')}
              >
                За весь период
              </button>
            </div>
            <div className="ship-daily-custom-dates">
              <label className="stock-inline-label">
                с
                <input
                  type="date"
                  value={shipCustomDraft.from}
                  onChange={(e) => setShipCustomDraft((p) => ({ ...p, from: e.target.value }))}
                />
              </label>
              <label className="stock-inline-label">
                по
                <input
                  type="date"
                  value={shipCustomDraft.to}
                  onChange={(e) => setShipCustomDraft((p) => ({ ...p, to: e.target.value }))}
                />
              </label>
              <button
                type="button"
                className={`btn-secondary btn-sm${shipMonthKey === 'custom' ? ' active-period' : ''}`}
                onClick={() => {
                  if (!shipCustomDraft.from || !shipCustomDraft.to) return;
                  if (shipCustomDraft.from > shipCustomDraft.to) return;
                  setShipCustomFrom(shipCustomDraft.from);
                  setShipCustomTo(shipCustomDraft.to);
                  setShipMonthKey('custom');
                }}
              >
                Даты
              </button>
            </div>
          </div>
          {shipDailyRows.length > 0 ? (
            <table className="stats-table stock-lots-table">
              <thead>
                <tr>
                  <th>Дата</th>
                  <th>Заявка</th>
                  <th>Продукт</th>
                  <th>Лот</th>
                  <th>Шт</th>
                </tr>
              </thead>
              <tbody>
                {shipDailyRows.map((row, idx) => (
                  <tr key={`${row.day}-${row.order_id || 'x'}-${row.sku}-${row.lot_number || 'x'}-${idx}`}>
                    <td className="cell-nowrap">{isoToDmy(row.day)}</td>
                    <td className="cell-nowrap">
                      {row.external_order_id
                        ? `#${row.external_order_id}${row.order_type === 'glucometer' ? ' ARIA' : ''}`
                        : (row.order_id ? `id ${row.order_id}` : <span className="cell-muted">—</span>)}
                    </td>
                    <td>{SKU_LABEL[row.sku] || row.sku}</td>
                    <td>{row.lot_number || <span className="cell-muted">без лота</span>}</td>
                    <td className="qty-neg">−{(row.shipped || 0).toLocaleString('ru-RU')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="hint">Нет отгрузок за {shipPeriodLabel}{shipDailySku ? ` · ${SKU_LABEL[shipDailySku]}` : ''}.</p>
          )}
          <p className="hint-small">
            Только списания (expense)
            {shipDailySku ? ` · фильтр ${SKU_LABEL[shipDailySku]}` : ''}
            {shipDailyRows.length ? ` · в таблице −${shipDailyShippedSum.toLocaleString('ru-RU')} шт` : ''}
            . Откат статуса в NEW пишет return — в этой таблице не видно, остаток уже учтён.
          </p>
        </section>
      )}

      {mbMonths.length > 0 && (
        <section className="stock-card">
          <div className="stock-card-head"><h3>Остатки на конец месяца</h3></div>
          <div className="stock-monthly-scroll">
            <table className="stats-table stock-monthly-table">
              <thead>
                <tr>
                  <th>Месяц</th>
                  {['i3', 'i6', 'gluck', 'tp50'].map((s) => (
                    <th key={`f-${s}`}>{SKU_LABEL[s]} Ф</th>
                  ))}
                  {['i3', 'i6', 'gluck', 'tp50'].map((s) => (
                    <th key={`t-${s}`}>{SKU_LABEL[s]} Т</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {mbMonths.map((mk) => (
                  <tr key={mk}>
                    <td className="cell-nowrap"><b>{monthLabel(mk)}</b></td>
                    {['i3', 'i6', 'gluck', 'tp50'].map((s) => {
                      const v = mbGet(mk, 'F', s);
                      return <td key={`f-${s}`}>{v !== null ? v : '—'}</td>;
                    })}
                    {['i3', 'i6', 'gluck', 'tp50'].map((s) => {
                      const v = mbGet(mk, 'T', s);
                      return <td key={`t-${s}`}>{v !== null ? v : '—'}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="hint-small">Накопительный остаток на конец каждого месяца (из журнала движений).</p>
        </section>
      )}

      <section
        ref={inventorySnapRef}
        className={`stock-card${inventoryFlash ? ' stock-card-flash' : ''}`}
        id="stock-inventory-snap"
      >
        <div className="stock-card-head">
          <h3>
            Сверка инвентаризации
            {inventorySnap?.warehouse_code
              ? ` — ${WH_SHORT[inventorySnap.warehouse_code] || inventorySnap.warehouse_code}`
              : ''}
          </h3>
          {inventorySnap?.generated_at && (
            <span className="hint-small">
              {new Date(inventorySnap.generated_at).toLocaleString('ru-RU')}
            </span>
          )}
        </div>
        {inventoryLoading && <p className="hint-small">Загрузка…</p>}
        {!inventoryLoading && !inventorySnap && (
          <p className="hint-small">Нет данных сверки. Выберите F или T и нажмите «Сверка экрана».</p>
        )}
        {!inventoryLoading && inventorySnap && !(inventorySnap.items?.length > 0) && (
          <p className="hint-small">Снимок пустой — нет SKU в ответе API.</p>
        )}
        {inventorySnap?.items?.length > 0 && (
          <div className="stock-journal-scroll">
            <table className="stats-table stock-journal-table">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Остаток</th>
                  <th>Лоты Σ</th>
                  <th>Без лота</th>
                  <th>Партий</th>
                </tr>
              </thead>
              <tbody>
                {inventorySnap.items.map((it) => {
                  const lotsSum = (it.lots || []).reduce((s, l) => s + (l.qty || 0), 0);
                  return (
                    <tr key={it.sku} className={it.unlabeled_qty ? 'stock-journal-row-warn' : undefined}>
                      <td><b>{SKU_LABEL[it.sku] || it.name || it.sku}</b></td>
                      <td>{(it.qty || 0).toLocaleString('ru-RU')}</td>
                      <td>{lotsSum.toLocaleString('ru-RU')}</td>
                      <td className={it.unlabeled_qty ? 'qty-neg' : ''}>
                        {it.unlabeled_qty
                          ? (it.unlabeled_qty > 0 ? `+${it.unlabeled_qty}` : it.unlabeled_qty)
                          : '—'}
                      </td>
                      <td>{(it.lots || []).length || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="hint-small">
          Сверка: остаток SKU vs сумма лотов. Колонка «Без лота» ≠ 0 — дыра учёта (только gluck/tp50).
          PDF-листы сверху — для обхода склада с пустыми графами «факт».
        </p>
      </section>
      </>)}

      {stockView === 'receipt' && (<>
        {canReceipt && (
          <section className="stock-card">
            <div className="stock-card-head">
              <h3>Приход — Фергана / Ташкент</h3>
              <label className="stock-inline-label">Склад
                <select
                  value={receiptMeta.warehouse_code}
                  onChange={(e) => setReceiptMeta({ ...receiptMeta, warehouse_code: e.target.value })}
                >
                  <option value="F">Фергана</option>
                  <option value="T">Ташкент</option>
                </select>
              </label>
            </div>
            <table className="stats-table stock-receipt-table">
              <thead>
                <tr>
                  <th>Продукт</th>
                  <th>Кол-во</th>
                  <th>Лот (партия)</th>
                  <th>Дата произ-ва</th>
                  <th>Срок годности</th>
                </tr>
              </thead>
              <tbody>
                {STOCK_SKUS.map((sku) => {
                  const row = receiptRows[sku];
                  const hasLot = BOX_SKUS.includes(sku);
                  const bc = hasLot ? boxCount(row.qty, sku) : null;
                  const snPreview = sku === 'gluck' ? previewGluckReceipt(row) : '';
                  return (
                    <Fragment key={sku}>
                    <tr>
                      <td>{SKU_LABEL[sku]}</td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          value={row.qty}
                          onChange={(e) => setReceiptRow(sku, { qty: e.target.value })}
                        />
                        {bc && <div className="box-count-hint">{bc}</div>}
                      </td>
                      {hasLot ? (
                        <>
                          <td>
                            <input
                              value={row.lot}
                              onChange={(e) => setReceiptRow(sku, { lot: e.target.value })}
                              placeholder="A25L165x4"
                            />
                          </td>
                          <td>
                            <input
                              value={row.prod}
                              onChange={(e) => setReceiptRow(sku, { prod: e.target.value })}
                              placeholder="ДД.ММ.ГГГГ"
                            />
                          </td>
                          <td>
                            <input
                              value={row.exp}
                              onChange={(e) => setReceiptRow(sku, { exp: e.target.value })}
                              placeholder="ДД.ММ.ГГГГ"
                            />
                          </td>
                        </>
                      ) : (
                        <td colSpan={3} className="cell-muted">лот — при сканировании SN</td>
                      )}
                    </tr>
                    {sku === 'gluck' && (
                      <tr>
                        <td colSpan={5}>
                          <div className="gluck-sn-receipt">
                            <label>SN с
                              <input
                                value={row.sn_from || ''}
                                onChange={(e) => setReceiptRow(sku, { sn_from: e.target.value })}
                                placeholder="ARIA261100001"
                              />
                            </label>
                            <label>SN по
                              <input
                                value={row.sn_to || ''}
                                onChange={(e) => setReceiptRow(sku, { sn_to: e.target.value })}
                                placeholder="ARIA261102720"
                              />
                            </label>
                            {snPreview && <span className="hint-small">{snPreview}</span>}
                          </div>
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
            <p className="hint-small">
              Коммерческий набор с ТП10 — лот обязателен. У глюкометра можно дописать SN:
              целые коробки по {BOX_QTY.gluck} нумеруем с 1 по лоту и фиксируем номер с бирки при отгрузке.
              Хвост (неполная коробка) — без SN. Тендер без ТП10 — блок ниже.
            </p>
          </section>
        )}

        {canReceipt && (
          <section className="stock-card tender-receipt-card">
            <div className="stock-card-head">
              <h3>Приход на тендер R</h3>
              <span className="tender-badge-head">склад R · не в заявки</span>
            </div>
            <div className="tender-receipt-grid">
              <div className="tender-receipt-block">
                <h4>Глюкометр — без лота</h4>
                <p className="hint-small">Без ПФ ТП10. Идентичность — диапазон SN, фасовка {BOX_QTY.gluck} шт/кор.</p>
                <div className="stock-inline-form">
                  <label>Кол-во, шт
                    <input
                      type="number"
                      min="0"
                      value={tenderReceipt.gluck.qty}
                      onChange={(e) => setTenderReceiptRow('gluck', { qty: e.target.value })}
                    />
                  </label>
                  <label>SN с
                    <input
                      value={tenderReceipt.gluck.sn_from}
                      onChange={(e) => setTenderReceiptRow('gluck', { sn_from: e.target.value })}
                      placeholder="ARIA260100001"
                    />
                  </label>
                  <label>SN по
                    <input
                      value={tenderReceipt.gluck.sn_to}
                      onChange={(e) => setTenderReceiptRow('gluck', { sn_to: e.target.value })}
                      placeholder="ARIA260103000"
                    />
                  </label>
                  <label>Дата пр-ва
                    <input
                      value={tenderReceipt.gluck.prod}
                      onChange={(e) => setTenderReceiptRow('gluck', { prod: e.target.value })}
                      placeholder="ДД.ММ.ГГГГ"
                    />
                  </label>
                </div>
                {tenderReceipt.gluck.qty > 0 && (
                  <p className="hint-small tender-next-box">
                    {boxCount(tenderReceipt.gluck.qty, 'gluck')}
                    {previewTenderBoxes('gluck', tenderReceipt.gluck.qty, tenderReserve)
                      ? ` · ${previewTenderBoxes('gluck', tenderReceipt.gluck.qty, tenderReserve)}`
                      : ''}
                  </p>
                )}
              </div>
              <div className="tender-receipt-block">
                <h4>ТП50 — лот</h4>
                <p className="hint-small">Лот обязателен, фасовка {BOX_QTY.tp50} шт/кор.</p>
                <div className="stock-inline-form">
                  <label>Кол-во, шт
                    <input
                      type="number"
                      min="0"
                      value={tenderReceipt.tp50.qty}
                      onChange={(e) => setTenderReceiptRow('tp50', { qty: e.target.value })}
                    />
                  </label>
                  <label>Лот
                    <input
                      value={tenderReceipt.tp50.lot}
                      onChange={(e) => setTenderReceiptRow('tp50', { lot: e.target.value })}
                      placeholder="A25L165x4"
                    />
                  </label>
                  <label>Дата пр-ва
                    <input
                      value={tenderReceipt.tp50.prod}
                      onChange={(e) => setTenderReceiptRow('tp50', { prod: e.target.value })}
                      placeholder="ДД.ММ.ГГГГ"
                    />
                  </label>
                  <label>Срок
                    <input
                      value={tenderReceipt.tp50.exp}
                      onChange={(e) => setTenderReceiptRow('tp50', { exp: e.target.value })}
                      placeholder="ДД.ММ.ГГГГ"
                    />
                  </label>
                </div>
                {tenderReceipt.tp50.qty > 0 && (
                  <p className="hint-small tender-next-box">
                    {boxCount(tenderReceipt.tp50.qty, 'tp50')}
                    {previewTenderBoxes('tp50', tenderReceipt.tp50.qty, tenderReserve)
                      ? ` · ${previewTenderBoxes('tp50', tenderReceipt.tp50.qty, tenderReserve)}`
                      : ''}
                  </p>
                )}
              </div>
            </div>
            <div className="stock-card-foot">
              <input
                className="stock-comment-input"
                value={receiptMeta.comment}
                onChange={(e) => setReceiptMeta({ ...receiptMeta, comment: e.target.value })}
                placeholder="Комментарий (накладная, поставщик...)"
              />
              <button
                type="button"
                className="btn-primary"
                onClick={submitReceipt}
                disabled={receiptSubmitting}
              >
                {receiptSubmitting ? 'Записываем…' : 'Записать приход'}
              </button>
            </div>
            {receiptMsg && (
              <p className={`tender-ship-result ${receiptMsg.type === 'ok' ? 'ok' : 'err'}`}>
                {receiptMsg.text}
              </p>
            )}
          </section>
        )}

        {stockLots.length === 0 && (balanceQty('F', 'tp50') > 0 || balanceQty('F', 'gluck') > 0
          || balanceQty('T', 'tp50') > 0 || balanceQty('T', 'gluck') > 0) && (
          <p className="hint-small stock-lots-missing">
            Есть остаток ТП/глюкометра без партий — укажите лот при приходе.
          </p>
        )}
      </>)}

      {stockView === 'transfers' && (<>
        {canTransfer && (
          <section className="stock-card">
            <div className="stock-card-head">
              <h3>Перемещения</h3>
            </div>

            <div className="stock-subblock">
              <h4>
                <span className="badge wh-badge wh-F">Фергана</span>
                {' → '}
                <span className="badge wh-badge wh-T">Ташкент</span>
              </h4>
              <div className="stock-inline-form">
                {['qty_i3', 'qty_i6'].map((field) => {
                  const sku = field.replace('qty_', '');
                  return (
                    <label key={field}>{SKU_LABEL[sku]}
                      <input
                        type="number"
                        min="0"
                        value={newTransfer[field]}
                        onChange={(e) => setNewTransfer({ ...newTransfer, [field]: e.target.value })}
                      />
                    </label>
                  );
                })}
                {['gluck', 'tp50'].map((sku) => {
                  const qtyField = `qty_${sku}`;
                  const lotField = `lot_number_${sku}`;
                  const lots = stockLots.filter(
                    (l) => l.warehouse_code === 'F' && l.sku === sku && l.qty > 0,
                  );
                  return (
                    <Fragment key={sku}>
                      <label>{SKU_LABEL[sku]}
                        <input
                          type="number"
                          min="0"
                          value={newTransfer[qtyField]}
                          onChange={(e) => setNewTransfer({
                            ...newTransfer,
                            [qtyField]: e.target.value,
                            ...(e.target.value === '' || e.target.value === '0'
                              ? { [lotField]: '' }
                              : null),
                          })}
                        />
                      </label>
                      <label>Лот {SKU_LABEL[sku]}
                        <select
                          value={newTransfer[lotField]}
                          onChange={(e) => setNewTransfer({ ...newTransfer, [lotField]: e.target.value })}
                          disabled={!(parseInt(newTransfer[qtyField], 10) > 0)}
                        >
                          <option value="">— выберите —</option>
                          {lots.map((l) => (
                            <option key={l.lot_number} value={l.lot_number}>
                              {l.lot_number} ({l.qty} шт)
                            </option>
                          ))}
                        </select>
                      </label>
                    </Fragment>
                  );
                })}
                <label className="grow">Комментарий
                  <input
                    value={newTransfer.comment}
                    onChange={(e) => setNewTransfer({ ...newTransfer, comment: e.target.value })}
                  />
                </label>
                <button type="button" className="btn-primary" onClick={createTransferDoc}>
                  Создать черновик
                </button>
              </div>
              <p className="hint-small">
                Для ТП50/глюкометра с лотом — лот обязателен. Тендерные глюки без лота живут только на R, на Фергану не тащим.
              </p>
            </div>

            <div className="stock-subblock stock-subblock-reserve">
              <h4>
                <span className="badge wh-badge wh-R">Тендер</span>
                {' → '}
                <span className="badge wh-badge wh-F">Фергана</span>
              </h4>
              {reserveLots.length === 0 ? (
                <p className="hint-small">Тендерный резерв пуст — приход на R во вкладке «Приход».</p>
              ) : (
                <div className="stock-inline-form">
                  <label>Продукт
                    <select
                      value={reserveTransfer.sku}
                      onChange={(e) => setReserveTransfer({ ...reserveTransfer, sku: e.target.value, lot_number: '' })}
                    >
                      <option value="tp50">ТП50</option>
                      <option value="gluck">Глюкометр</option>
                    </select>
                  </label>
                  <label>Лот
                    <select
                      value={reserveTransfer.lot_number}
                      onChange={(e) => setReserveTransfer({ ...reserveTransfer, lot_number: e.target.value })}
                    >
                      <option value="">— выберите —</option>
                      {reserveLots
                        .filter((l) => l.sku === reserveTransfer.sku)
                        .slice()
                        .sort(sortLotsNewestFirst)
                        .map((l) => (
                          <option key={l.lot_number} value={l.lot_number}>
                            {l.lot_number} ({l.qty} шт)
                          </option>
                        ))}
                    </select>
                  </label>
                  <label>Кол-во
                    <input
                      type="number"
                      min="1"
                      value={reserveTransfer.qty}
                      onChange={(e) => setReserveTransfer({ ...reserveTransfer, qty: e.target.value })}
                    />
                  </label>
                  <button type="button" className="btn-primary" onClick={submitReserveTransfer}>
                    Переместить
                  </button>
                </div>
              )}
            </div>
          </section>
        )}

      {canTransfer && transfers.length > 0 && (
        <section className="stock-card">
          <div className="stock-card-head">
            <h3>Документы перемещений Фергана → Ташкент</h3>
          </div>
          <div className="stock-transfers-layout">
            <table className="stats-table stock-transfer-table">
              <thead>
                <tr>
                  <th>#</th><th>Статус</th><th>i3</th><th>i6</th><th>Гл</th><th>ТП</th><th>SN</th>
                </tr>
              </thead>
              <tbody>
                {transfers.map((t) => (
                  <tr
                    key={t.id}
                    className={`stock-row-click ${selectedTransfer?.id === t.id ? 'selected' : ''}`}
                    onClick={() => openTransfer(t.id)}
                  >
                    <td><b>#{t.id}</b></td>
                    <td>
                      <span className={`badge transfer-badge transfer-${(t.status || '').toLowerCase()}`}>
                        {TRANSFER_STATUS_LABEL[t.status] || t.status}
                      </span>
                    </td>
                    <td>{t.qty_i3 || '—'}</td>
                    <td>{t.qty_i6 || '—'}</td>
                    <td>{t.qty_gluck || '—'}</td>
                    <td>{t.qty_tp50 || '—'}</td>
                    <td>{t.sn_count || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {selectedTransfer && (
              <div className="stock-transfer-detail">
                <h4>Перемещение #{selectedTransfer.id}</h4>
                <p>
                  <span className="badge wh-badge wh-F">Фергана</span>
                  {' → '}
                  <span className="badge wh-badge wh-T">Ташкент</span>
                  {' · '}
                  {TRANSFER_STATUS_LABEL[selectedTransfer.status]}
                </p>
                <p className="hint-small">
                  i3 {selectedTransfer.qty_i3} · i6 {selectedTransfer.qty_i6}
                  {' · '}Гл {selectedTransfer.qty_gluck}
                  {selectedTransfer.lot_number_gluck ? ` (${selectedTransfer.lot_number_gluck})` : ''}
                  {' · '}ТП {selectedTransfer.qty_tp50}
                  {selectedTransfer.lot_number_tp50 ? ` (${selectedTransfer.lot_number_tp50})` : ''}
                </p>

                {selectedTransfer.status === 'DRAFT' && (
                  <>
                    <div className="scan-block">
                      <label htmlFor="transfer-scan">Опциональный скан SN (i3/i6)</label>
                      <input
                        id="transfer-scan"
                        ref={transferScanRef}
                        value={transferScan}
                        onChange={(e) => setTransferScan(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && doTransferScan()}
                        placeholder="DataMatrix..."
                        autoComplete="off"
                      />
                      <button type="button" onClick={doTransferScan}>Добавить SN</button>
                    </div>
                    <div className="actions">
                      <button type="button" className="btn-primary" onClick={() => patchTransferStatus('SENT')}>
                        Отправить (списать с Ферганы)
                      </button>
                      <button type="button" className="btn-danger" onClick={() => patchTransferStatus('CANCELLED')}>
                        Отменить
                      </button>
                    </div>
                  </>
                )}

                {selectedTransfer.status === 'SENT' && (
                  <div className="actions">
                    <button type="button" className="btn-primary" onClick={() => patchTransferStatus('RECEIVED')}>
                      Принять в Ташкенте
                    </button>
                  </div>
                )}

                {selectedTransfer.serial_numbers?.length > 0 && (
                  <ul className="sn-list">
                    {selectedTransfer.serial_numbers.map((s) => (
                      <li key={s.sn}>
                        <span className="model-tag">{s.model}</span>
                        <span className="sn-value">{s.sn}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {!canTransfer && (
        <p className="hint-small">Нет прав на перемещения.</p>
      )}
      </>)}

      {stockView === 'journal' && canViewJournal && (<>
      <section className="stock-card stock-journal-card">
        <div className="stock-card-head">
          <h3>Журнал операций</h3>
        </div>
        <div className="stock-journal-filters">
          <div className="chip-filter stock-journal-wh-chips">
            {[
              { code: 'F', label: 'Фергана' },
              { code: 'R', label: 'Тендер' },
              { code: 'T', label: 'Ташкент' },
              { code: '', label: 'Все' },
            ].map((w) => (
              <button
                type="button"
                key={w.code || 'all'}
                className={movFilter.warehouse === w.code ? 'active' : ''}
                onClick={() => setMovFilter({ ...movFilter, warehouse: w.code })}
              >
                {w.label}
              </button>
            ))}
          </div>
          <select
            value={movFilter.sku}
            onChange={(e) => setMovFilter({ ...movFilter, sku: e.target.value })}
          >
            <option value="">Все продукты</option>
            {STOCK_SKUS.map((sku) => (
              <option key={sku} value={sku}>{SKU_LABEL[sku]}</option>
            ))}
          </select>
          <select
            value={movFilter.type}
            onChange={(e) => setMovFilter({ ...movFilter, type: e.target.value })}
          >
            <option value="">Все операции</option>
            <option value="receipt">Приход</option>
            <option value="expense">Расход</option>
            <option value="return">Возврат</option>
            <option value="transfer_out">Перемещение −</option>
            <option value="transfer_in">Перемещение +</option>
            <option value="adjustment">Корректировка</option>
          </select>
          <select
            value={movFilter.limit}
            onChange={(e) => setMovFilter({ ...movFilter, limit: e.target.value })}
          >
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="200">200</option>
            <option value="500">500</option>
          </select>
          <label className="stock-journal-check">
            <input
              type="checkbox"
              checked={movFilter.no_lot}
              onChange={(e) => setMovFilter({ ...movFilter, no_lot: e.target.checked })}
            />
            Без лота
          </label>
          <input
            className="stock-journal-search"
            type="search"
            placeholder="Поиск: лот, заявка, комментарий…"
            value={movSearch}
            onChange={(e) => setMovSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setMovFilter({ ...movFilter, q: movSearch.trim() });
                loadMovements();
              }
            }}
          />
          <button
            type="button"
            className="btn-secondary"
            onClick={async () => {
              const nextQ = movSearch.trim();
              setMovFilter((prev) => ({ ...prev, q: nextQ }));
              setMsg('Обновляю журнал…');
              await load();
              await loadMovements({ q: nextQ });
              setMsg('Журнал обновлён');
            }}
          >
            Обновить
          </button>
        </div>
        {movFilter.warehouse === 'R' && (
          <div className="stock-journal-totals">
            <span className="stock-journal-totals-title">Остаток тендера:</span>
            {STOCK_SKUS
              .filter((sku) => !movFilter.sku || movFilter.sku === sku)
              .map((sku) => (
                <span key={sku} className="stock-journal-total-chip">
                  {SKU_LABEL[sku]} <b>{balanceQty('R', sku).toLocaleString('ru-RU')} шт</b>
                </span>
              ))}
          </div>
        )}
        {movements.length === 0 ? (
          <p className="hint-small">Операций по фильтру нет.</p>
        ) : (
          <div className="stock-journal-scroll">
            <table className="stats-table stock-journal-table">
              <thead>
                <tr>
                  <th>Дата</th>
                  <th>Заявка</th>
                  <th>Операция</th>
                  <th>Склад</th>
                  <th>Продукт</th>
                  <th>Шт</th>
                  <th>Лот</th>
                  <th>Дата производства</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {movements.map((m) => {
                  const lotMissing = BOX_SKUS.includes(m.sku) && !m.lot_number
                    && !(m.sku === 'gluck' && m.sn_from);
                  const canEdit = m.type === 'receipt' && role === 'admin';
                  return (
                    <tr
                      key={m.id}
                      title={m.comment || ''}
                      className={lotMissing ? 'stock-journal-row-warn' : undefined}
                    >
                      <td className="cell-nowrap">{fmtDateTime(m.created_at)}</td>
                      <td className="cell-nowrap">
                        {m.external_order_id
                          ? <b>#{m.external_order_id}</b>
                          : '—'}
                      </td>
                      <td>{MOVEMENT_TYPE_LABEL[m.type] || m.type}</td>
                      <td>{WH_SHORT[m.warehouse_code] || m.warehouse_code}</td>
                      <td>{SKU_LABEL[m.sku] || m.sku}</td>
                      <td className={m.qty > 0 ? 'qty-pos' : 'qty-neg'}>
                        {m.qty > 0 ? `+${m.qty}` : m.qty}
                      </td>
                      <td>
                        {m.lot_number || (
                          m.sn_from && m.sn_to
                            ? `${m.sn_from}–${m.sn_to}`
                            : (lotMissing
                              ? <span className="lot-missing">нет лота</span>
                              : '—')
                        )}
                        {m.lot_number && m.sn_from && m.sn_to && (
                          <div className="hint-small">{m.sn_from}–{m.sn_to}</div>
                        )}
                        {m.boxes && <div className="hint-small">кор. {m.boxes}</div>}
                      </td>
                      <td className="cell-nowrap">
                        {isoToDmy(m.production_date) || '—'}
                      </td>
                      <td className="cell-nowrap">
                        {canEdit && (
                          <button
                            type="button"
                            className="btn-link"
                            onClick={() => openEditMovement(m)}
                          >
                            Изменить
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
      </>)}

      {stockView === 'service' && canRepairStock && (
        <section className="stock-card">
          <div className="stock-card-head">
            <h3>Сервис склада</h3>
          </div>
          <p className="hint-small">
            Админ-инструменты: пересчёт остатков из журнала и досписание отгрузок без expense.
            Остаток «без лота» без строк в журнале — кнопка на Остатках или форма ниже.
          </p>
          <div className="actions">
            <button
              type="button"
              className="btn-secondary"
              disabled={!!invLoading}
              onClick={rebuildShipStock}
            >
              {invLoading === 'rebuild'
                ? 'Пересчёт…'
                : `Пересчитать ${lotFilter === 'T' ? 'T' : 'F'}`}
            </button>
            <button type="button" className="btn-secondary" onClick={repairMissingExpenses}>
              Досписать отгрузки
            </button>
            <button type="button" className="btn-link" onClick={rebuildTenderStock} disabled={tenderShipBusy}>
              Пересчитать тендер R
            </button>
          </div>
          <div className="chip-filter" style={{ marginTop: 12 }}>
            {['F', 'T'].map((code) => (
              <button
                type="button"
                key={code}
                className={lotFilter === code ? 'active' : ''}
                onClick={() => setLotFilter(code)}
              >
                {WH_SHORT[code]}
              </button>
            ))}
          </div>
          <div className="stock-subblock" style={{ marginTop: 16 }}>
            <h4>Прописать лот остатку без партии</h4>
            <div className="stock-inline-form">
              <label>Склад
                <select
                  value={lotFilter === 'T' ? 'T' : 'F'}
                  onChange={(e) => setLotFilter(e.target.value)}
                >
                  <option value="F">Фергана</option>
                  <option value="T">Ташкент</option>
                </select>
              </label>
              <label>Продукт
                <select id="service-assign-sku" defaultValue="tp50">
                  <option value="tp50">ТП50</option>
                  <option value="gluck">Глюкометр</option>
                </select>
              </label>
              <button
                type="button"
                className="btn-primary"
                onClick={() => {
                  const skuEl = document.getElementById('service-assign-sku');
                  const sku = skuEl?.value || 'tp50';
                  const wh = lotFilter === 'T' ? 'T' : 'F';
                  const f = balanceQty(wh, sku);
                  const lotsSum = stockLots
                    .filter((l) => l.warehouse_code === wh && l.sku === sku)
                    .reduce((s, l) => s + (l.qty || 0), 0);
                  const gap = f - lotsSum;
                  if (gap <= 0) {
                    setMsg(`Нет остатка без лота: ${SKU_LABEL[sku]} на ${WH_SHORT[wh]} (gap ${gap})`);
                    return;
                  }
                  setAssignLotForm({
                    warehouse: wh,
                    sku,
                    qty: String(gap),
                    lot_number: '',
                    expiration: '',
                    comment: '',
                    busy: false,
                  });
                }}
              >
                Открыть форму
              </button>
            </div>
          </div>
        </section>
      )}

      {assignLotForm && (
        <div className="modal-overlay" onClick={() => !assignLotForm.busy && setAssignLotForm(null)}>
          <div className="modal-card stock-edit-receipt-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Прописать лот остатку без партии</h3>
            <p className="hint-small">
              {SKU_LABEL[assignLotForm.sku] || assignLotForm.sku}
              {' · '}
              {WH_SHORT[assignLotForm.warehouse] || assignLotForm.warehouse}
              {' · '}
              баланс не меняется — появляется строка лота
            </p>
            <div className="stock-edit-receipt-form">
              <label>Лот
                <input
                  value={assignLotForm.lot_number}
                  onChange={(e) => setAssignLotForm({ ...assignLotForm, lot_number: e.target.value })}
                  placeholder="номер партии"
                  autoFocus
                  list={`assign-lot-suggest-${assignLotForm.sku}`}
                />
                <datalist id={`assign-lot-suggest-${assignLotForm.sku}`}>
                  {stockLots
                    .filter((l) => l.sku === assignLotForm.sku && l.lot_number)
                    .map((l) => l.lot_number)
                    .filter((v, i, a) => a.indexOf(v) === i)
                    .map((lot) => (
                      <option key={lot} value={lot} />
                    ))}
                </datalist>
              </label>
              <label>Кол-во, шт
                <input
                  type="number"
                  min="1"
                  value={assignLotForm.qty}
                  onChange={(e) => setAssignLotForm({ ...assignLotForm, qty: e.target.value })}
                />
              </label>
              <label>Срок (ДД.ММ.ГГГГ)
                <input
                  value={assignLotForm.expiration}
                  onChange={(e) => setAssignLotForm({ ...assignLotForm, expiration: e.target.value })}
                  placeholder="необязательно"
                />
              </label>
              <label className="grow">Комментарий
                <input
                  value={assignLotForm.comment}
                  onChange={(e) => setAssignLotForm({ ...assignLotForm, comment: e.target.value })}
                />
              </label>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                disabled={assignLotForm.busy}
                onClick={() => setAssignLotForm(null)}
              >
                Отмена
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={assignLotForm.busy}
                onClick={submitAssignLot}
              >
                {assignLotForm.busy ? 'Записываем…' : 'Присвоить лот'}
              </button>
            </div>
          </div>
        </div>
      )}

      {receiptSuccess && (
        <div className="modal-overlay" onClick={() => setReceiptSuccess(null)}>
          <div className="modal-card receipt-success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="receipt-success-icon">✓</div>
            <h3>Приход успешно записан</h3>
            <p>
              Склад: <b>
                {receiptSuccess.lines.some((l) => l.tender_reserve)
                  ? (receiptSuccess.lines.every((l) => l.tender_reserve)
                    ? 'Тендер R'
                    : `${WH_SHORT[receiptSuccess.warehouse] || receiptSuccess.warehouse} + тендер R`)
                  : (WH_SHORT[receiptSuccess.warehouse] || receiptSuccess.warehouse)}
              </b>
            </p>
            <ul>
              {receiptSuccess.lines.map((line) => (
                <li key={`${line.sku}-${line.lot_number || line.sn_from || 'no-lot'}`}>
                  <b>{SKU_LABEL[line.sku]}</b>: {line.qty.toLocaleString('ru-RU')} шт
                  {line.tender_reserve ? ' · тендер R' : ''}
                  {line.lot_number ? ` · лот ${line.lot_number}` : ''}
                  {line.sn_from && line.sn_to ? ` · SN ${line.sn_from}–${line.sn_to}` : ''}
                  {!line.lot_number && line.tender_reserve && line.sku === 'gluck' ? ' · без лота' : ''}
                  {line.production_date ? ` · производство ${line.production_date}` : ''}
                </li>
              ))}
            </ul>
            <div className="modal-actions">
              <button type="button" className="btn-primary" onClick={() => setReceiptSuccess(null)}>
                Готово
              </button>
            </div>
          </div>
        </div>
      )}

      {editMovement && (
        <div className="modal-overlay" onClick={closeEditMovement}>
          <div className="modal-card stock-edit-receipt-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Редактировать приход #{editMovement.id}</h3>
            <p className="hint-small">
              {SKU_LABEL[editMovement.sku] || editMovement.sku}
              {' · '}
              {WH_SHORT[editMovement.warehouse_code] || editMovement.warehouse_code}
              {' · '}
              <b>+{editMovement.qty}</b> шт
              {' · '}
              {fmtDateTime(editMovement.created_at)}
            </p>
            <p className="hint-small">
              {isUnlabeledTenderGluck(editMovement)
                ? 'Глюки тендера без лота: количество должно совпадать с диапазоном SN. Склад не меняется.'
                : 'Склад не меняется. Уменьшение количества или удаление возможно, только если соответствующий остаток ещё есть на складе.'}
            </p>
            {editError && <p className="tender-ship-result err">{editError}</p>}
            <div className="stock-edit-receipt-form">
              <label>Количество, шт
                <input
                  type="number"
                  min="1"
                  value={editForm.qty}
                  onChange={(e) => setEditForm({ ...editForm, qty: e.target.value })}
                  autoFocus
                />
              </label>
              {isUnlabeledTenderGluck(editMovement) ? (
                <>
                  <label>SN с
                    <input
                      value={editForm.sn_from}
                      onChange={(e) => setEditForm({ ...editForm, sn_from: e.target.value })}
                      placeholder="ARIA260100001"
                    />
                  </label>
                  <label>SN по
                    <input
                      value={editForm.sn_to}
                      onChange={(e) => setEditForm({ ...editForm, sn_to: e.target.value })}
                      placeholder="ARIA260103000"
                    />
                  </label>
                  <label>Дата производства (ДД.ММ.ГГГГ)
                    <input
                      value={editForm.production_date}
                      onChange={(e) => setEditForm({
                        ...editForm,
                        production_date: formatDmyInput(e.target.value),
                      })}
                      placeholder="опционально"
                    />
                  </label>
                </>
              ) : BOX_SKUS.includes(editMovement.sku) && (
                <>
                  <label>Лот
                    <input
                      value={editForm.lot_number}
                      onChange={(e) => setEditForm({ ...editForm, lot_number: e.target.value })}
                      placeholder="A24L11-5x3"
                    />
                  </label>
                  <label>Срок годности (ДД.ММ.ГГГГ)
                    <input
                      value={editForm.expiration_date}
                      onChange={(e) => setEditForm({
                        ...editForm,
                        expiration_date: formatDmyInput(e.target.value),
                      })}
                      placeholder="14.02.2025"
                    />
                  </label>
                  <label>Дата производства (ДД.ММ.ГГГГ)
                    <input
                      value={editForm.production_date}
                      onChange={(e) => setEditForm({
                        ...editForm,
                        production_date: formatDmyInput(e.target.value),
                      })}
                      placeholder="опционально"
                    />
                  </label>
                  {editMovement.sku === 'gluck' && (
                    <>
                      <label>SN с
                        <input
                          value={editForm.sn_from}
                          onChange={(e) => setEditForm({ ...editForm, sn_from: e.target.value })}
                          placeholder="ARIA261100001"
                        />
                      </label>
                      <label>SN по
                        <input
                          value={editForm.sn_to}
                          onChange={(e) => setEditForm({ ...editForm, sn_to: e.target.value })}
                          placeholder="ARIA261102720"
                        />
                      </label>
                    </>
                  )}
                </>
              )}
              <label>Комментарий
                <input
                  value={editForm.comment}
                  onChange={(e) => setEditForm({ ...editForm, comment: e.target.value })}
                />
              </label>
            </div>
            <div className="modal-actions">
              {role === 'admin' && (
                <button type="button" className="btn-danger" onClick={deleteEditMovement}>
                  Удалить приход
                </button>
              )}
              <button type="button" className="btn-secondary" onClick={closeEditMovement}>
                Отмена
              </button>
              <button type="button" className="btn-primary" onClick={saveEditMovement}>
                Сохранить
              </button>
            </div>
          </div>
        </div>
      )}

      {stockView === 'tender' && (<>
        {tenderPlans.map((plan) => (
          <TenderPlanBanner key={plan.sku} plan={plan} />
        ))}
        <section className="stock-card tender-ship-card">
          <div className="stock-card-head">
            <h3>Отгрузка тендера</h3>
            <span className="tender-badge-head">расход со склада R</span>
          </div>
          <p className="hint-small stock-expense-hint">
            Списание с тендерного резерва. Несколько лотов — одна фура, отдельные движения.
            «Набрать {TENDER_TRUCK_PALLETS} палет» — только цельные коробки FEFO.
            «Весь остаток» — полный остаток R по SKU, включая неполные коробки.
          </p>
          {tenderShipMsg && (
            <p className={`tender-ship-result ${tenderShipMsg.type === 'ok' ? 'ok' : 'err'}`}>
              {tenderShipMsg.text}
            </p>
          )}
          {tenderBySku.every((g) => !(g.total > 0)) && reserveLots.length === 0 ? (
            <p className="hint-small">Нет остатков для отгрузки.</p>
          ) : (
            <>
              <div className="stock-inline-form tender-ship-form">
                <label>Продукт
                  <select
                    value={tenderShip.sku}
                    onChange={(e) => {
                      setTenderShip({
                        ...tenderShip,
                        sku: e.target.value,
                        sn_from: '',
                        sn_to: '',
                      });
                      setTenderShipPicks({});
                    }}
                  >
                    <option value="tp50">ТП50</option>
                    <option value="gluck">Глюкометр</option>
                  </select>
                </label>
                <label title="Сколько цельных коробок на палете">Кор/пал.
                  <input
                    type="number"
                    min="1"
                    value={tenderShip.boxes_per_pallet}
                    onChange={(e) => setTenderShip({
                      ...tenderShip,
                      boxes_per_pallet: e.target.value,
                    })}
                  />
                </label>
                <label className="tender-client-field">Клиент
                  <input
                    value={tenderShip.client_name}
                    onChange={(e) => setTenderShip({ ...tenderShip, client_name: e.target.value })}
                    placeholder={DEFAULT_TENDER_CLIENT}
                  />
                </label>
                <label className="tender-client-field">Комментарий
                  <input
                    value={tenderShip.comment}
                    onChange={(e) => setTenderShip({ ...tenderShip, comment: e.target.value })}
                    placeholder={`напр. фура ${TENDER_TRUCK_PALLETS} палет`}
                  />
                </label>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={fillTenderShipTruck}
                  disabled={tenderShipBusy || tenderShipLotsAvail.length === 0}
                >
                  Набрать {TENDER_TRUCK_PALLETS} палет
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={fillTenderShipAll}
                  disabled={tenderShipBusy || tenderShipLotsAvail.length === 0}
                >
                  Весь остаток
                </button>
                <button
                  type="button"
                  className="btn-link"
                  onClick={() => setTenderShipPicks({})}
                  disabled={tenderShipBusy || tenderShipPicked.length === 0}
                >
                  Снять все
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={submitTenderShip}
                  disabled={tenderShipBusy || tenderShipPicked.length === 0}
                >
                  {tenderShipBusy ? 'Запись…' : 'Записать отгрузку'}
                </button>
              </div>
              {unlabeledPicked && (
                <div className="stock-inline-form tender-ship-form" style={{ marginTop: 8 }}>
                  <label>SN с
                    <input
                      value={tenderShip.sn_from}
                      onChange={(e) => setTenderShip({ ...tenderShip, sn_from: e.target.value })}
                      placeholder="необяз."
                    />
                  </label>
                  <label>SN по
                    <input
                      value={tenderShip.sn_to}
                      onChange={(e) => setTenderShip({ ...tenderShip, sn_to: e.target.value })}
                      placeholder="необяз."
                    />
                  </label>
                </div>
              )}
              {tenderShipLotsAvail.length === 0 ? (
                <p className="hint-small">Нет лотов с остатком по этому SKU.</p>
              ) : (
                <table className="stats-table tender-lots-table tender-ship-lots">
                  <thead>
                    <tr>
                      <th />
                      <th>Лот</th>
                      <th>Остаток</th>
                      <th>Кор. на R</th>
                      <th>Отгрузить, шт</th>
                      <th>Кор.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenderShipLotsAvail.map((l) => {
                      const key = tenderPickKey(l);
                      const picked = key in tenderShipPicks;
                      const qty = parseInt(tenderShipPicks[key], 10) || 0;
                      const lotBoxes = Math.floor((l.qty || 0) / tenderShipBoxSize);
                      const lotRem = (l.qty || 0) % tenderShipBoxSize;
                      const pickBoxes = picked ? Math.floor(qty / tenderShipBoxSize) : 0;
                      return (
                        <tr key={key} className={picked ? 'tender-ship-lot-picked' : undefined}>
                          <td>
                            <input
                              type="checkbox"
                              checked={picked}
                              onChange={() => {
                                setTenderShipPicks((prev) => {
                                  const next = { ...prev };
                                  if (key in next) delete next[key];
                                  else next[key] = String(l.qty);
                                  return next;
                                });
                              }}
                            />
                          </td>
                          <td>
                            <b>{l.unlabeled ? 'без лота · SN' : l.lot_number}</b>
                            {l.expiration_date ? (
                              <span className="cell-muted"> · до {isoToDmy(l.expiration_date)}</span>
                            ) : null}
                          </td>
                          <td>{(l.qty || 0).toLocaleString('ru-RU')}</td>
                          <td className="cell-muted">
                            {lotBoxes || '—'}{lotRem ? ` + ${lotRem}` : ''}
                          </td>
                          <td>
                            <input
                              type="number"
                              min="0"
                              max={l.qty || 0}
                              className="tender-ship-qty"
                              value={picked ? (tenderShipPicks[key] || '') : ''}
                              placeholder={picked ? '' : '—'}
                              onChange={(e) => {
                                const v = e.target.value;
                                setTenderShipPicks((prev) => {
                                  const next = { ...prev };
                                  if (!v) delete next[key];
                                  else next[key] = v;
                                  return next;
                                });
                              }}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              min="0"
                              max={lotBoxes}
                              className="tender-ship-qty"
                              value={picked && pickBoxes > 0 ? String(pickBoxes) : ''}
                              placeholder={picked && lotRem && !pickBoxes ? `хвост ${lotRem}` : (picked ? '' : '—')}
                              onChange={(e) => {
                                const boxes = parseInt(e.target.value, 10);
                                setTenderShipPicks((prev) => {
                                  const next = { ...prev };
                                  if (!Number.isFinite(boxes) || boxes <= 0) delete next[key];
                                  else next[key] = String(boxes * tenderShipBoxSize);
                                  return next;
                                });
                              }}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </>
          )}
          {tenderShipQtyTotal > 0 && (
            <p className={`tender-ship-total${tenderShipTruckExact ? ' is-exact' : ''}`}>
              Итого: {tenderShipQtyTotal.toLocaleString('ru-RU')} шт
              {' · '}{tenderShipBoxesTotal} кор.
              {tenderShipRemTotal ? ` + ${tenderShipRemTotal} шт` : ''}
              {' · '}{fmtPallets(tenderShipBoxesTotal, tenderShipPerPallet)}
              {' · '}{tenderShipPicked.length} {tenderShipPicked.length === 1 ? 'лот' : tenderShipPicked.length < 5 ? 'лота' : 'лотов'}
              {' · '}{tenderShipBoxSize} шт/кор. · {tenderShipPerPallet} кор/пал.
              {tenderShipTruckExact ? ` · фура ${TENDER_TRUCK_PALLETS} палет ровно` : ''}
            </p>
          )}
        </section>

        <section className="stock-card tender-card">
          <div className="stock-card-head">
            <h3>Тендерный резерв</h3>
            <span className="tender-badge-head">только склад R · не участвует в отгрузке заявок</span>
          </div>
          <p className="hint-small stock-expense-hint">
            Приход: вкладка «Приход» → блок «На тендер R». Глюкометры без ТП10 — SN-диапазон, без лота. ТП50 — лот. Глюки без лота на Фергану не перемещаем.
          </p>
          {tenderBySku.length === 0 ? (
            <p className="hint-small">Резерв пуст.</p>
          ) : (
            <div className="tender-sku-list">
              {tenderBySku.map(({ sku, total, shipped_total: shippedTotal, shipped_boxes: shippedBoxesIn, received_boxes: receivedBoxesIn, next_box_number: nextBoxNo, lots }) => {
                const boxSize = BOX_QTY[sku] || BOX_SIZE;
                const boxesTotal = Math.floor(total / boxSize);
                const remTotal = total % boxSize;
                const uniqueLots = lots.length;
                const shippedBoxes = shippedBoxesIn || Math.floor((shippedTotal || 0) / boxSize);
                const receivedBoxes = receivedBoxesIn || lots.reduce((s, l) => s + (l.received_boxes || 0), 0);
                const nextBox = nextBoxNo || (receivedBoxes ? receivedBoxes + 1 : 1);
                const lotKeys = lots.map((lot) => tenderLotKey(sku, lot));
                const skuLotsOpen = lotKeys.length > 0 && lotKeys.every((k) => tenderOpenLots.has(k));
                const skuPlan = tenderPlans.find((p) => p.sku === sku);
                return (
                  <div key={sku} className="tender-sku-block">
                    <div className="tender-sku-head">
                      <div>
                        <div className="tender-sku-name">{SKU_LABEL[sku]}</div>
                        <div className="tender-sku-meta">
                          {sku === 'gluck'
                            ? `${uniqueLots} ${uniqueLots === 1 ? 'партия' : 'партий'}`
                            : `${uniqueLots} ${uniqueLots === 1 ? 'лот' : uniqueLots < 5 ? 'лота' : 'лотов'}`}
                          {lots.reduce((n, l) => n + (l.batches?.length || 0), 0) > 0 && (
                            <>
                              {' · '}
                              {lots.reduce((n, l) => n + (l.batches?.length || 0), 0)} приходов
                            </>
                          )}
                          {(shippedTotal || 0) > 0 && (
                            <>
                              {' · '}
                              отгружено {(shippedTotal || 0).toLocaleString('ru-RU')} шт
                              {shippedBoxes > 0 ? ` (${shippedBoxes} кор.)` : ''}
                            </>
                          )}
                          {receivedBoxes > 0 && (
                            <>
                              {' · '}
                              {boxesTotal} + {shippedBoxes} = {receivedBoxes} кор.
                            </>
                          )}
                          {nextBox > 1 && (
                            <>
                              {' · '}
                              <span className="tender-next-box">след. кор. №{nextBox}</span>
                            </>
                          )}
                          {skuPlan?.plan_qty > 0 && (
                            <>
                              {' · '}
                              <span className="tender-plan-inline">
                                план {skuPlan.plan_qty.toLocaleString('ru-RU')}: упаковать ещё {skuPlan.remaining_pack.toLocaleString('ru-RU')}
                                {' · '}отгрузить ещё {skuPlan.remaining_ship.toLocaleString('ru-RU')}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="tender-sku-total">
                        <span className="tender-qty">{total.toLocaleString('ru-RU')}</span>
                        <span className="tender-unit">шт</span>
                        {boxesTotal > 0 && (
                          <span className="tender-boxes">
                            {boxesTotal} кор.{remTotal ? ` + ${remTotal}` : ''}
                          </span>
                        )}
                        {lots.length > 1 && (
                          <button
                            type="button"
                            className="btn-link tender-sku-toggle"
                            onClick={() => toggleTenderSkuLots(sku, lots)}
                          >
                            {skuLotsOpen ? 'свернуть лоты' : 'развернуть лоты'}
                          </button>
                        )}
                      </div>
                    </div>
                    {lots.length === 0 ? (
                      <p className="hint-small">Нет разбивки по лотам (только баланс SKU).</p>
                    ) : (
                      lots.map((lot) => {
                        const lotKey = tenderLotKey(sku, lot);
                        const isOpen = tenderOpenLots.has(lotKey);
                        const boxes = Math.floor(lot.qty / boxSize);
                        const rem = lot.qty % boxSize;
                        const batches = Array.isArray(lot.batches) ? lot.batches : [];
                        const received = lot.received_qty
                          || batches.reduce((s, b) => s + (b.qty || 0), 0);
                        const shipped = lot.shipped_qty || 0;
                        const isShipped = lot.qty <= 0 && shipped > 0;
                        const isPartial = lot.qty > 0 && shipped > 0;
                        const boxRange = fmtBoxRange(lot.box_from, lot.box_to);
                        const batchCount = batches.length;
                        return (
                          <div
                            key={lotKey}
                            className={`tender-lot-block tender-lot-acc${isShipped ? ' is-shipped' : ''}${isPartial ? ' is-partial' : ''}${isOpen ? ' is-open' : ''}`}
                          >
                            <button
                              type="button"
                              className="tender-lot-summary"
                              aria-expanded={isOpen}
                              onClick={() => toggleTenderLot(lotKey)}
                            >
                              <span className={`tender-lot-chevron${isOpen ? ' is-open' : ''}`} aria-hidden>▸</span>
                              <span className="tender-lot-code">
                                {lot.unlabeled ? 'без лота · SN' : lot.lot_number}
                                {isShipped && <span className="tender-shipped-badge">отгружен</span>}
                                {isPartial && <span className="tender-partial-badge">частично</span>}
                                {boxRange && <span className="tender-lot-boxes">{boxRange}</span>}
                              </span>
                              <span className="tender-lot-qty">
                                остаток <b>{lot.qty.toLocaleString('ru-RU')}</b> шт
                                {boxes > 0 ? ` · ${boxes} кор.${rem ? ` + ${rem}` : ''}` : ''}
                                {received > 0 && (
                                  <>
                                    {' · '}
                                    <span className="cell-muted">
                                      приход {received.toLocaleString('ru-RU')}
                                    </span>
                                  </>
                                )}
                                {shipped > 0 && (
                                  <>
                                    {' · '}
                                    <span className="tender-lot-shipped">
                                      отгружено <b>{shipped.toLocaleString('ru-RU')}</b>
                                    </span>
                                  </>
                                )}
                                {(lot.transferred_qty || 0) > 0 && (
                                  <>
                                    {' · '}
                                    <span className="tender-lot-transferred">
                                      на Фергану {lot.transferred_qty.toLocaleString('ru-RU')}
                                    </span>
                                  </>
                                )}
                                {batchCount > 0 && (
                                  <>
                                    {' · '}
                                    <span className="cell-muted">
                                      {batchCount} {batchCount === 1 ? 'приход' : batchCount < 5 ? 'прихода' : 'приходов'}
                                    </span>
                                  </>
                                )}
                              </span>
                            </button>
                            {isOpen && (
                              <div className="tender-lot-body">
                            {batches.length > 0 && (
                              <p className="hint-small tender-lot-formula">
                                {received.toLocaleString('ru-RU')} приход
                                {(lot.shipped_qty || 0) > 0 && ` − ${(lot.shipped_qty || 0).toLocaleString('ru-RU')} отгрузка`}
                                {(lot.transferred_qty || 0) > 0 && ` − ${(lot.transferred_qty || 0).toLocaleString('ru-RU')} на F`}
                                {' = '}
                                <b>{lot.qty.toLocaleString('ru-RU')}</b> остаток
                              </p>
                            )}
                            {batches.length === 0 ? (
                              <table className="stats-table tender-lots-table">
                                <thead>
                                  <tr>
                                    <th>Производство</th>
                                    <th>Срок годности</th>
                                    <th>Остаток</th>
                                    <th>Коробки</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr>
                                    <td>{isoToDmy(lot.production_date) || '—'}</td>
                                    <td>{isoToDmy(lot.expiration_date) || '—'}</td>
                                    <td><b>{lot.qty.toLocaleString('ru-RU')}</b></td>
                                    <td>
                                      {boxRange
                                        ? <span className="tender-lot-boxes">{boxRange}</span>
                                        : <span className="cell-muted">—</span>}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            ) : (
                              <table className="stats-table tender-lots-table">
                                <thead>
                                  <tr>
                                    <th>Приход</th>
                                    <th>#</th>
                                    <th>Дата производства</th>
                                    {lot.unlabeled ? <th>SN</th> : <th>Срок годности</th>}
                                    <th>Шт</th>
                                    <th>Коробки</th>
                                    {(role === 'admin' || lot.unlabeled) && <th />}
                                  </tr>
                                </thead>
                                <tbody>
                                  {batches.map((b, idx) => (
                                    <tr
                                      key={b.id || `${lot.lot_number || 'no-lot'}-${b.created_at || idx}`}
                                      className={idx === 0 ? 'tender-receipt-latest' : undefined}
                                    >
                                      <td className="cell-nowrap">{fmtDateTime(b.created_at) || '—'}</td>
                                      <td className="cell-muted">{b.id ? `#${b.id}` : '—'}</td>
                                      <td>{isoToDmy(b.production_date) || '—'}</td>
                                      {lot.unlabeled ? (
                                        <td className="cell-nowrap">
                                          {b.sn_from && b.sn_to ? `${b.sn_from}–${b.sn_to}` : '—'}
                                        </td>
                                      ) : (
                                        <td>{isoToDmy(b.expiration_date) || '—'}</td>
                                      )}
                                      <td><b>{b.qty.toLocaleString('ru-RU')}</b></td>
                                      <td>
                                        {fmtBoxRange(b.box_from, b.box_to)
                                          ? <span className="tender-lot-boxes">{fmtBoxRange(b.box_from, b.box_to)}</span>
                                          : <span className="cell-muted">—</span>}
                                      </td>
                                      {(role === 'admin' || lot.unlabeled) && (
                                        <td>
                                          {role === 'admin' && b.id && (
                                            <button
                                              type="button"
                                              className="btn-link"
                                              onClick={() => openEditMovement({
                                                id: b.id,
                                                type: 'receipt',
                                                sku,
                                                warehouse_code: 'R',
                                                qty: b.qty,
                                                lot_number: lot.unlabeled ? null : lot.lot_number,
                                                sn_from: b.sn_from,
                                                sn_to: b.sn_to,
                                                production_date: b.production_date,
                                                expiration_date: b.expiration_date,
                                                comment: b.comment || '',
                                                created_at: b.created_at,
                                              })}
                                            >
                                              Изменить
                                            </button>
                                          )}
                                          {lot.unlabeled && b.id && b.sn_from && (
                                            <>
                                              {role === 'admin' && ' · '}
                                              <button
                                                type="button"
                                                className="btn-link"
                                                disabled={tenderLabelsLoading}
                                                onClick={() => printTenderPackingList(b.id)}
                                              >
                                                лист PDF
                                              </button>
                                              {' · '}
                                              <button
                                                type="button"
                                                className="btn-link"
                                                disabled={tenderLabelsLoading}
                                                onClick={() => printTenderGluckBoxLabels(b.id)}
                                              >
                                                бирки PDF
                                              </button>
                                            </>
                                          )}
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section className="stock-card tender-shipments-card">
          <div className="stock-card-head">
            <h3>Журнал отгрузок тендера</h3>
          </div>
          {tenderShipments.length === 0 ? (
            <p className="hint-small">Отгрузок ещё не записано.</p>
          ) : (
            <div className="stock-journal-scroll">
              <table className="stats-table tender-lots-table">
                <thead>
                  <tr>
                    <th>Дата</th>
                    <th>Клиент</th>
                    <th>Продукт</th>
                    <th>Лот / SN</th>
                    <th>Шт</th>
                    <th>Кор.</th>
                    <th>Комментарий</th>
                    <th>Кто</th>
                    {role === 'admin' && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {groupTenderShipmentRows(tenderShipments).map((g) => {
                    const qtySum = g.items.reduce((s, m) => s + Math.abs(m.qty || 0), 0);
                    const boxSize = BOX_QTY[g.sku] || BOX_SIZE;
                    const boxes = Math.floor(qtySum / boxSize);
                    const colSpan = role === 'admin' ? 9 : 8;
                    return (
                      <Fragment key={g.items.map((m) => m.id).join('-')}>
                        {g.items.length > 1 && (
                          <tr className="tender-ship-group">
                            <td colSpan={colSpan}>
                              Фура · {g.items.length} лота · {qtySum.toLocaleString('ru-RU')} шт
                              {boxes ? ` · ${boxes} кор.` : ''}
                            </td>
                          </tr>
                        )}
                        {g.items.map((m) => (
                          <tr key={m.id} className={g.items.length > 1 ? 'tender-ship-group-row' : undefined}>
                            <td className="cell-nowrap">{fmtDateTime(m.created_at)}</td>
                            <td>{m.client_name || '—'}</td>
                            <td>{SKU_LABEL[m.sku] || m.sku}</td>
                            <td>
                              <b>
                                {m.lot_number
                                  || (m.sn_from && m.sn_to ? `${m.sn_from}–${m.sn_to}` : 'без лота')}
                              </b>
                            </td>
                            <td><b>{Math.abs(m.qty).toLocaleString('ru-RU')}</b></td>
                            <td className="cell-muted">
                              {Math.floor(Math.abs(m.qty) / (BOX_QTY[m.sku] || BOX_SIZE)) || '—'}
                            </td>
                            <td>{m.comment || '—'}</td>
                            <td className="cell-muted">{m.created_by || '—'}</td>
                            {role === 'admin' && (
                              <td className="cell-nowrap">
                                <button
                                  type="button"
                                  className="btn-link"
                                  onClick={() => setTenderEditShip({
                                    id: m.id,
                                    client_name: m.client_name || DEFAULT_TENDER_CLIENT,
                                    comment: m.comment || '',
                                  })}
                                >
                                  ✎
                                </button>
                                {' '}
                                <button
                                  type="button"
                                  className="btn-link danger"
                                  onClick={() => cancelTenderShipment(m.id)}
                                >
                                  ✕
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {tenderEditShip && (
          <div className="modal-overlay" onClick={() => setTenderEditShip(null)} role="presentation">
            <div className="modal-card" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
              <h3>Правка отгрузки #{tenderEditShip.id}</h3>
              <label className="field-block">Клиент
                <input
                  value={tenderEditShip.client_name}
                  onChange={(e) => setTenderEditShip({ ...tenderEditShip, client_name: e.target.value })}
                />
              </label>
              <label className="field-block">Комментарий
                <input
                  value={tenderEditShip.comment}
                  onChange={(e) => setTenderEditShip({ ...tenderEditShip, comment: e.target.value })}
                />
              </label>
              <div className="modal-actions">
                <button type="button" className="btn-ghost" onClick={() => setTenderEditShip(null)}>Отмена</button>
                <button type="button" className="btn-primary" onClick={saveTenderShipmentEdit}>Сохранить</button>
              </div>
            </div>
          </div>
        )}

      </>)}

      {stockView === 'labels' && (
        <section className="stock-card tender-labels-card">
          <div className="stock-card-head">
            <h3>Бирки коробок</h3>
            <span className="tender-badge-head">вертикальные · 2 на лист A4</span>
          </div>
          {labelsMsg && (
            <p className={`tender-ship-result ${labelsMsg.type === 'ok' ? 'ok' : 'err'}`}>
              {labelsMsg.text}
            </p>
          )}
          <div className="tender-labels-bar">
            <button
              type="button"
              className={labelSourcesLoaded ? 'btn-ghost' : 'btn-primary'}
              disabled={labelSourcesLoading}
              onClick={loadLabelSources}
            >
              {labelSourcesLoading
                ? 'Читаю Google Sheets…'
                : (labelSourcesLoaded ? 'Обновить из Google Sheets' : 'Загрузить листы упаковки')}
            </button>
            {!labelSourcesLoaded && !labelSourcesLoading && (
              <span className="hint-small">
                Листы не читаются автоматически — печать бирок разовая.
              </span>
            )}
          </div>
          {!labelSourcesLoaded ? null : (!hasTp50Labels && !hasAria10Sheet && !hasAria11Labels ? (
            <p className="hint-small">
              Нет лотов ТП50 в таблице и нет коробок глюкометров в Gluck_tender / Глюк Фер Склад.
            </p>
          ) : (
            <div className="tender-sku-list labels-sku-list">
              {hasTp50Labels && (() => {
                const tp50Open = labelsOpenSections.has('tp50');
                return (
                <div className={`tender-sku-block${tp50Open ? ' is-open' : ''}`}>
                  <button
                    type="button"
                    className="tender-sku-head labels-sku-toggle"
                    aria-expanded={tp50Open}
                    onClick={() => toggleLabelsSection('tp50')}
                  >
                    <span className={`tender-lot-chevron${tp50Open ? ' is-open' : ''}`} aria-hidden>▸</span>
                    <div>
                      <div className="tender-sku-name">ТП50 · тендер</div>
                      <div className="tender-sku-meta">
                        цельные коробки {BOX_QTY.tp50} шт
                        {tp50LabelLots.length > 0 && (
                          <>
                            {' · '}
                            {tp50LabelLots.length} {tp50LabelLots.length === 1 ? 'лот' : tp50LabelLots.length < 5 ? 'лота' : 'лотов'}
                          </>
                        )}
                        {tp50LabelPartials.length > 0 && (
                          <>
                            {' · '}
                            хвост {tp50LabelPartials.length} {tp50LabelPartials.length === 1 ? 'коробка' : 'коробки'}
                          </>
                        )}
                      </div>
                    </div>
                  </button>
                  {tp50Open && tp50LabelLots.map((l) => {
                    const range = l.box_range || fmtBoxRange(l.box_from, l.box_to);
                    return (
                      <div key={l.lot_number} className="tender-lot-block labels-lot-row">
                        <div className="labels-lot-main">
                          <span className="tender-lot-code">{l.lot_number}</span>
                          {range && <span className="tender-lot-boxes">{range}</span>}
                          <span className="labels-lot-meta">{l.count} кор.</span>
                        </div>
                        <button
                          type="button"
                          className="btn-primary btn-sm"
                          disabled={tenderLabelsLoading || !l.lot_number}
                          onClick={() => printTenderBoxLabels({ lot: l.lot_number })}
                        >
                          {tenderLabelsLoading ? 'PDF…' : 'Печать'}
                        </button>
                      </div>
                    );
                  })}
                  {tp50Open && tp50LabelPartials.map((b) => (
                    <div key={`partial-${b.box}`} className="tender-lot-block labels-lot-row labels-partial-row">
                      <div className="labels-lot-main">
                        <span className="tender-lot-code">{b.lot_number || '—'}</span>
                        <span className="tender-lot-boxes">№{b.box}</span>
                        <span className="labels-lot-meta">хвост · {b.qty} шт</span>
                      </div>
                      <button
                        type="button"
                        className="btn-primary btn-sm"
                        disabled={tenderLabelsLoading}
                        onClick={() => printTenderBoxLabels({
                          box: b.box,
                          lot: (b.lot_numbers && b.lot_numbers[0]) || b.lot_number,
                        })}
                      >
                        {tenderLabelsLoading ? 'PDF…' : `Бирка №${b.box}`}
                      </button>
                    </div>
                  ))}
                </div>
                );
              })()}
              {hasAria10Sheet && (() => {
                const aria10Open = labelsOpenSections.has('aria10');
                return (
                <div className={`tender-sku-block${aria10Open ? ' is-open' : ''}`}>
                  <button
                    type="button"
                    className="tender-sku-head labels-sku-toggle"
                    aria-expanded={aria10Open}
                    onClick={() => toggleLabelsSection('aria10')}
                  >
                    <span className={`tender-lot-chevron${aria10Open ? ' is-open' : ''}`} aria-hidden>▸</span>
                    <div>
                      <div className="tender-sku-name">ARIA10 · тендер</div>
                      <div className="tender-sku-meta">
                        Gluck_tender · {BOX_QTY.gluck} шт/кор.
                        {hasAria10Labels ? (
                          <>
                            {' · '}
                            {aria10LabelBoxes.length} кор.
                            {' · '}GROSS/NET {tenderGluckSheet.weighed || 0}/{aria10LabelBoxes.length}
                          </>
                        ) : (
                          <> · лист пуст или нет SN</>
                        )}
                      </div>
                    </div>
                  </button>
                  {aria10Open && hasAria10Labels && (
                    <div className="tender-lot-block labels-lot-row">
                      <div className="labels-lot-main">
                        {tenderGluckSheet.box_range && (
                          <span className="tender-lot-boxes">{tenderGluckSheet.box_range}</span>
                        )}
                        <span className="labels-lot-meta">
                          {aria10LabelBoxes.length} кор.
                          {' · '}GROSS/NET {tenderGluckSheet.weighed || 0}/{aria10LabelBoxes.length}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="btn-primary btn-sm"
                        disabled={tenderLabelsLoading}
                        onClick={() => printTenderGluckBoxLabels()}
                      >
                        {tenderLabelsLoading ? 'PDF…' : 'Печать'}
                      </button>
                    </div>
                  )}
                  {aria10Open && !hasAria10Labels && (
                    <p className="hint-small labels-empty-hint">Нет коробок в Gluck_tender (нужны BOX + SN_FROM + SN_TO).</p>
                  )}
                </div>
                );
              })()}
              {hasAria11Labels && (() => {
                const aria11Open = labelsOpenSections.has('aria11');
                return (
                <div className={`tender-sku-block${aria11Open ? ' is-open' : ''}`}>
                  <button
                    type="button"
                    className="tender-sku-head labels-sku-toggle"
                    aria-expanded={aria11Open}
                    onClick={() => toggleLabelsSection('aria11')}
                  >
                    <span className={`tender-lot-chevron${aria11Open ? ' is-open' : ''}`} aria-hidden>▸</span>
                    <div>
                      <div className="tender-sku-name">ARIA11 · склад Фергана</div>
                      <div className="tender-sku-meta">
                        Глюк Фер Склад · {BOX_QTY.gluck} шт/кор.
                        {' · '}
                        {aria11LabelLots.length} {aria11LabelLots.length === 1 ? 'лот' : aria11LabelLots.length < 5 ? 'лота' : 'лотов'}
                      </div>
                    </div>
                  </button>
                  {aria11Open && aria11LabelLots.map((l) => {
                    const range = l.box_range || fmtBoxRange(l.box_from, l.box_to);
                    return (
                      <div key={l.lot_number} className="tender-lot-block labels-lot-row">
                        <div className="labels-lot-main">
                          <span className="tender-lot-code">{l.lot_number}</span>
                          {range && <span className="tender-lot-boxes">{range}</span>}
                          <span className="labels-lot-meta">
                            {l.count} кор.{l.qty ? ` · ${l.qty} шт` : ''}
                            {' · '}GROSS/NET {l.weighed || 0}/{l.count}
                            {' · '}SN {l.with_sn || 0}/{l.count}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="btn-primary btn-sm"
                          disabled={tenderLabelsLoading || !l.lot_number}
                          onClick={() => printWarehouseGluckBoxLabels({ lot: l.lot_number })}
                        >
                          {tenderLabelsLoading ? 'PDF…' : 'Печать'}
                        </button>
                      </div>
                    );
                  })}
                </div>
                );
              })()}
            </div>
          ))}

          <h4>Сформированные ранее</h4>
          {labelPrints.length === 0 ? (
            <p className="hint-small">Пока ничего не формировали.</p>
          ) : (
            <table className="stats-table stock-journal-table">
              <thead>
                <tr>
                  <th>Когда</th>
                  <th>Вид</th>
                  <th>Лот</th>
                  <th>Коробки</th>
                  <th>Кто</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {labelPrints.map((row) => (
                  <tr key={row.id}>
                    <td className="cell-nowrap">{fmtDateTime(row.created_at)}</td>
                    <td>{row.kind_label}</td>
                    <td>{row.lot_number || '—'}</td>
                    <td className="cell-nowrap">
                      {row.box_range || '—'}
                      {row.boxes_count ? ` · ${row.boxes_count} шт` : ''}
                    </td>
                    <td className="cell-muted">{row.created_by || '—'}</td>
                    <td className="cell-nowrap">
                      <button
                        type="button"
                        className="btn-link"
                        disabled={tenderLabelsLoading}
                        onClick={() => reprintLabels(row)}
                      >
                        скачать снова
                      </button>
                      {role === 'admin' && (
                        <>
                          {' · '}
                          <button
                            type="button"
                            className="btn-link"
                            onClick={() => deleteLabelPrint(row)}
                          >
                            удалить
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      {stockView === 'tashkent' && (
        <section className="stock-card">
          <div className="stock-card-head">
            <h3>Остатки — Ташкент</h3>
            <div className="stock-card-head-right">
              <div className="warehouse-filter stock-period-filter" role="group" aria-label="Период">
                <button
                  type="button"
                  className={marketingMonthKey === marketingCurrentMonthKey ? 'active' : ''}
                  onClick={() => setMarketingMonthKey(marketingCurrentMonthKey)}
                >
                  {monthNameOnly(marketingCurrentMonthKey)}
                </button>
                <button
                  type="button"
                  className={marketingMonthKey === marketingPrevMonthKey ? 'active' : ''}
                  onClick={() => setMarketingMonthKey(marketingPrevMonthKey)}
                >
                  {monthNameOnly(marketingPrevMonthKey)}
                </button>
                <button
                  type="button"
                  className={marketingMonthKey === 'all' ? 'active' : ''}
                  onClick={() => setMarketingMonthKey('all')}
                >
                  За весь период
                </button>
              </div>
            </div>
          </div>
          <table className="stats-table stock-matrix">
            <thead>
              <tr>
                <th>Продукт</th>
                <th>{marketingAllPeriod ? 'Начало' : 'На начало месяца'}</th>
                <th>Отгружено по заявкам</th>
                <th>Маркетинг</th>
                <th>{!marketingAllPeriod && marketingMonthSummary.is_current_month === false ? 'На конец мес.' : 'Остаток'}</th>
              </tr>
            </thead>
            <tbody>
              {STOCK_SKUS.map((sku) => {
                const monthRow = tashkentMonthRow(sku);
                const opening = monthRow?.opening_qty;
                const orders = monthRow?.orders_qty;
                const remainder = monthRow?.current_qty;
                const mkt = (sku === 'i3' || sku === 'i6')
                  ? (monthRow?.marketing_qty ?? 0)
                  : null;
                const extraIn = marketingAllPeriod ? 0 : (monthRow?.incoming_qty || 0);
                const extraOut = marketingAllPeriod ? 0 : (monthRow?.other_outgoing_qty || 0);
                return (
                  <tr key={sku} className="stock-matrix-summary">
                    <td><b>{SKU_LABEL[sku]}</b></td>
                    <td><b>{marketingAllPeriod ? '—' : (opening ?? '—')}</b></td>
                    <td><b>{orders ?? 0}</b></td>
                    <td><b>{mkt == null ? '—' : mkt}</b></td>
                    <td>
                      <b>{remainder || '—'}</b>
                      {(extraIn || extraOut) ? (
                        <div className="hint-small">
                          {extraIn ? `+${extraIn} приход` : ''}
                          {extraIn && extraOut ? ', ' : ''}
                          {extraOut ? `−${extraOut} прочий расход` : ''}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="hint-small">
            Только Ташкент. Период: {marketingAllPeriod
              ? 'весь период'
              : monthLabel(marketingMonthSummary.month_key || marketingMonthKey)}.
            {marketingAllPeriod
              ? ' Цифры по каждой позиции за всё время, без суммирования разных SKU.'
              : ' Начало месяца = остаток до 1-го числа; отгрузки и маркетинг — за выбранный месяц.'}
            {' '}Всего маркетинг SN: i3 {marketingSummary.i3 || 0} · i6 {marketingSummary.i6 || 0}.
          </p>

          {canMarketingScan && (
            <div className="scan-block marketing-scan-block">
              <label htmlFor="marketing-scan">Скан SN на маркетинг (i3/i6)</label>
              <input
                id="marketing-comment"
                value={marketingComment}
                onChange={(e) => setMarketingComment(e.target.value)}
                placeholder={marketingBatchId
                  ? 'Комментарий не нужен: партия уже открыта'
                  : 'Комментарий (врач / повод) — опционально'}
                autoComplete="off"
                disabled={marketingBusy || Boolean(marketingBatchId)}
              />
              <input
                id="marketing-scan"
                ref={marketingScanRef}
                value={marketingScan}
                onChange={(e) => setMarketingScan(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  doMarketingScan();
                }}
                placeholder="DataMatrix..."
                autoComplete="off"
                disabled={marketingBusy}
              />
              <p className="hint-small">
                Сканер жмёт Enter — SN списывается сразу без кнопки.
                {currentMarketingBatch && (
                  <> Текущая запись: i3 {currentMarketingBatch.qty_i3} · i6 {currentMarketingBatch.qty_i6}
                    {' · '}итого {currentMarketingBatch.qty_total}</>
                )}
              </p>
              <div className="actions">
                <button type="button" className="btn-secondary" onClick={closeMarketingBatch} disabled={marketingBusy}>
                  Новая запись
                </button>
              </div>
            </div>
          )}
          {marketingMsg && (
            <p className={marketingMsg.type === 'err' ? 'hint-small warn' : 'hint-small sync-ok'}>
              {marketingMsg.text}
            </p>
          )}

          {marketingBatches.length > 0 && (
            <>
              <h4 className="marketing-list-title">Журнал списаний маркетинг</h4>
              <table className="stats-table">
                <thead>
                  <tr>
                    <th>Дата</th>
                    <th>i3</th>
                    <th>i6</th>
                    <th>Итого</th>
                    <th>Комментарий</th>
                    <th>Кто</th>
                  </tr>
                </thead>
                <tbody>
                  {marketingBatches.map((row) => (
                    <tr key={row.batch_id}>
                      <td>{row.created_at ? new Date(row.created_at).toLocaleString('ru-RU') : '—'}</td>
                      <td>{row.qty_i3 || 0}</td>
                      <td>{row.qty_i6 || 0}</td>
                      <td><b>{row.qty_total || 0}</b></td>
                      <td>{row.comment || '—'}</td>
                      <td>{row.created_by || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {marketingItems.length > 0 && (
            <>
              <h4 className="marketing-list-title">SN в списаниях</h4>
              <table className="stats-table">
                <thead>
                  <tr>
                    <th>Дата</th>
                    <th>Модель</th>
                    <th>SN</th>
                    <th>Комментарий</th>
                    <th>Кто</th>
                    {canMarketingCancel && <th />}
                  </tr>
                </thead>
                <tbody>
                  {marketingItems.map((row) => (
                    <tr key={row.sn}>
                      <td>{row.created_at ? new Date(row.created_at).toLocaleString('ru-RU') : '—'}</td>
                      <td><span className="model-tag">{row.model}</span></td>
                      <td className="sn-value">{row.sn}</td>
                      <td>{row.comment || '—'}</td>
                      <td>{row.created_by || '—'}</td>
                      {canMarketingCancel && (
                        <td>
                          <button
                            type="button"
                            className="btn-danger btn-small"
                            disabled={marketingBusy}
                            onClick={() => cancelMarketingItem(row.sn)}
                          >
                            Отменить
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </section>
      )}
    </div>
  );
}

function fmtEmuTime(ts) {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleString('ru-RU', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return String(ts);
  }
}

function isEmuTerminalCancelUi(statusCode, statusTitle) {
  const c = String(statusCode || '').toUpperCase();
  const t = String(statusTitle || '').toLowerCase().replace(/ё/g, 'е');
  if (['CANCEL', 'CANCELED', 'CANCELLED', 'PARTLYCANCEL', 'RETURN', 'UNCONFIRM', 'REFUSAL', 'LOST', 'DAMAGE', 'DELETED', 'ANNUL', 'ANNULLED', 'VOID'].includes(c)) {
    return true;
  }
  return /отмен|аннулир|удален|возврат|не удалось согласовать|не доставлен|отказ|закрыт/.test(t);
}

function isEmuCourierIssuedUi(statusCode, statusTitle) {
  const c = String(statusCode || '').toUpperCase();
  const t = String(statusTitle || '').toLowerCase();
  if (['COURIERDELIVERY', 'COURIERDELIVERING', 'COURIER'].includes(c)) return true;
  return /выдан курьер|у курьер|передан курьер/.test(t);
}

function orderEmuTrack(order) {
  return order?.emu_awb || order?.emu_barcode || '';
}

function ShipmentLinkBlock({ order, token, canEdit, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [ariaNumber, setAriaNumber] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const isPrimaryEligible = order?.order_type === 'cgm' || order?.order_type === 'glucometer';
  const beforeEmu = !order?.emu_created_at && !order?.emu_ordercode;
  const canAddLink = canEdit
    && isPrimaryEligible
    && !order?.shipment_primary_id
    && ['ASSEMBLED', 'IN_TRANSIT', 'DELIVERED'].includes(order?.status)
    && beforeEmu;

  const linkByNumber = async () => {
    const raw = ariaNumber.trim();
    if (!raw) {
      setError('Введите номер ARIA, например 326');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await apiFetch(token, `/orders/${order.id}/shipment-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ external_order_id: raw }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Не удалось привязать');
        return;
      }
      setOpen(false);
      setAriaNumber('');
      onUpdated?.(data.order);
    } finally {
      setBusy(false);
    }
  };

  const unlink = async () => {
    if (!window.confirm('Отвязать заявки от совместной отправки?')) return;
    setBusy(true);
    try {
      const res = await apiFetch(token, `/orders/${order.id}/shipment-link`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Не удалось отвязать');
        return;
      }
      onUpdated?.(data.order);
    } finally {
      setBusy(false);
    }
  };

  const linkModal = open && (
    <div className="modal-overlay" onClick={() => setOpen(false)} role="presentation">
      <div className="modal-card" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <h3>
          Привязать ARIA к
          {' '}
          {order.order_type === 'glucometer' ? 'ARIA' : 'CGM'}
          {' '}
          #{order.external_order_id}
        </h3>
        <p className="hint-small">
          Одна коробка / один EMU. Можно несколько ARIA (корпуса клиники). Пример: 326 или ARIA #326.
          Привязка до «В EMU».
        </p>
        <label>
          Номер ARIA
          <input
            autoFocus
            value={ariaNumber}
            onChange={(e) => setAriaNumber(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && linkByNumber()}
            placeholder="326"
            disabled={busy}
          />
        </label>
        {error && <p className="hint-small warn">{error}</p>}
        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={() => setOpen(false)} disabled={busy}>
            Закрыть
          </button>
          <button type="button" className="btn-primary" onClick={linkByNumber} disabled={busy}>
            {busy ? '…' : 'Привязать'}
          </button>
        </div>
      </div>
    </div>
  );

  if (order?.is_shipment_linked && order?.shipment_primary) {
    return (
      <div className="shipment-link-banner hint-small">
        📦 В одной отправке с {shipmentPrimaryTitle(order.shipment_primary)}
        {order.emu_via_primary ? ' · EMU на primary' : ''}
        {canEdit && (
          <button type="button" className="btn-secondary btn-sm" onClick={unlink} disabled={busy} style={{ marginLeft: 8 }}>
            Отвязать
          </button>
        )}
      </div>
    );
  }

  if (!isPrimaryEligible) return null;

  const hasLinked = (order.linked_orders?.length || 0) > 0;

  return (
    <div className="shipment-link-block" style={{ marginBottom: 12 }}>
      {hasLinked && (
        <div className="shipment-linked-list">
          <span>🔗 В одной коробке: </span>
          <span className="badge" style={{ marginRight: 6 }}>
            {order.order_type === 'glucometer' ? 'ARIA' : 'CGM'} #{order.external_order_id} (primary)
          </span>
          {order.linked_orders.map((l) => (
            <span key={l.id} className="badge" style={{ marginRight: 6 }}>
              ARIA #{l.external_order_id}
            </span>
          ))}
          {canEdit && (
            <button type="button" className="btn-secondary btn-sm" onClick={unlink} disabled={busy}>
              Отвязать все
            </button>
          )}
        </div>
      )}
      {canAddLink && (
        <button
          type="button"
          className="btn-secondary btn-sm"
          style={hasLinked ? { marginTop: 8 } : undefined}
          onClick={() => { setOpen(true); setError(''); setAriaNumber(''); }}
        >
          🔗 + ARIA в одну отправку
        </button>
      )}
      {linkModal}
    </div>
  );
}

function EmuTrackingBlock({
  order, events, onRefresh, refreshing, onPrintLabel, onDownloadLabel, onRecreate, recreating,
}) {
  if (!order?.emu_orderno && !order?.emu_created_at) return null;
  const track = orderEmuTrack(order);
  const cancelled = isEmuTerminalCancelUi(order.emu_status_code, order.emu_status_title);
  const hasCourier = order.emu_courier_name || order.emu_courier_phone;
  return (
    <div className="emu-block">
      <div className="emu-block-head">
        <h3>EMU доставка</h3>
        <div className="emu-block-actions">
          {onDownloadLabel && (
            <button type="button" className="btn-primary btn-sm" onClick={onDownloadLabel} disabled={cancelled}>
              ⬇ EMU-бирка
            </button>
          )}
          {onPrintLabel && (
            <button type="button" className="btn-secondary btn-sm" onClick={onPrintLabel} disabled={cancelled}>
              🖨 Печать
            </button>
          )}
          <button type="button" className="btn-secondary btn-sm" onClick={onRefresh} disabled={refreshing}>
            {refreshing ? '…' : '↻ Статус'}
          </button>
          {onRecreate && (
            <button type="button" className="btn-primary btn-sm" onClick={onRecreate} disabled={recreating}>
              {recreating ? '…' : '↻ Создать заново'}
            </button>
          )}
        </div>
      </div>
      <div className="emu-meta">
        <div><b>№ EMU:</b> {order.emu_orderno || '—'}</div>
        {track && <div><b>Трек:</b> {track}</div>}
        {(order.emu_weight != null || order.emu_places) && (
          <div>
            <b>Вес / места:</b> {order.emu_weight != null ? `${order.emu_weight} кг` : '—'}
            {order.emu_places ? ` · ${order.emu_places} мест` : ''}
          </div>
        )}
        <div>
          <b>Статус:</b>{' '}
          <span className="badge badge-emu">{order.emu_status_title || order.emu_status_code || '—'}</span>
        </div>
        <div>
          <b>Район доставки:</b> {order.emu_town_name || order.emu_town_code || '—'}
          {order.emu_zipcode ? ` · индекс ${order.emu_zipcode}` : ''}
          {order.emu_area ? ` · СЦ: ${order.emu_area}` : ''}
        </div>
        {order.emu_via_primary && (
          <p className="hint-small">EMU оформлена на primary CGM — бирка одна на коробку</p>
        )}
        {hasCourier && (
          <div className="emu-courier">
            <b>Курьер:</b> {order.emu_courier_name || '—'}
            {order.emu_courier_phone && (
              <>
                {' · '}
                <a href={`tel:${String(order.emu_courier_phone).replace(/[^\d+]/g, '')}`}>
                  {order.emu_courier_phone}
                </a>
              </>
            )}
          </div>
        )}
      </div>
      {cancelled && (
        <p className="hint-small emu-warn">
          Заявка закрыта/отменена в ЛК EMU — официальная бирка (со штрихкодом) больше не выдаётся
          (waybill: orders not found). Наша «бирка» без ШК — только fallback.
          Нажмите «Создать заново»: отвяжем трек и откроем форму (номер станет …-r2 — старый orderno в EMU нельзя удалить).
        </p>
      )}
      {!cancelled && onRecreate && (
        <p className="hint-small emu-warn">
          Ошиблись городом / отменили в ЛК? «Создать заново» → новый номер …-r2
          (в EMU orderno нельзя переиспользовать после отмены).
        </p>
      )}
      {!cancelled && (
        <p className="hint-small">
          Скачивайте «EMU-бирка» (Zebra ~100×100) и печатайте на термопринтере.
          Не Print→PDF в Safari — он кладёт этикетку на лист A4 и подставляет Title «VitaNova склад».
          «Сергелий» в timeline — филиал статуса EMU, не район доставки.
        </p>
      )}
      {events?.length > 0 && (
        <ol className="emu-timeline">
          {events.map((ev, i) => {
            const showCourier = hasCourier
              && isEmuCourierIssuedUi(ev.status_code, ev.status_title);
            return (
              <li key={`${ev.status_code}-${ev.eventtime || i}`}>
                <span className="emu-tl-time">{fmtEmuTime(ev.eventtime || ev.created_at)}</span>
                <span className="emu-tl-title">{ev.status_title || ev.status_code}</span>
                {(ev.eventstore || ev.eventtown) && (
                  <span className="emu-tl-place">
                    {[ev.eventstore, ev.eventtown ? `филиал ${ev.eventtown}` : null].filter(Boolean).join(' · ')}
                  </span>
                )}
                {showCourier && (
                  <span className="emu-tl-courier">
                    Курьер: {order.emu_courier_name || '—'}
                    {order.emu_courier_phone && (
                      <>
                        {' · '}
                        <a href={`tel:${String(order.emu_courier_phone).replace(/[^\d+]/g, '')}`}>
                          {order.emu_courier_phone}
                        </a>
                      </>
                    )}
                  </span>
                )}
                {ev.message && <span className="emu-tl-msg">{ev.message}</span>}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

function EmuSendModal({
  open, order, token, onClose, onCreated,
}) {
  const [form, setForm] = useState({
    person: '', phone: '', address: '', company: '', instruction: '',
    weight: '1', places: '1', service: '3',
    box: '', length: '', width: '', height: '',
    sendDims: false,
  });
  const [towns, setTowns] = useState([]);
  const [services, setServices] = useState([
    { code: '3', name: 'НА ДОМ' },
    { code: '1', name: 'ДО ОФИСА' },
  ]);
  const [townQ, setTownQ] = useState('');
  const [townCode, setTownCode] = useState('');
  const [townName, setTownName] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [districtMeta, setDistrictMeta] = useState({});
  const [tashkentDistricts, setTashkentDistricts] = useState([]);
  const [regionalTowns, setRegionalTowns] = useState([]);
  const [townWarn, setTownWarn] = useState('');
  const [genericCode, setGenericCode] = useState('272765');
  const [suggestions, setSuggestions] = useState([]);
  const [townHints, setTownHints] = useState([]);
  const [townHintNote, setTownHintNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Справочник EMU — ~200 городов, плоским списком не выбрать
  const regionalTownGroups = useMemo(() => {
    const byRegion = new Map();
    for (const t of regionalTowns) {
      const key = t.city || '';
      if (!byRegion.has(key)) byRegion.set(key, []);
      byRegion.get(key).push(t);
    }
    return [...byRegion.entries()].sort((a, b) => a[0].localeCompare(b[0], 'ru'));
  }, [regionalTowns]);

  useEffect(() => {
    if (!open || !token) return undefined;
    let cancelled = false;
    (async () => {
      const res = await apiFetch(token, '/emu/districts');
      if (!res.ok || cancelled) return;
      const data = await res.json();
      if (cancelled) return;
      if (data.districts?.length) setTashkentDistricts(data.districts);
      if (data.regional?.length) setRegionalTowns(data.regional);
    })();
    return () => { cancelled = true; };
  }, [open, token]);

  useEffect(() => {
    if (!open || !order) return;
    setForm({
      person: order.name_rec || '',
      phone: order.phone_rec || '',
      address: order.address || '',
      company: order.client_name || '',
      instruction: '',
      weight: '1',
      places: '1',
      service: '3',
      box: '',
      length: '',
      width: '',
      height: '',
      sendDims: false,
    });
    setTownCode(order.district_emu_code || '');
    setTownName(order.district_emu_name || '');
    setZipcode('');
    setTownQ('');
    setTowns([]);
    setError('');
    setSuggestions([]);
    setTownHints([]);
    setTownHintNote('');
  }, [open, order]);

  useEffect(() => {
    if (!open || !token || !order?.id) return undefined;
    let cancelled = false;
    (async () => {
      const res = await apiFetch(token, `/orders/${order.id}/emu-town-suggest`);
      if (!res.ok || cancelled) return;
      const data = await res.json();
      if (cancelled) return;
      setTownHints(data.suggestions || []);
      setTownHintNote(data.note || '');
      if (data.autofill?.town_code) {
        setTownCode(data.autofill.town_code);
        setTownName(data.autofill.town_name || '');
        setTownQ(data.autofill.town_name || order.city || '');
        const meta = districtMeta[data.autofill.town_code];
        if (meta?.zipcode) setZipcode(meta.zipcode);
        else setZipcode('');
      }
    })();
    return () => { cancelled = true; };
  }, [open, token, order]);

  useEffect(() => {
    if (!open || !token) return undefined;
    let cancelled = false;
    (async () => {
      const res = await apiFetch(token, '/emu/services');
      if (!res.ok || cancelled) return;
      const data = await res.json();
      if (cancelled) return;
      if (data.services?.length) setServices(data.services);
      if (data.defaultService) {
        setForm((f) => ({ ...f, service: String(data.defaultService) }));
      }
    })();
    return () => { cancelled = true; };
  }, [open, token]);

  useEffect(() => {
    if (!open || !token) return undefined;
    let cancelled = false;
    const t = setTimeout(async () => {
      const res = await apiFetch(token, `/emu/towns?q=${encodeURIComponent(townQ || '')}`);
      if (cancelled) return;
      if (!res.ok) return;
      const data = await res.json();
      setTowns(data.towns || []);
      setTownWarn(data.warning || '');
      if (data.districtMeta) setDistrictMeta(data.districtMeta);
      if (data.genericTashkentCode) setGenericCode(data.genericTashkentCode);
      if (data.remoteError && !(data.towns || []).length) {
        setError(data.remoteError.startsWith('EMU') ? data.remoteError : `EMU townlist: ${data.remoteError}`);
      } else {
        setError('');
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, token, townQ]);

  useEffect(() => {
    if (!open || townCode || towns.length !== 1) return;
    const t = towns[0];
    if (t.code === genericCode) return;
    setTownCode(t.code);
    setTownName(t.name);
    const meta = districtMeta[t.code];
    if (meta?.zipcode) setZipcode(String(meta.zipcode));
    else setZipcode('');
  }, [open, towns, townCode, genericCode, districtMeta]);

  useEffect(() => {
    if (!open || !townCode) return;
    const meta = districtMeta[townCode];
    if (meta?.zipcode && !zipcode) setZipcode(String(meta.zipcode));
  }, [open, townCode, districtMeta, zipcode]);

  useEffect(() => {
    if (!open || !token) return undefined;
    const q = form.company || form.phone || '';
    if (String(q).trim().length < 2) {
      setSuggestions([]);
      return undefined;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      const res = await apiFetch(token, `/orders/suggest-clients?q=${encodeURIComponent(q.trim())}`);
      if (!res.ok || cancelled) return;
      const rows = await res.json();
      if (!cancelled) setSuggestions(rows);
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, token, form.company, form.phone]);

  if (!open || !order) return null;

  const applySuggestion = (s) => {
    setForm((f) => ({
      ...f,
      company: s.client_name || f.company,
      phone: s.phone_rec || f.phone,
      person: s.name_rec || f.person,
      address: s.address || f.address,
    }));
    if (s.city) setTownQ(s.city);
    setSuggestions([]);
  };

  const selectTown = (t) => {
    setTownCode(t.code);
    setTownName(t.name);
    const meta = districtMeta[t.code];
    if (meta?.zipcode) setZipcode(String(meta.zipcode));
    else setZipcode('');
  };

  const EMU_BOX_PRESETS = {
    XL: { length: '40', width: '40', height: '60' },
    L: { length: '40', width: '40', height: '40' },
  };

  const applyBox = (code) => {
    const p = EMU_BOX_PRESETS[code];
    if (!p) return;
    setForm((f) => ({ ...f, box: code, sendDims: true, ...p }));
  };

  const boxVolumeM3 = (() => {
    if (!form.sendDims) return null;
    const l = parseFloat(String(form.length).replace(',', '.'));
    const w = parseFloat(String(form.width).replace(',', '.'));
    const h = parseFloat(String(form.height).replace(',', '.'));
    if (![l, w, h].every((n) => Number.isFinite(n) && n > 0)) return null;
    return Number(((l * w * h) / 1e6).toFixed(3));
  })();

  const volumetricKg = (() => {
    if (!form.sendDims) return null;
    const l = parseFloat(String(form.length).replace(',', '.'));
    const w = parseFloat(String(form.width).replace(',', '.'));
    const h = parseFloat(String(form.height).replace(',', '.'));
    if (![l, w, h].every((n) => Number.isFinite(n) && n > 0)) return null;
    return Number(((l * w * h) / 4000).toFixed(2));
  })();

  const weightNumPreview = parseFloat(String(form.weight).replace(',', '.'));
  const billablePreview = (() => {
    if (!form.sendDims || volumetricKg == null || !Number.isFinite(weightNumPreview)) return null;
    return Math.max(weightNumPreview, volumetricKg);
  })();

  const submit = async () => {
    setError('');
    if (!townCode) {
      setError('Выберите город EMU — кликните пункт в списке ниже. Для Ташкента нужен район (Чиланзар, Яшнабад…), не только «Ташкент»');
      return;
    }
    if (townCode === genericCode) {
      setError('Нельзя «Ташкент» без района. Выберите Чиланзар, Юнусабад и т.д.');
      return;
    }
    if (!form.phone.trim() || !form.address.trim() || !(form.person.trim() || form.company.trim())) {
      setError('Проверьте телефон, адрес и ФИО/компанию');
      return;
    }
    const weightNum = parseFloat(String(form.weight).replace(',', '.'));
    const placesNum = parseInt(form.places, 10);
    const lengthCm = parseFloat(String(form.length).replace(',', '.'));
    const widthCm = parseFloat(String(form.width).replace(',', '.'));
    const heightCm = parseFloat(String(form.height).replace(',', '.'));
    if (!Number.isFinite(weightNum) || weightNum <= 0) {
      setError('Укажите вес посылки в кг (например 1.5)');
      return;
    }
    if (!Number.isFinite(placesNum) || placesNum < 1) {
      setError('Количество мест ≥ 1');
      return;
    }
    if (form.sendDims && ![lengthCm, widthCm, heightCm].every((n) => Number.isFinite(n) && n > 0)) {
      setError('Включили габариты — укажите Д×Ш×В или выберите XL/L');
      return;
    }
    setLoading(true);
    try {
      const res = await apiFetch(token, `/orders/${order.id}/emu`, {
        method: 'POST',
        body: JSON.stringify({
          person: form.person,
          phone: form.phone,
          address: form.address,
          company: form.company,
          instruction: form.instruction,
          town_code: townCode,
          town_name: townName,
          zipcode: zipcode.trim() || undefined,
          weight: weightNum,
          places: placesNum,
          ...(form.sendDims ? {
            length_cm: lengthCm,
            width_cm: widthCm,
            height_cm: heightCm,
            send_dims: true,
          } : {}),
          service: form.service || '3',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Ошибка создания заявки EMU');
        return;
      }
      onCreated(data);
      onClose();
    } catch (e) {
      setError(e.message || 'Сеть');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={loading ? undefined : onClose} role="presentation">
      <div className="modal-card emu-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <h3>Отправка в EMU — #{order.external_order_id}</h3>
        <p className="hint-small emu-warn">
          Район города критичен: неверный пункт → чужой сортировочный центр и задержка.
          Проверьте данные получателя перед отправкой.
        </p>
        {townWarn && <p className="hint-small warn">{townWarn}</p>}

        <div className="emu-form">
          <label>Клиент / компания
            <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
          </label>
          <p className="hint-small full" style={{ gridColumn: '1 / -1', margin: 0 }}>
            Крупная ячейка на бирке EMU = «компания получателя». Для юрлица лучше полное имя (как у менеджеров: OOO VITA NOVA DIAGNOSTIC), не короткое «ОФИС».
            Префикс трека FA = сдача в ПВЗ Фергана; EMU = создано через API — нормально.
          </p>
          <label>Получатель (ФИО)
            <input value={form.person} onChange={(e) => setForm({ ...form, person: e.target.value })} />
          </label>
          <label>Телефон
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </label>
          <label>Вес, кг *
            <input
              type="number"
              min="0.1"
              step="0.1"
              value={form.weight}
              onChange={(e) => setForm({ ...form, weight: e.target.value })}
            />
          </label>
          <label>Мест *
            <input
              type="number"
              min="1"
              step="1"
              value={form.places}
              onChange={(e) => setForm({ ...form, places: e.target.value })}
            />
          </label>
          <div className="emu-box-presets full">
            <span className="emu-box-label">Габариты (опционально — как в ПВЗ лучше без них)</span>
            <p className="hint-small" style={{ margin: 0 }}>
              По умолчанию шлём только вес — так дешевле (пример UZUM FA…0740: 10 кг без volume → 108к, с объёмом XL/L → ~150к).
              Volume/габариты включайте только если реально нужно.
            </p>
            <label className="emu-box-bill">
              <input
                type="checkbox"
                checked={Boolean(form.sendDims)}
                onChange={(e) => setForm({
                  ...form,
                  sendDims: e.target.checked,
                  ...(e.target.checked && !form.length
                    ? { box: 'L', length: '40', width: '40', height: '40' }
                    : {}),
                })}
              />
              Указать габариты / Volume
            </label>
            {form.sendDims && (
              <>
                <div className="emu-box-btns">
                  <button
                    type="button"
                    className={form.box === 'XL' ? 'selected' : ''}
                    onClick={() => applyBox('XL')}
                  >
                    XL · 40×40×60
                  </button>
                  <button
                    type="button"
                    className={form.box === 'L' ? 'selected' : ''}
                    onClick={() => applyBox('L')}
                  >
                    L · 40×40×40
                  </button>
                </div>
                <div className="emu-box-dims">
                  <label>Д, см
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={form.length}
                      onChange={(e) => setForm({ ...form, box: 'custom', length: e.target.value })}
                    />
                  </label>
                  <label>Ш, см
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={form.width}
                      onChange={(e) => setForm({ ...form, box: 'custom', width: e.target.value })}
                    />
                  </label>
                  <label>В, см
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={form.height}
                      onChange={(e) => setForm({ ...form, box: 'custom', height: e.target.value })}
                    />
                  </label>
                  <span className="emu-box-vol">
                    Volume: {boxVolumeM3 != null ? `${boxVolumeM3} м³` : '—'}
                    {volumetricKg != null ? ` · ≈${volumetricKg} кг объём.` : ''}
                    {billablePreview != null ? ` → тариф max≈${billablePreview} кг` : ''}
                  </span>
                </div>
              </>
            )}
          </div>
          <label className="full">Режим доставки (service) *
            <select
              value={form.service}
              onChange={(e) => setForm({ ...form, service: e.target.value })}
            >
              {services.map((s) => (
                <option key={s.code} value={s.code}>{s.name} ({s.code})</option>
              ))}
            </select>
          </label>
          <label className="full">Адрес
            <textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </label>
          <label className="full">Комментарий курьеру
            <input value={form.instruction} onChange={(e) => setForm({ ...form, instruction: e.target.value })} />
          </label>
        </div>

        {suggestions.length > 0 && (
          <div className="emu-suggest">
            <div className="hint-small">Из наших прошлых заказов (подставить):</div>
            <ul>
              {suggestions.slice(0, 6).map((s) => (
                <li key={`${s.client_inn}-${s.phone_rec}-${s.address}`}>
                  <button type="button" onClick={() => applySuggestion(s)}>
                    {[s.client_name, s.phone_rec, s.city].filter(Boolean).join(' · ')}
                    {s.address ? ` — ${s.address}` : ''}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {townHints.length > 0 && (
          <div className="emu-suggest emu-town-hints">
            <div className="hint-small">
              Район из истории отгрузок EMU
              {townHintNote ? ` — ${townHintNote}` : ''}
            </div>
            <ul>
              {townHints.map((h) => (
                <li key={h.town_code}>
                  <button
                    type="button"
                    className={townCode === h.town_code ? 'selected' : ''}
                    onClick={() => {
                      setTownCode(h.town_code);
                      setTownName(h.town_name);
                      setTownQ(h.town_name || '');
                      const meta = districtMeta[h.town_code];
                      if (meta?.zipcode) setZipcode(String(meta.zipcode));
                      else setZipcode('');
                    }}
                  >
                    <b>{h.town_name}</b>
                    {' '}
                    <code>{h.town_code}</code>
                    <span className="hint-small">
                      {' '}· {h.hits}× · {h.reason}
                      {h.sample_address ? ` · «${h.sample_address}»` : ''}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="emu-town-block">
          {regionalTowns.length > 0 && (
            <label>Город / область
              <select
                value={townCode && regionalTowns.some((d) => d.code === townCode) ? townCode : ''}
                onChange={(e) => {
                  const d = regionalTowns.find((x) => x.code === e.target.value);
                  if (!d) return;
                  selectTown({ code: d.code, name: d.name });
                  setTownQ(d.name);
                }}
              >
                <option value="">— выберите город —</option>
                {regionalTownGroups.map(([region, items]) => (
                  <optgroup key={region || 'other'} label={region || 'Прочее'}>
                    {items.map((d) => (
                      <option key={d.code} value={d.code}>{d.name}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </label>
          )}
          {tashkentDistricts.length > 0 && (
            <label>Район Ташкента <span className="hint-small">(только если доставка в Ташкент)</span>
              <select
                value={townCode && tashkentDistricts.some((d) => d.code === townCode) ? townCode : ''}
                onChange={(e) => {
                  const d = tashkentDistricts.find((x) => x.code === e.target.value);
                  if (!d) return;
                  selectTown({ code: d.code, name: d.name });
                  setTownQ(d.name);
                }}
              >
                <option value="">— выберите район —</option>
                {tashkentDistricts.map((d) => (
                  <option key={d.code} value={d.code}>{d.label}</option>
                ))}
              </select>
            </label>
          )}
          <label>Поиск района / города EMU
            <input
              value={townQ}
              onChange={(e) => setTownQ(e.target.value)}
              placeholder="Чиланзар, Юнусабад, Фергана…"
            />
          </label>
          {townCode && (
            <div className="emu-town-selected">
              Выбрано: <b>{townName}</b> <code>{townCode}</code>
              {districtMeta[townCode]?.area ? ` · СЦ ${districtMeta[townCode].area}` : ''}
            </div>
          )}
          <label>
            Recipient postcode (индекс на бирке)
            <input
              value={zipcode}
              onChange={(e) => setZipcode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="100021"
            />
          </label>
          <p className="hint-small">
            На бирке FA печатается индекс (zipcode), не строка area.
            Яшнабад: <code>100021</code> + СЦ <code>100047 - Xamzin</code>; Сергелий: <code>100085</code> + Mirabad.
            Без индекса в ЛК пустой Recipient postcode.
          </p>
          {townWarn && <p className="hint-small warn">{townWarn}</p>}
          {!towns.length && (
            <p className="hint-small">
              Выберите город в списке выше или начните ввод в поиске (например «Бухара», «Фергана»).
            </p>
          )}
          <ul className="emu-town-list">
            {towns.map((t) => (
              <li key={t.code}>
                <button
                  type="button"
                  className={townCode === t.code ? 'selected' : ''}
                  disabled={t.code === genericCode}
                  title={t.code === genericCode ? 'Без района нельзя' : undefined}
                  onClick={() => selectTown(t)}
                >
                  <span>{t.name}{t.city ? ` (${t.city})` : ''}</span>
                  <code>{t.code}</code>
                  {t.code === genericCode && <em> запрещено</em>}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {error && <p className="login-error">{error}</p>}

        <div className="edit-actions">
          <button type="button" className="btn-primary" onClick={submit} disabled={loading}>
            {loading ? 'Отправка…' : 'Создать заявку в EMU'}
          </button>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={loading}>Отмена</button>
        </div>
      </div>
    </div>
  );
}

function SyncResultModal({ open, loading, data, error, onClose }) {
  if (!open) return null;

  const renderBlock = (title, block, skipped) => {
    if (skipped) {
      return (
        <div className="sync-block sync-block-muted">
          <strong>{title}</strong>
          <p className="hint-small">Не настроен (нет GLUCOMETER_SHEET_GID в .env)</p>
        </div>
      );
    }
    if (!block) return null;

    const scanned = block.scanned || 0;
    const imported = block.imported || 0;
    const updated = block.updated || 0;
    const existing = block.skippedExisting || 0;
    const empty = block.skippedEmpty || 0;
    const other = Math.max(0, scanned - imported - updated - existing - empty);
    const newPct = scanned ? Math.round((imported / scanned) * 100) : 0;
    const updatedPct = scanned ? Math.round((updated / scanned) * 100) : 0;
    const existPct = scanned ? Math.round((existing / scanned) * 100) : 0;
    const emptyPct = scanned ? Math.round((empty / scanned) * 100) : 0;

    return (
      <div className="sync-block">
        <div className="sync-block-head">
          <strong>{title}</strong>
          {block.sheet && <span className="sync-sheet-name">«{block.sheet}»</span>}
        </div>
        <div className="sync-stats-row">
          <span className="sync-stat sync-stat-new">{imported} новых</span>
          {updated > 0 && <span className="sync-stat sync-stat-new">{updated} обновлено (NEW)</span>}
          <span className="sync-stat">{existing} без изменений</span>
          {empty > 0 && <span className="sync-stat">{empty} без кол-ва</span>}
          {other > 0 && <span className="sync-stat">{other} прочих</span>}
          <span className="sync-stat-total">{scanned} строк в таблице</span>
        </div>
        <div className="sync-bar-track" title="Новые / обновлённые / уже импортированные">
          {imported > 0 && (
            <div className="sync-bar-fill sync-bar-new" style={{ width: `${newPct}%` }} />
          )}
          {updated > 0 && (
            <div className="sync-bar-fill sync-bar-new" style={{ width: `${updatedPct}%`, opacity: 0.65 }} />
          )}
          {existing > 0 && (
            <div className="sync-bar-fill sync-bar-existing" style={{ width: `${existPct}%` }} />
          )}
          {empty > 0 && (
            <div className="sync-bar-fill sync-bar-empty" style={{ width: `${emptyPct}%` }} />
          )}
        </div>
        {block.newOrders?.length > 0 && (
          <ul className="sync-new-list">
            {block.newOrders.map((o) => (
              <li key={`${o.order_type}-${o.external_order_id}`}>
                #{o.external_order_id} {o.client_name ? `· ${o.client_name}` : ''}
              </li>
            ))}
          </ul>
        )}
        {imported === 0 && scanned > 0 && (
          <p className="hint-small">
            {existing > 0 && `Новых нет: ${existing} уже в системе.`}
            {existing === 0 && empty > 0 && `${empty} строк без количества (i3/i6 или Gluck/TP = 0).`}
            {existing === 0 && empty === 0 && 'Новых заявок нет.'}
          </p>
        )}
        {block.rowErrors?.length > 0 && (
          <ul className="sync-wb-list">
            {block.rowErrors.map((e) => (
              <li key={`${e.id}-${e.error}`} className="sync-warn">
                #{e.id}: {e.error}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const wb = data?.sheetsWriteback;

  return (
    <div className="modal-overlay" onClick={loading ? undefined : onClose} role="presentation">
      <div className="modal-card sync-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <h3>Синхронизация Google Sheets</h3>

        {loading && (
          <div className="sync-loading">
            <div className="sync-bar-track sync-bar-indeterminate">
              <div className="sync-bar-fill sync-bar-pulse" />
            </div>
            <p>Читаем таблицу менеджера…</p>
          </div>
        )}

        {!loading && error && <p className="login-error">{error}</p>}

        {!loading && data && (
          <>
            {renderBlock(`${BRAND_SINOCARE} (i3 / i6)`, data.cgm, false)}
            {renderBlock(`${BRAND_ARIA} (глюкометры)`, data.glucometer, data.glucometer?.skipped)}
            {data.rowErrors?.length > 0 && (
              <p className="hint-small warn">
                Не импортировано строк: {data.rowErrors.length}. Смотрите детали выше.
              </p>
            )}

            <div className="sync-writeback-info">
              <strong>Запись статуса в таблицу</strong>
              {!wb?.enabled && (
                <>
                  <p className="hint-small warn">
                    Отключено в .env: <code>SHEETS_STATUS_WRITEBACK=false</code>
                  </p>
                  <p className="hint-small">
                    Удалите строку или поставьте <code>true</code>, затем:
                    <code> docker compose -f docker-compose.prod.yml up -d backend</code>
                  </p>
                </>
              )}
              {wb?.enabled && data.statusWriteback && (
                <>
                  <p className={`hint-small ${(data.statusWriteback.failed || 0) > 0 ? 'warn' : 'sync-ok'}`}>
                    Статусы в таблицу: обновлено {data.statusWriteback.synced}
                    {data.statusWriteback.failed > 0 && `, ошибок ${data.statusWriteback.failed}`}
                    {data.statusWriteback.skipped > 0 && `, пропуск ${data.statusWriteback.skipped}`}
                  </p>
                  {data.statusWriteback.errors?.length > 0 && (
                    <ul className="sync-wb-list">
                      {data.statusWriteback.errors.map((err) => (
                        <li key={err} className="sync-warn">{err}</li>
                      ))}
                    </ul>
                  )}
                </>
              )}
              {wb?.enabled && (
                <ul className="sync-wb-list">
                  <li>
                    {BRAND_SINOCARE}: {wb.cgm?.sheet || '—'}
                    {wb.cgm?.statusColumn
                      ? <span className="sync-ok"> · колонка «{wb.cgm.statusHeader}» ({wb.cgm.statusColIndex != null ? String.fromCharCode(65 + wb.cgm.statusColIndex) : '?'}) ✓</span>
                      : <span className="sync-warn"> · нет колонки Status/Статус</span>}
                    {wb.cgm?.error && <span className="sync-warn"> · {wb.cgm.error}</span>}
                    {wb.cgm?.headers && (
                      <div className="hint-small">заголовки: {wb.cgm.headers.slice(0, 16).join(', ')}</div>
                    )}
                  </li>
                  <li>
                    {BRAND_ARIA}: {wb.glc?.configured === false
                      ? 'не настроен'
                      : (
                        <>
                          {wb.glc?.sheet || '—'}
                          {wb.glc?.statusColumn
                            ? <span className="sync-ok"> · колонка «{wb.glc.statusHeader}» ✓</span>
                            : <span className="sync-warn"> · нет колонки Status/Статус</span>}
                          {wb.glc?.error && <span className="sync-warn"> · {wb.glc.error}</span>}
                          {wb.glc?.headers && (
                            <div className="hint-small">заголовки: {wb.glc.headers.slice(0, 16).join(', ')}</div>
                          )}
                        </>
                      )}
                  </li>
                </ul>
              )}
              {wb?.enabled && (
                <p className="hint-small">
                  SA должен быть <b>Editor</b> на spreadsheet (Viewer → 403).
                  email: <code>sinnocare@sinnocare.iam.gserviceaccount.com</code>.
                  Статус пишется при смене в складе и при sync.
                </p>
              )}
            </div>
          </>
        )}

        <div className="modal-actions">
          <button type="button" className="btn-primary" onClick={onClose} disabled={loading}>
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
}

function LoginScreen({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Ошибка входа');
        return;
      }
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token, data.user);
    } catch {
      setError('Сервер недоступен');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <form className="login-card" onSubmit={handleSubmit}>
        <h1>VitaNova склад</h1>
        <p className="login-sub">Вход в систему</p>
        <label htmlFor="username">Логин</label>
        <input
          id="username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoComplete="username"
          required
        />
        <label htmlFor="password">Пароль</label>
        <input
          id="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          required
        />
        {error && <div className="login-error">{error}</div>}
        <button type="submit" className="btn-primary login-btn" disabled={loading}>
          {loading ? 'Вход…' : 'Войти'}
        </button>
        <div className="login-hint">
          <small>Завсклад: sklad · Markell · Водитель: driver · Админ: admin · Бух: Dzhamilya · Alina</small>
        </div>
      </form>
    </div>
  );
}

function App() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [tab, setTab] = useState(() => loadUiState().tab || 'orders');
  const [orders, setOrders] = useState([]);
  const [warehouseFilter, setWarehouseFilter] = useState(() => {
    const saved = loadUiState().warehouseFilter;
    if (saved === 'T' || saved === 'F') return saved;
    if (saved === '' || saved === 'all') return '';
    return 'F';
  });
  const [statusFilter, setStatusFilter] = useState(() => loadUiState().statusFilter || '');
  const [dateFilter, setDateFilter] = useState(() => loadUiState().dateFilter || '');
  const [orderSearch, setOrderSearch] = useState(() => loadUiState().orderSearch || '');
  const [attentionFilter, setAttentionFilter] = useState(() => loadUiState().attentionFilter || '');
  const [deskMode, setDeskMode] = useState(() => (
    loadUiState().deskMode === 'assemble' ? 'assemble' : 'list'
  ));
  const [shipDrawerOpen, setShipDrawerOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [sns, setSns] = useState([]);
  const [scanInput, setScanInput] = useState('');
  const [scanMsg, setScanMsg] = useState(null); // { type: 'ok'|'err', text }
  const [glcLotLines, setGlcLotLines] = useState([]);
  const [glcStockLots, setGlcStockLots] = useState({ gluck: [], tp50: [] });
  const [searchSn, setSearchSn] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [syncModal, setSyncModal] = useState({ open: false, loading: false, data: null, error: null });
  const [emuModalOpen, setEmuModalOpen] = useState(false);
  const [emuEvents, setEmuEvents] = useState([]);
  const [emuRefreshing, setEmuRefreshing] = useState(false);
  const [emuRecreating, setEmuRecreating] = useState(false);
  const scanRef = useRef(null);

  const setAppTab = (next) => {
    setTab(next);
    setMobileMenuOpen(false);
    const prev = loadUiState();
    localStorage.setItem(UI_STATE_KEY, JSON.stringify({ ...prev, tab: next }));
  };

  const persistOrderFilters = (patch) => {
    const prev = loadUiState();
    localStorage.setItem(UI_STATE_KEY, JSON.stringify({ ...prev, ...patch }));
  };

  const role = user?.role;
  const canScan = role === 'warehouse' || role === 'admin';
  const canPrint = canScan;
  const canSync = role === 'admin';
  const canAdminOrder = role === 'admin';
  const canAccounting = role === 'viewer' || role === 'warehouse' || role === 'admin';
  const canRawUi = role === 'viewer' || role === 'warehouse' || role === 'admin';
  const canWarehouseUi = role === 'warehouse' || role === 'admin';
  const canOrdersUi = role === 'warehouse' || role === 'admin' || role === 'driver';

  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [stockBalances, setStockBalances] = useState([]);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
    setSelected(null);
    setOrders([]);
    setStockBalances([]);
  }, []);

  useEffect(() => {
    if (!token) {
      setAuthLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const res = await apiFetch(token, '/auth/me');
      if (cancelled) return;
      if (!res.ok) {
        logout();
        setAuthLoading(false);
        return;
      }
      const data = await res.json();
      setUser(data.user);
      setAuthLoading(false);
      if (data.user?.role === 'viewer') {
        setTab('accounting');
        const prev = loadUiState();
        localStorage.setItem(UI_STATE_KEY, JSON.stringify({ ...prev, tab: 'accounting' }));
      }
    })();
    return () => { cancelled = true; };
  }, [token, logout]);

  const loadOrders = useCallback(async () => {
    if (!token) return;
    const isGlcTab = tab === 'orders-glc';
    const params = new URLSearchParams({ type: isGlcTab ? 'glucometer' : 'cgm' });
    if (warehouseFilter) params.set('warehouse', warehouseFilter);
    const res = await apiFetch(token, `/orders?${params}`);
    if (res.status === 401) { logout(); return; }
    const data = await res.json();
    if (!res.ok || !Array.isArray(data)) {
      console.error('[orders]', data?.error || 'invalid response');
      setOrders([]);
      return;
    }
    setOrders(data);
  }, [token, logout, tab, warehouseFilter]);

  const loadOrderDetail = useCallback(async (id) => {
    if (!token) return;
    const [o, s] = await Promise.all([
      apiFetch(token, `/orders/${id}`).then((r) => r.json()),
      apiFetch(token, `/orders/${id}/serial-numbers`).then((r) => r.json()),
    ]);
    if (o?.error) {
      console.error('[order]', o.error);
      return;
    }
    setSelected(o);
    setSns(Array.isArray(s) ? s : []);
    setEmuEvents(Array.isArray(o.emu_events) ? o.emu_events : []);

    if (o.order_type === 'glucometer') {
      let lines = glcLotLinesFromOrder(o);
      const wh = o.warehouse_code || 'F';
      let stockLots = { gluck: [], tp50: [] };
      try {
        const invRes = await apiFetch(token, `/stock/inventory?warehouse=${encodeURIComponent(wh)}&sku=gluck,tp50`);
        if (invRes.ok) {
          stockLots = lotsFromInventory(await invRes.json());
        }
      } catch {
        /* ignore */
      }
      try {
        const rv = await apiFetch(
          token,
          `/stock/glc-reservations?warehouse=${encodeURIComponent(wh)}&exclude=${encodeURIComponent(o.id)}`,
        );
        if (rv.ok) {
          const data = await rv.json();
          stockLots = applyGlcReservations(stockLots, data.lots);
        }
      } catch {
        /* ignore */
      }
      setGlcStockLots(stockLots);

      // Автоподстановка только если на складе ровно один лот и строка без лота.
      if (o.status === 'NEW') {
        for (const sku of ['gluck', 'tp50']) {
          const need = sku === 'gluck' ? (o.qty_gluck || 0) : (o.qty_tp50 || 0);
          if (need <= 0) continue;
          const avail = (stockLots[sku] || []).filter((l) => (l.qty || 0) > 0);
          const skuLines = lines.filter((l) => l.sku === sku);
          if (avail.length === 1 && skuLines.length === 1 && !skuLines[0].lot_number) {
            lines = lines.map((l) => (l.key === skuLines[0].key
              ? {
                ...l,
                lot_number: avail[0].lot_number,
                expiration: l.expiration || isoToDmy(avail[0].expiration_date),
                qty: l.qty || String(need),
              }
              : l));
          } else if (avail.length === 1 && skuLines.length === 0) {
            lines = [
              ...lines,
              {
                ...emptyGlcLotLine(sku, need),
                lot_number: avail[0].lot_number,
                expiration: isoToDmy(avail[0].expiration_date),
              },
            ];
          } else if (skuLines.length === 0) {
            lines = [...lines, emptyGlcLotLine(sku, need)];
          }
        }
      }
      setGlcLotLines(lines);
    } else {
      setGlcLotLines([]);
      setGlcStockLots({ gluck: [], tp50: [] });
    }
  }, [token]);

  useEffect(() => { if (user && (tab === 'orders' || tab === 'orders-glc')) loadOrders(); }, [loadOrders, user, tab]);

  useEffect(() => {
    if (!token || !user || tab !== 'orders-glc') return undefined;
    let cancelled = false;
    (async () => {
      const res = await apiFetch(token, '/stock/balances');
      if (!res.ok || cancelled) return;
      const rows = await res.json();
      if (!cancelled) setStockBalances(Array.isArray(rows) ? rows : []);
    })();
    return () => { cancelled = true; };
  }, [token, user, tab, selected?.id]);

  useEffect(() => {
    if (!user) return undefined;
    if (tab !== 'orders' && tab !== 'orders-glc') return undefined;
    const t = setInterval(loadOrders, 30000);
    return () => clearInterval(t);
  }, [loadOrders, user, tab]);

  useEffect(() => {
    if (selected && canScan && ['NEW', 'ASSEMBLING'].includes(selected.status)) {
      focusScannerInput(scanRef);
    }
  }, [selected?.id, selected?.status, canScan]);

  useEffect(() => {
    if (!canScan) return undefined;
    const maybeRefocus = () => {
      if (document.visibilityState === 'hidden') return;
      if (!selected || !['NEW', 'ASSEMBLING'].includes(selected.status)) return;
      if (emuModalOpen || editing || syncModal.open) return;
      if (isTypingTarget(document.activeElement) && document.activeElement !== scanRef.current) return;
      focusScannerInput(scanRef);
    };
    window.addEventListener('focus', maybeRefocus);
    document.addEventListener('visibilitychange', maybeRefocus);
    return () => {
      window.removeEventListener('focus', maybeRefocus);
      document.removeEventListener('visibilitychange', maybeRefocus);
    };
  }, [canScan, selected, emuModalOpen, editing, syncModal.open]);

  useEffect(() => {
    if (!canScan && role !== 'warehouse' && role !== 'admin' && role !== 'driver') return undefined;
    const onKey = (e) => {
      if (e.defaultPrevented) return;
      if (e.key === 'Escape') {
        if (emuModalOpen || syncModal.open) return;
        if (shipDrawerOpen) {
          e.preventDefault();
          setShipDrawerOpen(false);
          return;
        }
        if (selected) {
          e.preventDefault();
          setSelected(null);
        }
        return;
      }
      if (e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        if (isTypingTarget(document.activeElement) && document.activeElement !== scanRef.current) return;
        if (!selected || !canScan || !['NEW', 'ASSEMBLING'].includes(selected.status)) return;
        e.preventDefault();
        focusScannerInput(scanRef);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canScan, role, selected, emuModalOpen, syncModal.open, shipDrawerOpen]);

  const handleSelectOrder = (order) => {
    loadOrderDetail(order.id);
    setScanMsg(null);
    setScanInput('');
    setEditing(false);
    setEmuModalOpen(false);
    setShipDrawerOpen(false);
  };

  const setDeskModePersist = (mode) => {
    setDeskMode(mode);
    persistOrderFilters({ deskMode: mode });
    if (mode !== 'assemble') setShipDrawerOpen(false);
  };

  const refreshEmu = async () => {
    if (!selected?.id || !token) return;
    setEmuRefreshing(true);
    try {
      const res = await apiFetch(token, `/orders/${selected.id}/emu`);
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'EMU refresh failed');
        return;
      }
      if (data.order) {
        setSelected((prev) => (prev ? { ...prev, ...data.order } : data.order));
      }
      setEmuEvents(Array.isArray(data.events) ? data.events : []);
      loadOrders();
    } finally {
      setEmuRefreshing(false);
    }
  };

  const onEmuCreated = (data) => {
    if (data.order) {
      setSelected((prev) => (prev ? { ...prev, ...data.order } : data.order));
    }
    setEmuEvents(Array.isArray(data.events) ? data.events : []);
    loadOrders();
  };

  const recreateEmu = async () => {
    if (!selected?.id) return;
    if (!window.confirm(
      'Отвязать заявку EMU и создать новую?\n'
      + 'Старый orderno в ЛК останется (удалить нельзя). Новая получит …-r2 и официальную бирку.',
    )) return;
    setEmuRecreating(true);
    try {
      const res = await apiFetch(token, `/orders/${selected.id}/emu?force=1`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(data.error || 'Не удалось отвязать EMU');
        return;
      }
      if (data.order) {
        setSelected((prev) => (prev ? { ...prev, ...data.order } : data.order));
      }
      setEmuEvents(Array.isArray(data.events) ? data.events : []);
      loadOrders();
      setEmuModalOpen(true);
    } catch (e) {
      alert(e.message || 'Сеть');
    } finally {
      setEmuRecreating(false);
    }
  };

  const canSendEmu = canScan
    && selected
    && !selected.shipment_primary_id
    && !selected.is_shipment_linked
    && ['ASSEMBLED', 'IN_TRANSIT', 'DELIVERED'].includes(selected.status)
    && !selected.emu_created_at
    && !selected.emu_ordercode;

  const onShipmentUpdated = (order) => {
    if (order) setSelected((prev) => (prev ? { ...prev, ...order } : order));
    if (selected?.id) loadOrderDetail(selected.id);
    loadOrders();
  };

  const startEdit = () => {
    if (!selected) return;
    const isGlc = selected.order_type === 'glucometer';
    setEditForm({
      city: selected.city || '',
      client_name: selected.client_name || '',
      client_inn: selected.client_inn || '',
      address: selected.address || '',
      phone_rec: selected.phone_rec || '',
      name_rec: selected.name_rec || '',
      qty_i3: selected.qty_i3 ?? 0,
      qty_i6: selected.qty_i6 ?? 0,
      qty_gluck: selected.qty_gluck ?? 0,
      qty_tp50: selected.qty_tp50 ?? 0,
      warehouse_code: selected.warehouse_code || 'F',
      _isGlc: isGlc,
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    const isGlc = selected.order_type === 'glucometer' || editForm._isGlc;
    const body = {
      city: editForm.city,
      client_name: editForm.client_name,
      client_inn: editForm.client_inn,
      address: editForm.address,
      phone_rec: editForm.phone_rec,
      name_rec: editForm.name_rec,
      warehouse_code: editForm.warehouse_code,
    };
    if (isGlc) {
      body.qty_gluck = parseInt(editForm.qty_gluck, 10) || 0;
      body.qty_tp50 = parseInt(editForm.qty_tp50, 10) || 0;
    } else {
      body.qty_i3 = parseInt(editForm.qty_i3, 10) || 0;
      body.qty_i6 = parseInt(editForm.qty_i6, 10) || 0;
    }
    const res = await apiFetch(token, `/orders/${selected.id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) {
      alert(data.error);
      return;
    }
    setSelected(data);
    setEditing(false);
    loadOrders();
  };

  const setStockAction = async (stock_action) => {
    if (!selected) return;
    const res = await apiFetch(token, `/orders/${selected.id}/stock-action`, {
      method: 'PATCH',
      body: JSON.stringify({ stock_action }),
    });
    const data = await res.json();
    if (!res.ok) {
      alert(data.error || 'Не удалось сменить тип списания');
      return;
    }
    setSelected(data);
    loadOrders();
  };

  const deleteOrder = async () => {
    const snCount = sns.length;
    const msg = snCount
      ? `Удалить заказ #${selected.external_order_id}? Будет удалено ${snCount} SN.`
      : `Удалить заказ #${selected.external_order_id}?`;
    if (!window.confirm(msg)) return;

    const res = await apiFetch(token, `/orders/${selected.id}`, { method: 'DELETE' });
    const data = await res.json();
    if (!res.ok) {
      alert(data.error);
      return;
    }
    setSelected(null);
    setSns([]);
    setEditing(false);
    loadOrders();
  };

  const handleScan = async () => {
    if (!selected || !scanInput.trim()) return;
    setScanMsg(null);
    const res = await apiFetch(token, `/orders/${selected.id}/scan`, {
      method: 'POST',
      body: JSON.stringify({ barcode: scanInput.trim() }),
    });
    const data = await res.json();
    if (!res.ok) {
      setScanMsg({ type: 'err', text: data.error || 'Ошибка скана' });
    } else {
      setScanMsg({ type: 'ok', text: `${data.model}: ${data.sn}` });
      setSelected(data.order);
      await loadOrderDetail(selected.id);
      await loadOrders();
    }
    setScanInput('');
    focusScannerInput(scanRef);
  };

  const deleteSn = async (sn) => {
    if (!selected || !window.confirm(`Удалить SN ${sn} из заказа?`)) return;
    const res = await apiFetch(token, `/orders/${selected.id}/serial-numbers/${encodeURIComponent(sn)}`, {
      method: 'DELETE',
    });
    const data = await res.json();
    if (!res.ok) {
      alert(data.error);
      return;
    }
    setSelected(data.order);
    await loadOrderDetail(selected.id);
    await loadOrders();
  };

  const setStatus = async (status, extra = {}, orderId = null) => {
    const id = orderId || selected?.id;
    if (!id) return;
    const orderForMeta = selected?.id === id ? selected : orders.find((o) => o.id === id);
    const attachLots = orderForMeta?.order_type === 'glucometer'
      && selected?.id === id
      && ['ASSEMBLED', 'IN_TRANSIT', 'DELIVERED'].includes(status);
    const body = attachLots
      ? { status, glc_lot_lines: glcLotLinesToPayload(extra.glcLotLines || glcLotLines), ...extra }
      : { status, ...extra };
    // не слать внутренний UI-ключ
    if (body.glcLotLines !== undefined) delete body.glcLotLines;
    const res = await apiFetch(token, `/orders/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) {
      alert(data.error);
      return;
    }
    if (selected?.id === id) {
      setSelected(data);
      if (data.order_type === 'glucometer') {
        const lines = glcLotLinesFromOrder(data);
        setGlcLotLines(lines);
        if (['ASSEMBLED', 'IN_TRANSIT', 'DELIVERED'].includes(status)) {
          localStorage.setItem(GLC_LAST_META_KEY, JSON.stringify(glcLotLinesToPayload(lines)));
        }
      }
    }
    loadOrders();
  };

  const handleStatusStep = (targetStatus) => {
    if (!selected || targetStatus === selected.status) return;
    if (!window.confirm(`Сменить статус на «${STATUS_LABEL[targetStatus]}»?`)) return;

    // Только Sinocare (i3/i6): админ может обойти отсутствующий скан SN.
    // ARIA/GLC — без SN, нужен только лот/срок; диалог «без SN» не показываем.
    const needsScan = selected.order_type !== 'glucometer'
      && !selected.is_complete
      && !selected.sn_bypass
      && ['ASSEMBLED', 'IN_TRANSIT', 'DELIVERED'].includes(targetStatus)
      && !['IN_TRANSIT', 'DELIVERED'].includes(selected.status);

    if (needsScan && role === 'admin') {
      if (!window.confirm(
        'Скан SN неполный. Продолжить без SN? Заявка будет помечена, при отгрузке спишется со склада.',
      )) return;
      setStatus(targetStatus, {
        force_without_sn: true,
        sn_bypass_note: `Админ: статус без полного скана (${new Date().toLocaleString('ru-RU')})`,
      });
      return;
    }

    const glcNeedsLots = selected.order_type === 'glucometer'
      && ['ASSEMBLED', 'IN_TRANSIT', 'DELIVERED'].includes(targetStatus)
      && !['IN_TRANSIT', 'DELIVERED'].includes(selected.status);

    if (glcNeedsLots) {
      const errs = glcLotLinesUiErrors(selected, glcLotLines);
      if (errs.length) {
        alert(errs.join('\n'));
        return;
      }
      if (glcStockGaps.length) {
        alert(`Недостаточно остатка на складе:\n${glcStockGaps.join('\n')}`);
        return;
      }
      setStatus(targetStatus, { glcLotLines });
      return;
    }

    setStatus(targetStatus);
  };

  /** Быстрый статус с карточки списка (В пути / Доставлен) — без открытия карточки. */
  const handleCardQuickStatus = async (order, e) => {
    e.stopPropagation();
    const next = nextForwardStatus(role, order);
    if (!next || !isCardQuickStatus(order.status, next)) return;
    if (!window.confirm(`#${order.external_order_id}: «${STATUS_LABEL[order.status]}» → «${STATUS_LABEL[next]}»?`)) {
      return;
    }
    await setStatus(next, {}, order.id);
  };

  const printPdf = async () => {
    const res = await apiFetch(token, `/orders/${selected.id}/label.pdf`);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      alert(err.error || 'Не удалось получить PDF');
      return;
    }
    const blob = await res.blob();
    openPdfBlob(blob, `label-${selected.external_order_id}.pdf`);
  };

  /** Бирка EMU: при пустом индексе получателя даём переспросить, а не печатать вслепую. */
  const fetchEmuLabelBlob = async () => {
    let res = await apiFetch(token, `/orders/${selected.id}/emu/label.pdf?form=2`);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      if (err.code !== 'EMU_ZIPCODE_PENDING') {
        alert(err.error || 'Наклейка EMU недоступна');
        return null;
      }
      if (!window.confirm(`${err.error}\n\nПечатать без индекса?`)) return null;
      res = await apiFetch(token, `/orders/${selected.id}/emu/label.pdf?form=2&ignore_zipcode=1`);
      if (!res.ok) {
        const err2 = await res.json().catch(() => ({}));
        alert(err2.error || 'Наклейка EMU недоступна');
        return null;
      }
    }
    const source = res.headers.get('X-EMU-Label-Source') || '';
    if (source.startsWith('fallback')) {
      alert(`Это fallback без штрихкода EMU (${source}). Официальную бирку даёт только активная заявка — после «Создать заново».`);
    }
    return res.blob();
  };

  const printEmuLabel = async () => {
    if (!selected?.id) return;
    const track = selected.emu_awb || selected.emu_barcode || selected.emu_orderno || selected.external_order_id;
    const filename = `EMU-birka-${selected.emu_orderno || selected.external_order_id}-${track}.pdf`;
    const blob = await fetchEmuLabelBlob();
    if (!blob) return;
    // Не iframe.print() — Safari рисует этикетку на A4. Открываем PDF как есть (~Zebra).
    openPdfBlob(blob, filename, { autoPrint: false });
  };

  const downloadEmuLabel = async () => {
    if (!selected?.id) return;
    const track = selected.emu_awb || selected.emu_barcode || selected.emu_orderno || selected.external_order_id;
    const filename = `EMU-birka-${selected.emu_orderno || selected.external_order_id}-${track}.pdf`;
    const blob = await fetchEmuLabelBlob();
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const downloadPdf = async () => {
    const res = await apiFetch(token, `/orders/${selected.id}/label.pdf`);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      alert(err.error || 'Не удалось получить PDF');
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `label-${selected.external_order_id}.pdf`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  const handleSearch = async () => {
    const normalizedSearchSn = searchSn
      .trim()
      .replace(/^serial(?:\s+number)?[:\s-]*/i, '')
      .replace(/^sn[:\s-]*/i, '')
      .trim();
    if (!normalizedSearchSn) return;
    const res = await apiFetch(token, `/search/${encodeURIComponent(normalizedSearchSn)}`);
    if (res.ok) setSearchResult(await res.json());
    else { setSearchResult(null); alert('Не найдено'); }
  };

  const runSync = async () => {
    setSyncModal({ open: true, loading: true, data: null, error: null });
    try {
      const res = await apiFetch(token, '/sync', { method: 'POST' });
      const data = await res.json();
      if (!res.ok) {
        setSyncModal({ open: true, loading: false, data: null, error: data.error || 'Ошибка синхронизации' });
        return;
      }
      setSyncModal({ open: true, loading: false, data, error: null });
      loadOrders();
    } catch {
      setSyncModal({ open: true, loading: false, data: null, error: 'Сервер недоступен' });
    }
  };

  const isGlcTab = tab === 'orders-glc';
  const isGlcOrder = selected?.order_type === 'glucometer';
  const ordersFlow = isGlcTab ? GLC_FLOW : CGM_FLOW;

  const todayYmd = localYmd();
  const yesterdayYmd = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return localYmd(d);
  })();
  const nowMs = Date.now();

  const stuckCount = orders.filter((o) => isStuckInTransit(o, nowMs)).length;

  const dateFilteredOrders = orders.filter((o) => {
    if (!dateFilter) return true;
    const ymd = orderDateYmd(o);
    if (dateFilter === 'today') return ymd === todayYmd;
    if (dateFilter === 'yesterday') return ymd === yesterdayYmd;
    return true;
  });

  const attentionFilteredOrders = dateFilteredOrders.filter((o) => {
    if (attentionFilter === 'stuck24') return isStuckInTransit(o, nowMs);
    return true;
  });

  const statusFilteredOrders = attentionFilteredOrders.filter((o) => {
    if (!statusFilter) return true;
    return o.status === statusFilter;
  });

  const filteredOrders = statusFilteredOrders.filter((o) => orderMatchesSearch(o, orderSearch));

  const assembleMode = deskMode === 'assemble';
  const deskOrders = assembleMode
    ? filteredOrders.filter((o) => ['NEW', 'ASSEMBLING', 'ASSEMBLED'].includes(o.status))
    : filteredOrders;

  const dateCounts = {
    all: orders.length,
    today: orders.filter((o) => orderDateYmd(o) === todayYmd).length,
    yesterday: orders.filter((o) => orderDateYmd(o) === yesterdayYmd).length,
  };

  const statusCounts = {
    all: attentionFilteredOrders.length,
    ...Object.fromEntries(ordersFlow.map((st) => [
      st,
      attentionFilteredOrders.filter((o) => o.status === st).length,
    ])),
  };

  const stockQty = (wh, sku) => {
    const row = stockBalances.find((b) => b.warehouse_code === wh && b.sku === sku);
    return row?.qty ?? 0;
  };

  const glcStockGaps = (() => {
    if (!selected || selected.order_type !== 'glucometer') return [];
    const wh = selected.warehouse_code || 'F';
    const gaps = [];
    if (selected.qty_gluck > 0) {
      const have = stockQty(wh, 'gluck');
      if (have < selected.qty_gluck) {
        gaps.push(`Глюкометр: на складе ${have}, нужно ${selected.qty_gluck}`);
      }
    }
    if (selected.qty_tp50 > 0) {
      const have = stockQty(wh, 'tp50');
      if (have < selected.qty_tp50) {
        gaps.push(`ТП50: на складе ${have}, нужно ${selected.qty_tp50}`);
      }
    }
    for (const line of glcLotLines) {
      const qty = parseInt(line.qty, 10) || 0;
      const lotName = String(line.lot_number || '').trim();
      if (!lotName || qty <= 0) continue;
      const lot = (glcStockLots[line.sku] || []).find(
        (l) => String(l.lot_number).toUpperCase() === lotName.toUpperCase(),
      );
      const free = lot?.qty ?? 0;
      if (qty <= free) continue;
      const who = (lot?.reserved_orders || []).map((ord) => `#${ord.external_order_id}`).join(', ');
      gaps.push(
        `${lotName}: свободно ${free}, в строке ${qty}${who ? ` (собрано ${who})` : ''}`,
      );
    }
    return gaps;
  })();

  const glcLotErrors = selected?.order_type === 'glucometer'
    ? glcLotLinesUiErrors(selected, glcLotLines)
    : [];
  const glcCanAssemble = selected && glcLotErrors.length === 0;

  if (authLoading) {
    return <div className="login-page"><p>Загрузка…</p></div>;
  }

  if (!token || !user) {
    return (
      <LoginScreen
        onLogin={(t, u) => {
          setToken(t);
          setUser(u);
          if (u?.role === 'viewer') {
            setTab('accounting');
            const prev = loadUiState();
            localStorage.setItem(UI_STATE_KEY, JSON.stringify({ ...prev, tab: 'accounting' }));
          }
        }}
      />
    );
  }

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-left">
          <button
            type="button"
            className="btn-menu"
            aria-label="Меню"
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen((v) => !v)}
          >
            <span className="btn-menu-bars" />
          </button>
          <h1>VitaNova склад</h1>
        </div>
        <div className="header-right">
          <span className="user-badge">{user.username}</span>
          {(user.title || ROLE_LABEL[role]) && (
            <span className="user-role-badge">{user.title || ROLE_LABEL[role]}</span>
          )}
          {canSync && (
            <button type="button" className="btn-header btn-header-desktop" onClick={runSync}>↻ Синхр. таблица</button>
          )}
          <button type="button" className="btn-header btn-header-desktop" onClick={logout}>Выйти</button>
        </div>
      </header>

      {mobileMenuOpen && (
        <button
          type="button"
          className="mobile-menu-backdrop"
          aria-label="Закрыть меню"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
      <nav className={`tabs mobile-drawer ${mobileMenuOpen ? 'open' : ''}`}>
        {canOrdersUi && (
          <>
            <button
              type="button"
              className={(tab === 'orders' || tab === 'orders-glc') ? 'active' : ''}
              onClick={() => { setAppTab('orders'); setSelected(null); }}
            >
              Заявки
            </button>
            <button type="button" className={tab === 'search' ? 'active' : ''} onClick={() => setAppTab('search')}>Поиск SN</button>
          </>
        )}
        {canWarehouseUi && (
          <button type="button" className={tab === 'stock' ? 'active' : ''} onClick={() => { setAppTab('stock'); setSelected(null); }}>Склады</button>
        )}
        {canRawUi && (
          <button type="button" className={tab === 'raw' ? 'active' : ''} onClick={() => { setAppTab('raw'); setSelected(null); }}>Производство</button>
        )}
        {canOrdersUi && (
          <button type="button" className={tab === 'stats' ? 'active' : ''} onClick={() => { setAppTab('stats'); setSelected(null); }}>Статистика</button>
        )}
        {canAccounting && (
          <button type="button" className={tab === 'accounting' ? 'active' : ''} onClick={() => { setAppTab('accounting'); setSelected(null); }}>Бухгалтерия</button>
        )}
        <div className="mobile-drawer-footer">
          {canSync && (
            <button type="button" className="btn-header" onClick={() => { setMobileMenuOpen(false); runSync(); }}>↻ Синхр. таблица</button>
          )}
          <button type="button" className="btn-header" onClick={() => { setMobileMenuOpen(false); logout(); }}>Выйти</button>
        </div>
      </nav>

      <main className="content">
        {(tab === 'orders' || tab === 'orders-glc') && canOrdersUi && (
          <>
            <div className="workspace-toolbar">
              <div className="orders-brand-segment" role="tablist" aria-label="Тип заявок">
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === 'orders'}
                  className={tab === 'orders' ? 'active' : ''}
                  onClick={() => { setAppTab('orders'); setSelected(null); }}
                >
                  {BRAND_SINOCARE}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === 'orders-glc'}
                  className={tab === 'orders-glc' ? 'active' : ''}
                  onClick={() => { setAppTab('orders-glc'); setSelected(null); }}
                >
                  {BRAND_ARIA}
                </button>
              </div>
              <div className="orders-brand-segment desk-mode-segment" role="tablist" aria-label="Режим стола">
                <button
                  type="button"
                  role="tab"
                  aria-selected={!assembleMode}
                  className={!assembleMode ? 'active' : ''}
                  onClick={() => setDeskModePersist('list')}
                >
                  Список
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={assembleMode}
                  className={assembleMode ? 'active' : ''}
                  onClick={() => setDeskModePersist('assemble')}
                  title="Узкий список + скан; PDF/EMU в панели"
                >
                  Сборка
                </button>
              </div>
              <WarehouseContextBar
                value={warehouseFilter}
                onChange={(v) => {
                  setWarehouseFilter(v);
                  setSelected(null);
                  persistOrderFilters({ warehouseFilter: v });
                }}
              />
              <OrdersFilterBar
                statusFilter={statusFilter}
                onStatusChange={(v) => {
                  setStatusFilter(v);
                  persistOrderFilters({ statusFilter: v });
                  setSelected(null);
                }}
                dateFilter={dateFilter}
                onDateChange={(v) => {
                  setDateFilter(v);
                  persistOrderFilters({ dateFilter: v });
                  setSelected(null);
                }}
                searchQuery={orderSearch}
                onSearchChange={(v) => {
                  setOrderSearch(v);
                  persistOrderFilters({ orderSearch: v });
                }}
                attentionFilter={attentionFilter}
                onAttentionChange={(v) => {
                  setAttentionFilter(v);
                  if (v === 'stuck24') {
                    setStatusFilter('IN_TRANSIT');
                    persistOrderFilters({ attentionFilter: v, statusFilter: 'IN_TRANSIT' });
                  } else {
                    persistOrderFilters({ attentionFilter: v });
                  }
                  setSelected(null);
                }}
                statusCounts={statusCounts}
                dateCounts={dateCounts}
                stuckCount={stuckCount}
                flowStatuses={ordersFlow}
              />
              {canSync && (
                <p className="hint-small workspace-hint">
                  Склад берётся из колонки Warehouse в таблице — после правок нажмите «Синхронизация».
                </p>
              )}
            </div>
            <div className={`layout-split ${selected ? 'has-selected' : ''} ${assembleMode ? 'assemble-mode' : ''}`}>
            <section className={`orders-list ${selected ? 'hide-when-selected' : ''} ${assembleMode ? 'orders-list-compact' : ''}`}>
              <h2>
                {isGlcTab ? BRAND_ARIA : BRAND_SINOCARE}
                {' '}
                ({deskOrders.length}{deskOrders.length !== orders.length ? ` / ${orders.length}` : ''})
                {assembleMode && <span className="desk-mode-hint"> · сборка</span>}
              </h2>
              {deskOrders.length === 0 && (
                <p className="hint-small">
                  {orders.length === 0
                    ? (role === 'driver' ? 'Нет заказов «Собран» или «В пути»' : 'Нет заявок')
                    : assembleMode
                      ? 'Нет заявок NEW / Сборка / Собран по фильтрам'
                      : 'Нет заявок по выбранным фильтрам'}
                </p>
              )}
              {deskOrders.map((o) => {
                const lotLines = orderLotLines(o, isGlcTab);
                const qtyLines = orderQtyLines(o, isGlcTab);
                const cardTone = `tone-${String(o.status || 'NEW').toLowerCase()}`;
                const quickNext = nextForwardStatus(role, o);
                const showQuick = !assembleMode && quickNext && isCardQuickStatus(o.status, quickNext);
                const stuck = isStuckInTransit(o, nowMs);
                const hours = stuck ? stuckHours(o, nowMs) : 0;
                const track = orderEmuTrack(o);
                const emuTitle = o.emu_status_title || '';
                const mainLabel = STATUS_LABEL[o.status] || o.status;
                const showEmuBadge = Boolean(o.emu_orderno || emuTitle)
                  && !(
                    ['IN_TRANSIT', 'DELIVERED'].includes(o.status)
                    && emuTitle
                    && (
                      emuTitle.toLowerCase().includes(String(mainLabel).toLowerCase())
                      || (o.status === 'DELIVERED' && /доставлен|вручен/.test(emuTitle.toLowerCase()))
                      || (o.status === 'IN_TRANSIT' && /в пути|курьер|отправлен|склад|транзит/.test(emuTitle.toLowerCase()))
                    )
                  );
                return (
                <div
                  key={o.id}
                  className={`order-card ${cardTone} ${selected?.id === o.id ? 'selected' : ''} ${o.sn_bypass ? 'sn-bypass' : ''} ${stuck ? 'stuck' : ''} ${assembleMode ? 'order-card-compact' : ''}`}
                >
                  <button
                    type="button"
                    className="order-card-body"
                    onClick={() => handleSelectOrder(o)}
                  >
                    <div className="order-card-row">
                      <div className="order-card-main">
                        <div className="order-card-top">
                          <strong className="order-card-id">#{o.external_order_id}</strong>
                          {!assembleMode && formatOrderDate(o) && (
                            <span className="order-card-date">{formatOrderDate(o)}</span>
                          )}
                        </div>
                        <div className="order-card-badges">
                          <span className={`badge wh-badge wh-${o.warehouse_code || 'F'}`}>
                            {assembleMode ? (o.warehouse_code || 'F') : warehouseLabel(o.warehouse_code)}
                          </span>
                          {o.stock_action === 'transfer_ft' && (
                            <span className="badge transfer-ft-badge">F→T</span>
                          )}
                          {(o.is_shipment_primary || (o.linked_orders?.length || 0) > 0) && (
                            <span className="badge" title="Несколько заявок в одной коробке">🔗</span>
                          )}
                          {o.is_shipment_linked && (
                            <span className="badge" title="В совместной отправке">📦</span>
                          )}
                          <span className={`badge status-${o.status}`}>
                            {mainLabel}
                          </span>
                          {!assembleMode && stuck && (
                            <span className="badge badge-stuck" title="В пути дольше 24 часов">
                              {hours}ч в пути
                            </span>
                          )}
                          {!assembleMode && o.sn_bypass && <span className="badge badge-bypass">без SN</span>}
                          {!assembleMode && showEmuBadge && (
                            <span className="badge badge-emu" title={o.emu_orderno || ''}>
                              EMU: {emuTitle || 'отправлено'}
                            </span>
                          )}
                        </div>
                        {!assembleMode && <div className="order-card-client">{o.client_name}</div>}
                        {!assembleMode && <div className="order-card-city">{o.city}</div>}
                        {assembleMode && (
                          <div className="order-card-client order-card-client-compact">
                            {(o.client_name || '').slice(0, 28)}{(o.client_name || '').length > 28 ? '…' : ''}
                          </div>
                        )}
                        {!assembleMode && o.phone_rec && (
                          <a
                            className="order-card-phone"
                            href={`tel:${String(o.phone_rec).replace(/[^\d+]/g, '')}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {o.phone_rec}
                          </a>
                        )}
                        {!assembleMode && track && (
                          <div className="order-card-track" title="Трек EMU">
                            Трек: {track}
                          </div>
                        )}
                        {!assembleMode && lotLines.length > 0 && (
                          <div className="order-card-lots">{lotLines.join(' · ')}</div>
                        )}
                      </div>
                      {qtyLines.length > 0 && (
                        <div className="order-card-qty">
                          {qtyLines.map((q) => (
                            <div key={q}>{q}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  </button>
                  {showQuick && (
                    <button
                      type="button"
                      className="order-card-quick"
                      onClick={(e) => handleCardQuickStatus(o, e)}
                    >
                      → {STATUS_LABEL[quickNext]}
                    </button>
                  )}
                </div>
              );})}
            </section>

            <section className={`order-detail order-detail-stats ${selected ? 'mobile-open' : ''}`}>
              {selected && (
                <button
                  type="button"
                  className="btn-back-mobile"
                  onClick={() => setSelected(null)}
                >
                  ← К списку
                </button>
              )}
              {!selected ? (
                <StatsDashboard
                  token={token}
                  logout={logout}
                  warehouse={warehouseFilter}
                  embedded
                />
              ) : isGlcOrder ? (
                <>
                  <h2>
                    {BRAND_ARIA} #{selected.external_order_id}
                    {' '}
                    <span className={`badge wh-badge wh-${selected.warehouse_code || 'F'}`}>
                      {warehouseLabel(selected.warehouse_code)}
                    </span>
                    {selected.stock_action === 'transfer_ft' && (
                      <span className="badge transfer-ft-badge" title="Склад: transfer_out F → transfer_in T, не продажа">
                        F→T перемещение
                      </span>
                    )}
                  </h2>

                  <OrderStatusBar
                    order={selected}
                    role={role}
                    onStep={handleStatusStep}
                    glcCanAssemble={glcCanAssemble}
                    glcStockOk={glcStockGaps.length === 0}
                    isComplete
                  />

                  {canScan && !['IN_TRANSIT', 'DELIVERED'].includes(selected.status) && !selected.stock_deducted && (
                    <div className="stock-action-bar">
                      {selected.stock_action === 'transfer_ft' ? (
                        <>
                          <span className="hint-small">
                            Списание как перемещение: «В пути» −F, «Доставлен» +T. EMU как обычно.
                          </span>
                          <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => setStockAction('expense')}
                          >
                            Сделать продажей
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={() => setStockAction('transfer_ft')}
                          disabled={(selected.warehouse_code || 'F') !== 'F'}
                          title={(selected.warehouse_code || 'F') !== 'F' ? 'Нужен склад Фергана' : ''}
                        >
                          Оформить как перемещение F→T
                        </button>
                      )}
                    </div>
                  )}

                  {glcStockGaps.length > 0 && (
                    <div className="stock-gap-warn">
                      <b>Нельзя отгрузить — нет остатка на складе заявки ({warehouseLabel(selected.warehouse_code)}):</b>
                      <ul>
                        {glcStockGaps.map((g) => <li key={g}>{g}</li>)}
                      </ul>
                      <span className="hint-small">
                        Тендерный резерв не списывается автоматически. Сделайте приход или перемещение из тендера на Фергану.
                      </span>
                    </div>
                  )}

                  {canAdminOrder && !editing && (
                    <div className="admin-bar">
                      <button type="button" className="btn-secondary" onClick={startEdit}>✎ Редактировать</button>
                      <button type="button" className="btn-danger" onClick={deleteOrder}>🗑 Удалить</button>
                    </div>
                  )}

                  {selected.sn_bypass && (
                    <p className="hint-small status-bypass-note">
                      Пометка: без скана SN{selected.sn_bypass_note ? ` — ${selected.sn_bypass_note}` : ''}
                    </p>
                  )}

                  {editing ? (
                    <div className="edit-form">
                      <label>Город<input value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} /></label>
                      <label>Клиент<input value={editForm.client_name} onChange={(e) => setEditForm({ ...editForm, client_name: e.target.value })} /></label>
                      <label>ИНН<input value={editForm.client_inn} onChange={(e) => setEditForm({ ...editForm, client_inn: e.target.value })} /></label>
                      <label>Получатель<input value={editForm.name_rec} onChange={(e) => setEditForm({ ...editForm, name_rec: e.target.value })} /></label>
                      <label>Телефон<input value={editForm.phone_rec} onChange={(e) => setEditForm({ ...editForm, phone_rec: e.target.value })} /></label>
                      <label>Склад
                        <select
                          value={editForm.warehouse_code || 'F'}
                          onChange={(e) => setEditForm({ ...editForm, warehouse_code: e.target.value })}
                          disabled={['IN_TRANSIT', 'DELIVERED'].includes(selected.status) || selected.stock_deducted}
                        >
                          <option value="F">Фергана (F)</option>
                          <option value="T">Ташкент (T)</option>
                        </select>
                      </label>
                      <label className="full">Адрес<textarea value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} rows={2} /></label>
                      <label>Глюкометр шт<input type="number" min="0" value={editForm.qty_gluck} onChange={(e) => setEditForm({ ...editForm, qty_gluck: e.target.value })} /></label>
                      <label>ТП50 шт<input type="number" min="0" value={editForm.qty_tp50} onChange={(e) => setEditForm({ ...editForm, qty_tp50: e.target.value })} /></label>
                      <p className="hint-small full">
                        Пока статус NEW — sync с листа перезапишет qty/адрес. Чтобы костыль не откатился: поправь лист или сразу «→ Собран».
                      </p>
                      <div className="edit-actions full">
                        <button type="button" className="btn-primary" onClick={saveEdit}>Сохранить</button>
                        <button type="button" className="btn-secondary" onClick={() => setEditing(false)}>Отмена</button>
                      </div>
                    </div>
                  ) : (
                  <div className="detail-grid">
                    <div><b>Клиент:</b> {selected.client_name}</div>
                    <div><b>Город:</b> {selected.city}</div>
                    <div><b>Склад:</b> {warehouseLabel(selected.warehouse_code)}</div>
                    <div><b>Получатель:</b> {selected.name_rec}</div>
                    <div><b>Тел:</b> {selected.phone_rec}</div>
                    <div className="full"><b>Адрес:</b> {selected.address}</div>
                  </div>
                  )}

                  <div className="qty-boxes">
                    <div className="qty-box done">
                      <div className="qty-title">Глюкометр</div>
                      <div className="qty-num">{selected.qty_gluck} шт</div>
                    </div>
                    <div className="qty-box done">
                      <div className="qty-title">ТП50</div>
                      <div className="qty-num">{selected.qty_tp50} шт</div>
                    </div>
                  </div>

                  {canScan && ['NEW', 'ASSEMBLED'].includes(selected.status) && (
                    <div className="glc-meta-form">
                      <h3>Лоты и сроки</h3>
                      <p className="hint-small">
                        Склад {warehouseLabel(selected.warehouse_code)}. Несколько лотов на SKU — сумма qty должна совпасть.
                        Отгрузка списывает штуки по лоту. PDF заявки — наклейка с адресом и лотами.
                      </p>
                      {['gluck', 'tp50'].map((sku) => {
                        const need = sku === 'gluck' ? selected.qty_gluck : selected.qty_tp50;
                        if (!(need > 0)) return null;
                        const label = sku === 'gluck' ? 'Глюкометр' : 'ТП50';
                        const stock = glcStockLots[sku] || [];
                        const rows = glcLotLines.filter((l) => l.sku === sku);
                        const sum = rows.reduce((s, l) => s + (parseInt(l.qty, 10) || 0), 0);
                        return (
                          <div key={sku} className="glc-sku-lots">
                            <div className="glc-sku-lots-head">
                              <b>{label}</b>
                              <span className={sum === need ? 'hint-small' : 'hint-small glc-qty-mismatch'}>
                                лоты {sum} / нужно {need}
                              </span>
                              {selected.status === 'NEW' && (
                                <button
                                  type="button"
                                  className="btn-secondary btn-tiny"
                                  onClick={() => setGlcLotLines([...glcLotLines, emptyGlcLotLine(sku, '')])}
                                >
                                  + лот
                                </button>
                              )}
                            </div>
                            {stock.some((l) => l.reserved > 0) && (
                              <p className="hint-small">
                                Уже собрано и не в списке:{' '}
                                {stock.filter((l) => l.reserved > 0).map((l) => (
                                  `${l.lot_number} ${l.reserved} шт (${(l.reserved_orders || []).map((ord) => `#${ord.external_order_id}`).join(', ')})`
                                )).join(' · ')}
                              </p>
                            )}
                            {rows.map((row) => (
                              <div key={row.key} className="glc-meta-row glc-lot-line">
                                <label>Лот
                                  <select
                                    value={row.lot_number || ''}
                                    disabled={selected.status !== 'NEW'}
                                    onChange={(e) => {
                                      const lot = e.target.value;
                                      const hit = stock.find((l) => l.lot_number === lot);
                                      setGlcLotLines(glcLotLines.map((l) => (l.key === row.key
                                        ? {
                                          ...l,
                                          lot_number: lot,
                                          expiration: hit?.expiration_date
                                            ? isoToDmy(hit.expiration_date)
                                            : (lot ? l.expiration : ''),
                                        }
                                        : l)));
                                    }}
                                  >
                                    <option value="">— выберите лот —</option>
                                    {stock.filter((l) => l.qty > 0).map((l) => (
                                      <option key={l.lot_number} value={l.lot_number}>
                                        {l.lot_number} · {l.qty} шт
                                        {l.reserved > 0 ? ` · занято ${l.reserved}` : ''}
                                        {l.expiration_date ? ` · до ${isoToDmy(l.expiration_date)}` : ''}
                                      </option>
                                    ))}
                                    {row.lot_number
                                      && !stock.some((l) => l.lot_number === row.lot_number && l.qty > 0) && (
                                      <option value={row.lot_number}>{row.lot_number} (нет на складе)</option>
                                    )}
                                  </select>
                                </label>
                                <label>Кол-во
                                  <input
                                    type="number"
                                    min="1"
                                    value={row.qty}
                                    disabled={selected.status !== 'NEW'}
                                    onChange={(e) => setGlcLotLines(glcLotLines.map((l) => (
                                      l.key === row.key ? { ...l, qty: e.target.value } : l
                                    )))}
                                  />
                                </label>
                                <label>Срок (ДД.ММ.ГГГГ)
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    placeholder="01.02.2027"
                                    value={row.expiration}
                                    disabled={selected.status !== 'NEW'}
                                    onChange={(e) => setGlcLotLines(glcLotLines.map((l) => (
                                      l.key === row.key
                                        ? { ...l, expiration: formatDmyInput(e.target.value) }
                                        : l
                                    )))}
                                  />
                                </label>
                                {selected.status === 'NEW' && rows.length > 1 && (
                                  <button
                                    type="button"
                                    className="btn-danger btn-tiny glc-lot-remove"
                                    title="Удалить строку"
                                    onClick={() => setGlcLotLines(glcLotLines.filter((l) => l.key !== row.key))}
                                  >
                                    ×
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        );
                      })}
                      {glcLotErrors.length > 0 && (
                        <ul className="glc-lot-errors hint-small">
                          {glcLotErrors.map((e) => <li key={e}>{e}</li>)}
                        </ul>
                      )}
                    </div>
                  )}

                  {assembleMode ? (
                    <div className="actions assemble-secondary-actions">
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => setShipDrawerOpen(true)}
                      >
                        PDF / EMU
                      </button>
                      {canPrint && (
                        <button type="button" className="btn-primary" onClick={printPdf}>🖨 PDF</button>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="actions">
                        {canPrint && (
                          <>
                            <button type="button" className="btn-primary" onClick={printPdf}>🖨 Печать PDF</button>
                            <button type="button" className="btn-secondary" onClick={downloadPdf}>⬇ Скачать PDF</button>
                          </>
                        )}
                        {canSendEmu && (
                          <button type="button" className="btn-secondary" onClick={() => setEmuModalOpen(true)}>
                            📦 В EMU
                          </button>
                        )}
                      </div>

                      <ShipmentLinkBlock
                        order={selected}
                        token={token}
                        canEdit={canScan}
                        onUpdated={onShipmentUpdated}
                      />

                      <EmuTrackingBlock
                        order={selected}
                        events={emuEvents}
                        onRefresh={refreshEmu}
                        refreshing={emuRefreshing}
                        onPrintLabel={printEmuLabel}
                        onDownloadLabel={downloadEmuLabel}
                        onRecreate={canScan ? recreateEmu : undefined}
                        recreating={emuRecreating}
                      />
                    </>
                  )}
                </>
              ) : (
                <>
                  <h2>
                    Заказ #{selected.external_order_id}
                    {' '}
                    <span className={`badge wh-badge wh-${selected.warehouse_code || 'F'}`}>
                      {warehouseLabel(selected.warehouse_code)}
                    </span>
                    {selected.stock_action === 'transfer_ft' && (
                      <span className="badge transfer-ft-badge" title="Склад: transfer_out F → transfer_in T, не продажа">
                        F→T перемещение
                      </span>
                    )}
                  </h2>

                  <OrderStatusBar
                    order={selected}
                    role={role}
                    onStep={handleStatusStep}
                    glcCanAssemble
                    isComplete={selected.is_complete}
                  />

                  {canScan && !['IN_TRANSIT', 'DELIVERED'].includes(selected.status) && !selected.stock_deducted && (
                    <div className="stock-action-bar">
                      {selected.stock_action === 'transfer_ft' ? (
                        <>
                          <span className="hint-small">
                            Списание как перемещение: «В пути» −F, «Доставлен» +T. EMU/скан как обычно.
                          </span>
                          <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => setStockAction('expense')}
                          >
                            Сделать продажей
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={() => setStockAction('transfer_ft')}
                          disabled={(selected.warehouse_code || 'F') !== 'F'}
                          title={(selected.warehouse_code || 'F') !== 'F' ? 'Нужен склад Фергана' : ''}
                        >
                          Оформить как перемещение F→T
                        </button>
                      )}
                    </div>
                  )}

                  {canAdminOrder && !editing && (
                    <div className="admin-bar">
                      <button type="button" className="btn-secondary" onClick={startEdit}>✎ Редактировать</button>
                      <button type="button" className="btn-danger" onClick={deleteOrder}>🗑 Удалить</button>
                    </div>
                  )}

                  {selected.sn_bypass && (
                    <p className="hint-small status-bypass-note">
                      Пометка: без скана SN{selected.sn_bypass_note ? ` — ${selected.sn_bypass_note}` : ''}
                    </p>
                  )}

                  {editing ? (
                    <div className="edit-form">
                      <label>Город<input value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} /></label>
                      <label>Клиент<input value={editForm.client_name} onChange={(e) => setEditForm({ ...editForm, client_name: e.target.value })} /></label>
                      <label>ИНН<input value={editForm.client_inn} onChange={(e) => setEditForm({ ...editForm, client_inn: e.target.value })} /></label>
                      <label>Получатель<input value={editForm.name_rec} onChange={(e) => setEditForm({ ...editForm, name_rec: e.target.value })} /></label>
                      <label>Телефон<input value={editForm.phone_rec} onChange={(e) => setEditForm({ ...editForm, phone_rec: e.target.value })} /></label>
                      <label>Склад
                        <select
                          value={editForm.warehouse_code || 'F'}
                          onChange={(e) => setEditForm({ ...editForm, warehouse_code: e.target.value })}
                          disabled={['IN_TRANSIT', 'DELIVERED'].includes(selected.status)}
                        >
                          <option value="F">Фергана (F)</option>
                          <option value="T">Ташкент (T)</option>
                        </select>
                      </label>
                      <label className="full">Адрес<textarea value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} rows={2} /></label>
                      <label>i3 шт<input type="number" min="0" value={editForm.qty_i3} onChange={(e) => setEditForm({ ...editForm, qty_i3: e.target.value })} /></label>
                      <label>i6 шт<input type="number" min="0" value={editForm.qty_i6} onChange={(e) => setEditForm({ ...editForm, qty_i6: e.target.value })} /></label>
                      <div className="edit-actions full">
                        <button type="button" className="btn-primary" onClick={saveEdit}>Сохранить</button>
                        <button type="button" className="btn-secondary" onClick={() => setEditing(false)}>Отмена</button>
                      </div>
                    </div>
                  ) : (
                  <div className="detail-grid">
                    <div><b>Клиент:</b> {selected.client_name}</div>
                    <div><b>Город:</b> {selected.city}</div>
                    <div><b>Склад:</b> {warehouseLabel(selected.warehouse_code)}</div>
                    <div><b>Получатель:</b> {selected.name_rec}</div>
                    <div><b>Тел:</b> {selected.phone_rec}</div>
                    <div className="full"><b>Адрес:</b> {selected.address}</div>
                  </div>
                  )}

                  <div className="qty-boxes">
                    <div className={`qty-box ${selected.scanned_i3 >= selected.qty_i3 ? 'done' : ''}`}>
                      <div className="qty-title">iCan i3</div>
                      <div className="qty-num">{selected.scanned_i3} / {selected.qty_i3}</div>
                    </div>
                    <div className={`qty-box ${selected.scanned_i6 >= selected.qty_i6 ? 'done' : ''}`}>
                      <div className="qty-title">iCan i6</div>
                      <div className="qty-num">{selected.scanned_i6} / {selected.qty_i6}</div>
                    </div>
                  </div>

                  {canScan && ['NEW', 'ASSEMBLING'].includes(selected.status) && (
                    <div className="scan-block scan-dock">
                      <label htmlFor="scan">Bluetooth-сканер (Enter · / фокус · Esc к списку)</label>
                      <input
                        id="scan"
                        ref={scanRef}
                        value={scanInput}
                        onChange={(e) => setScanInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleScan()}
                        placeholder="Сканируйте DataMatrix..."
                        autoComplete="off"
                      />
                      <button type="button" onClick={handleScan}>Добавить</button>
                      {scanMsg && (
                        <div className={`scan-strip ${scanMsg.type === 'ok' ? 'ok' : 'err'}`}>
                          {scanMsg.text}
                        </div>
                      )}
                    </div>
                  )}

                  {assembleMode ? (
                    <div className="actions assemble-secondary-actions">
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => setShipDrawerOpen(true)}
                      >
                        PDF / EMU
                      </button>
                      {canPrint && (
                        <button type="button" className="btn-primary" onClick={printPdf}>🖨 PDF</button>
                      )}
                    </div>
                  ) : (
                    <>
                      <ShipmentLinkBlock
                        order={selected}
                        token={token}
                        canEdit={canScan}
                        onUpdated={onShipmentUpdated}
                      />

                      <div className="actions">
                        {canPrint && (
                          <>
                            <button type="button" className="btn-primary" onClick={printPdf}>🖨 Печать PDF</button>
                            <button type="button" className="btn-secondary" onClick={downloadPdf}>⬇ Скачать PDF</button>
                          </>
                        )}
                        {canSendEmu && (
                          <button type="button" className="btn-secondary" onClick={() => setEmuModalOpen(true)}>
                            📦 В EMU
                          </button>
                        )}
                      </div>

                      <EmuTrackingBlock
                        order={selected}
                        events={emuEvents}
                        onRefresh={refreshEmu}
                        refreshing={emuRefreshing}
                        onPrintLabel={printEmuLabel}
                        onDownloadLabel={downloadEmuLabel}
                        onRecreate={canScan ? recreateEmu : undefined}
                        recreating={emuRecreating}
                      />
                    </>
                  )}

                  {sns.length > 0 && (
                    <>
                      <h3>Отсканированные SN ({sns.length})</h3>
                      <ul className="sn-list">
                        {sns.map((s) => (
                          <li key={s.sn}>
                            <span className="model-tag">{s.model}</span>
                            <span className="sn-value">{s.sn}</span>
                            {s.lot_number && <span className="sn-lot">лот {s.lot_number}</span>}
                            {canScan && ['NEW', 'ASSEMBLING', 'ASSEMBLED'].includes(selected.status) && (
                              <button
                                type="button"
                                className="sn-del"
                                title="Удалить ошибочный скан"
                                onClick={() => deleteSn(s.sn)}
                              >
                                ✕
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </>
              )}
            </section>
          </div>

          {selected && shipDrawerOpen && (
            <div className="ship-drawer-overlay" onClick={() => setShipDrawerOpen(false)}>
              <aside
                className="ship-drawer"
                role="dialog"
                aria-label="PDF и EMU"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="ship-drawer-head">
                  <h3>PDF / EMU · #{selected.external_order_id}</h3>
                  <button
                    type="button"
                    className="icon-btn"
                    onClick={() => setShipDrawerOpen(false)}
                    aria-label="Закрыть"
                  >
                    ✕
                  </button>
                </div>
                <div className="ship-drawer-body">
                  <div className="actions">
                    {canPrint && (
                      <>
                        <button type="button" className="btn-primary" onClick={printPdf}>🖨 Печать PDF</button>
                        <button type="button" className="btn-secondary" onClick={downloadPdf}>⬇ Скачать PDF</button>
                      </>
                    )}
                    {canSendEmu && (
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => { setShipDrawerOpen(false); setEmuModalOpen(true); }}
                      >
                        📦 В EMU
                      </button>
                    )}
                  </div>
                  <ShipmentLinkBlock
                    order={selected}
                    token={token}
                    canEdit={canScan}
                    onUpdated={onShipmentUpdated}
                  />
                  <EmuTrackingBlock
                    order={selected}
                    events={emuEvents}
                    onRefresh={refreshEmu}
                    refreshing={emuRefreshing}
                    onPrintLabel={printEmuLabel}
                    onDownloadLabel={downloadEmuLabel}
                    onRecreate={canScan ? recreateEmu : undefined}
                    recreating={emuRecreating}
                  />
                </div>
              </aside>
            </div>
          )}
          </>
        )}

        {tab === 'stock' && canWarehouseUi && (
          <StockPage token={token} logout={logout} role={role} />
        )}

        {tab === 'raw' && canRawUi && (
          <RawMaterialsPage token={token} logout={logout} role={role} />
        )}

        {tab === 'stats' && canOrdersUi && (
          <>
            <div className="workspace-toolbar">
              <WarehouseContextBar
                value={warehouseFilter}
                onChange={(v) => {
                  setWarehouseFilter(v);
                  persistOrderFilters({ warehouseFilter: v });
                }}
              />
            </div>
            <StatsDashboard token={token} logout={logout} warehouse={warehouseFilter} />
          </>
        )}

        {tab === 'accounting' && canAccounting && (
          <AccountingPage token={token} logout={logout} role={role} />
        )}

        {tab === 'search' && canOrdersUi && (
          <div>
            <h2>Поиск по SN</h2>
            <div className="search-form">
              <input
                value={searchSn}
                onChange={(e) => setSearchSn(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="SN или часть..."
              />
              <button type="button" onClick={handleSearch}>Найти</button>
            </div>
            {searchResult && (
              <div className="search-result">
                <p><b>SN:</b> {searchResult.sn} ({searchResult.model})</p>
                {searchResult.status === 'MARKETING' ? (
                  <>
                    <p><b>Статус:</b> Маркетинг (списание мимо заявки)</p>
                    <p><b>Клиент:</b> {searchResult.client_name || 'Маркетинг'}</p>
                    <p><b>Комментарий:</b> {searchResult.comment || '—'}</p>
                    <p><b>Партия:</b> {searchResult.lot_number || '—'}</p>
                    {searchResult.warehouse_code && (
                      <p><b>Склад:</b> {warehouseLabel(searchResult.warehouse_code)}</p>
                    )}
                    <p><b>Когда:</b> {searchResult.created_at
                      ? new Date(searchResult.created_at).toLocaleString('ru-RU')
                      : '—'}</p>
                    <p><b>Кто:</b> {searchResult.created_by || '—'}</p>
                  </>
                ) : (
                  <>
                    <p><b>Заказ:</b> {searchResult.external_order_id}</p>
                    <p><b>Клиент:</b> {searchResult.client_name}</p>
                    <p><b>Город:</b> {searchResult.city}</p>
                    <p><b>Получатель:</b> {searchResult.name_rec} · {searchResult.phone_rec}</p>
                    <p><b>Адрес:</b> {searchResult.address}</p>
                    <p><b>Партия:</b> {searchResult.lot_number}</p>
                    <p><b>Статус заказа:</b> {STATUS_LABEL[searchResult.order_status] || searchResult.order_status}</p>
                    {searchResult.warehouse_code && (
                      <p><b>Склад:</b> {warehouseLabel(searchResult.warehouse_code)}</p>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </main>

      <SyncResultModal
        open={syncModal.open}
        loading={syncModal.loading}
        data={syncModal.data}
        error={syncModal.error}
        onClose={() => setSyncModal({ open: false, loading: false, data: null, error: null })}
      />

      <EmuSendModal
        open={emuModalOpen}
        order={selected}
        token={token}
        onClose={() => setEmuModalOpen(false)}
        onCreated={onEmuCreated}
      />
    </div>
  );
}

export default App;
